// Copy for every keyword landing page, rendered in the Website 2.0 style by
// <LandingPage slug> (src/components/landingPage). To add a page, add one
// entry here and a folder in its menu's route group whose page.tsx renders
// `<LandingPage slug="..." />` (copy any existing one). The sitemap entry (sitemap.ts), header/footer background (lib/backgrounds.ts),
// and related-pages fallback cross-links (relatedPagesSection.component.tsx)
// all derive from this file automatically.

// Copy strings may wrap keywords in `**bold**` - the section components
// render those with <RichText> (components/common/richText).

type FeatureTitlePart = { text: string; gradient: boolean };

/** A plain point, or a bold title with its explanation. */
export type LandingPagePoint = string | { title: string; text: string };

type LandingPageSolution = {
  badgeText?: string;
  heading: string;
  /** One paragraph, or several. */
  description: string | string[];
  /** Lead-in shown right above the steps, e.g. "With Fydo, you can:". */
  listLabel?: string;
  steps: LandingPagePoint[];
  bottomLine: string;
};

interface LandingPageFeature {
  id: number;
  titleStart: FeatureTitlePart;
  titleGrad: FeatureTitlePart;
  titleEnd: FeatureTitlePart;
  /** One paragraph, or several. */
  description: string | string[];
  isReversed: boolean;
  animation: "get_customer" | "bring_more" | "understand";
  bullets: string[];
  /** Paragraphs shown after the bullets. */
  closing?: string[];
}

export interface LandingPageData {
  /** Route segment with no leading slash, e.g. "customer-win-back-software". */
  slug: string;
  /**
   * Groups pages for the related-pages fallback (see
   * relatedPagesSection.component.tsx) when no hand-curated cross-links
   * exist for a path yet. Pages sharing a cluster get linked to each other.
   */
  cluster: string;
  seo: {
    title: string;
    description: string;
    /** Page keywords for the meta keywords tag. */
    keywords?: string[];
  };
  heroSection: {
    badgeText: string;
    title: string;
    gradientSubHeading: string;
    baseSubHeading: string;
    microSubHeading: string;
    /** Intro paragraphs under the sub-heading. */
    description?: string[];
    supportingText: string;
    primaryButton: { title: string; link: string };
    secondaryButton: { title: string; link: string };
  };
  /** Omit any section below to skip rendering it for this page. */
  /** Rendered before the problem section ("What is ..."). */
  whatIsSection?: LandingPageSolution;
  problemSection?: {
    badgeText: string;
    heading: string;
    subHeading: string;
    /** Extra intro paragraphs under the sub-heading. */
    description?: string[];
    /** Lead-in shown right above the points. */
    listLabel?: string;
    points: LandingPagePoint[];
    endingLine: string;
  };
  /** Rendered right after the problem section ("how Fydo solves it"). */
  solutionSection?: LandingPageSolution;
  featureSection?: {
    heading: string;
    subHeading: string;
    /** "grid" for a run of short one-paragraph features (default "rows"). */
    layout?: "rows" | "grid";
    features: LandingPageFeature[];
  };
  useCaseSection?: {
    badgeText: string;
    heading: string;
    subHeading: string;
    useCases: { title: string; detail: string }[];
    endingLine: string;
    /** "grid" shows the cards in order instead of the scrolling marquee. */
    layout?: "scroll" | "grid";
  };
  benefitsSection?: {
    badgeText: string;
    heading: string;
    description: string;
    benefits: LandingPagePoint[];
    /** Closing line under the benefit cards. */
    endingLine?: string;
  };
  customerTestimonialsSection?: {
    heading: string;
    subHeading: string;
    /** Omit to show the sitewide default testimonials. */
    testimonials?: {
      id: number;
      quote: string;
      description: string;
      author: string;
      rating: number;
      authorStoreName?: string;
      authorStoreType?: string;
      authorAvatar: string;
    }[];
    points: string[];
    /** Badge above the heading; "" hides it (default "Testimonials"). */
    badgeText?: string;
    /** Hide the sitewide trust stats shown when `points` is empty. */
    hideTrustPoints?: boolean;
  };
  stepsSection?: {
    title: string;
    description: string;
    steps: {
      id: number;
      iconUrl: string;
      title: string;
      description: string;
    }[];
    /** Set false to hide the "Get started now" button. */
    showButton?: boolean;
  };
  FAQSection?: {
    heading: string;
    faqItems: { question: string; answer: string }[];
  };
  signupSection?: {
    badgeText: string;
    title: string;
    subtitle: string;
    buttonLabel: string;
    buttonLink: string;
    secondaryButton?: { label: string; link: string };
  };
  /** Set to false to skip <MerchantsSection> for this page. Defaults to shown. */
  showMerchantsSection?: boolean;
  /** Keywords passed to <RelatedBlogPostsSection keywords={...} />. */
  relatedBlogKeywords: string[];
}

export const landingPages: Record<string, LandingPageData> = {
  "customer-reactivation-software": {
    slug: "customer-reactivation-software",
    cluster: "retention-recovery",
    showMerchantsSection: false,
    seo: {
      title: "Customer Reactivation Software for Retail Businesses",
      description:
        "Reactivate inactive customers with Fydo's customer reactivation software. Create targeted offers, launch re-engagement campaigns, reward returning customers, and use Free WhatsApp Marketing.",
      keywords: [
        "customer reactivation software",
        "customer reactivation platform",
        "customer reactivation solution",
        "customer re-engagement software",
        "customer re-engagement platform",
        "customer reactivation tools",
        "customer win-back software",
        "customer win-back platform",
        "customer retention software",
        "customer retention platform",
        "customer loyalty software",
        "customer reactivation software India",
        "customer reactivation software for retail",
        "customer re-engagement",
        "inactive customer reactivation",
        "inactive customer retention",
        "customer win-back campaigns",
        "lost customer recovery",
        "repeat customer software",
        "customer retention strategy",
        "customer loyalty program",
        "digital loyalty program",
        "customer rewards platform",
        "personalized customer offers",
        "cashback loyalty program",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Customer Reactivation Software",
      title:
        "Customer Reactivation Software to Bring Back Inactive Customers & Increase Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Reconnect with customers who have stopped purchasing and turn inactive shoppers into returning customers with a simple, powerful customer reactivation solution built for offline retail.",
        "Fydo helps retailers identify inactive customers, create targeted offers, launch re-engagement campaigns, and build stronger customer relationships through digital loyalty, rewards, and **Free WhatsApp Marketing**.",
        "Bring back customers who already know your business and create more opportunities for repeat purchases.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need Customer Reactivation Software?",
      subHeading:
        "Customers can become inactive for many reasons. They may have forgotten about your business, changed their buying habits, or simply haven't received a compelling reason to return.",
      description: [],
      listLabel:
        "Without a structured reactivation strategy, retailers often face:",
      points: [
        {
          title: "Inactive Customers Go Unnoticed",
          text: "Businesses may have hundreds or thousands of customers who haven't purchased recently without knowing who needs to be re-engaged.",
        },
        {
          title: "Lost Repeat Sales",
          text: "Customers who previously purchased from your business may gradually shift to competitors, resulting in missed revenue opportunities.",
        },
        {
          title: "No Follow-Up After Purchase",
          text: "Without continued communication, your relationship with a customer can end after a single transaction.",
        },
        {
          title: "Generic Offers Have Low Impact",
          text: "Sending identical promotions to every customer may not provide a meaningful reason for inactive shoppers to return.",
        },
        {
          title: "Customer Data Isn't Fully Utilized",
          text: "Purchase history and customer activity can reveal valuable re-engagement opportunities, but many retailers don't act on these insights.",
        },
        {
          title: "Too Much Focus on New Customer Acquisition",
          text: "Businesses often invest heavily in attracting new shoppers while overlooking customers they have already acquired.",
        },
      ],
      endingLine:
        "**The result?** Previously active customers become inactive, repeat purchases decline, and valuable revenue opportunities are left untapped.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Inactive Customers into Active Customers Again",
      description: [
        "Fydo's **customer reactivation software** helps retailers identify inactive shoppers and create meaningful opportunities to bring them back.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Identify customers who haven't purchased recently",
        "Create targeted reactivation campaigns",
        "Send personalized offers and incentives",
        "Offer cashback, points, discounts, and coupons",
        "Reconnect with customers through **Free WhatsApp Marketing**",
        "Encourage customers to make repeat purchases",
        "Reward customers when they return",
        "Track customer activity and engagement",
        "Run targeted promotional campaigns",
        "Combine reactivation with your existing loyalty program",
      ],
      bottomLine:
        "Instead of allowing valuable customers to disappear, Fydo helps you reconnect with them at the right time and give them a reason to shop with you again.",
    },
    featureSection: {
      heading: "Everything You Need for Effective Customer Reactivation",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Identify Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand which shoppers have stopped purchasing and find opportunities to reconnect with them.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Track customer activity",
            "Identify inactive shoppers",
            "Review purchase behavior",
            "Find customers who haven't returned",
            "Monitor customer engagement",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Create Reactivation Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create targeted campaigns designed specifically to encourage inactive customers to return.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Create comeback campaigns",
            "Offer limited-time incentives",
            "Set up targeted promotions",
            "Create customer-specific offers",
            "Encourage repeat purchases",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Re-Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers who haven't visited recently through **Free WhatsApp Marketing**.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "Share comeback offers",
            "Promote new products",
            "Announce special promotions",
            "Communicate seasonal campaigns",
            "Reconnect with inactive shoppers",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Offer Rewards That Encourage a Return",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make coming back more valuable with flexible digital rewards.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Cashback",
            "Loyalty points",
            "Discounts",
            "Coupons",
            "Special rewards",
            "Personalized incentives",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand how customers respond to your campaigns and loyalty initiatives.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Monitor customer activity",
            "Track purchases",
            "View reward usage",
            "Identify returning customers",
            "Understand engagement trends",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Digital Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Replace manual follow-ups with a convenient digital experience.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [
            "Digital customer records",
            "QR-based engagement",
            "Easy campaign management",
            "Digital rewards",
            "Simple customer experience",
          ],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo works for traditional retailers as well as businesses using POS systems.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "No complicated hardware required",
            "Works alongside existing processes",
            "Suitable for small and mid-sized businesses",
            "Simple merchant setup",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Re-Engagement for Every Type of Retail Business",
      subHeading:
        "Different retail businesses have different customer purchase cycles. Fydo helps each business create relevant opportunities to reconnect with customers.",
      useCases: [
        {
          title: "Grocery & Kirana/ General Stores",
          detail:
            "Encourage shoppers who haven't visited recently to return for their regular household purchases.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Reconnect with previous buyers through new collections, seasonal offers, and personalized promotions.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Remind customers about their next visit and encourage them to return for another service.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Bring back customers with special offers, cashback, and rewards for their next visit or order.",
        },
        {
          title: "Pharmacies",
          detail:
            "Maintain engagement with existing customers and encourage continued visits through digital rewards.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Reconnect with previous shoppers using relevant promotions and personalized incentives.",
        },
      ],
      endingLine:
        "Whether a customer has been inactive for a few weeks or several months, targeted re-engagement can create a valuable opportunity to restart the relationship.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Re-Engagement",
      description: "",
      benefits: [
        {
          title: "Bring Back Inactive Customers",
          text: "Reconnect with shoppers who have stopped purchasing and give them a compelling reason to return.",
        },
        {
          title: "Recover Lost Sales Opportunities",
          text: "Generate additional sales by re-engaging people who have already purchased from your business.",
        },
        {
          title: "Create More Relevant Offers",
          text: "Use customer activity and purchase behavior to make your promotions more targeted and meaningful.",
        },
        {
          title: "Strengthen Existing Relationships",
          text: "Continue engaging customers beyond their first purchase and build stronger long-term connections.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Stay connected with your audience through digital campaigns and **Free WhatsApp Marketing**.",
        },
        {
          title: "Reward Returning Customers",
          text: "Use cashback, points, discounts, and coupons to make the customer's return more rewarding.",
        },
        {
          title: "Increase Repeat Purchases",
          text: "Create timely incentives that encourage customers to shop with you again.",
        },
        {
          title: "Make Better Use of Customer Data",
          text: "Use customer activity and purchase information to identify inactive shoppers and re-engagement opportunities.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage campaigns, rewards, and customer engagement from one easy-to-use platform without complicated processes.",
        },
      ],
      endingLine:
        "The objective is simple: **reconnect with customers before an inactive relationship becomes a permanently lost customer.**",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps retailers strengthen customer relationships through digital loyalty, rewards, personalized engagement, and customer reactivation strategies. By combining customer insights with relevant offers and consistent communication, businesses can create more opportunities to reconnect with existing shoppers.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helps us stay connected with our customers, reward them for their purchases, and create more reasons for them to return.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Reactivate Customers in 3 Simple Steps",
      description: "Identify. Reconnect. Reward.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Identify",
          description:
            "Use customer activity and purchase information to identify shoppers who have become inactive.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Re-Engage",
          description:
            "Create a relevant offer or reward and reach inactive customers through targeted campaigns and **Free WhatsApp Marketing**.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Bring Them Back",
          description:
            "Customers return, make another purchase, and continue engaging with your business through rewards and loyalty benefits.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is customer reactivation software?",
          answer:
            "Customer reactivation software helps businesses identify inactive customers and engage them with targeted offers, rewards, and communication to encourage them to return.",
        },
        {
          question: "How does Fydo help reactivate customers?",
          answer:
            "Fydo helps businesses identify inactive shoppers, create targeted campaigns, provide rewards, and communicate with customers through digital engagement tools.",
        },
        {
          question: "Is Fydo suitable for small retail businesses?",
          answer:
            "Yes. Fydo is designed for small and mid-sized retailers including grocery stores, salons, restaurants, pharmacies, clothing stores, and specialty businesses.",
        },
        {
          question: "How can I encourage inactive customers to return?",
          answer:
            "Businesses can use cashback, loyalty points, discounts, coupons, personalized offers, and timely communication to give customers a reason to come back.",
        },
        {
          question: "Can I target customers who haven't purchased recently?",
          answer:
            "Yes. Customer activity and purchase information can be used to identify shoppers who may need re-engagement.",
        },
        {
          question: "Can I use WhatsApp to reconnect with customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, promotions, and updates with their customers.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo can work with or without a POS system, making it suitable for traditional offline retailers.",
        },
        {
          question: "Can reactivation campaigns work with my loyalty program?",
          answer:
            "Yes. You can combine customer re-engagement campaigns with cashback, points, discounts, coupons, and other loyalty benefits.",
        },
        {
          question: "Can this help increase repeat purchases?",
          answer:
            "Re-engagement campaigns can give inactive customers a timely reason to return and create additional opportunities for repeat purchases.",
        },
        {
          question: "Can Fydo also help retain active customers?",
          answer:
            "Yes. In addition to re-engaging inactive shoppers, Fydo can help businesses reward loyal customers, personalize offers, and maintain ongoing customer engagement.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Bring Back Customers Who Already Know Your Business",
      subtitle:
        "Your inactive customers have already discovered, visited, and purchased from your business. Give them a reason to return with digital rewards, personalized offers, loyalty benefits, and **Free WhatsApp Marketing**. Reconnect with your customers today and turn inactive shoppers into repeat buyers.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "automated customer reactivation",
      "inactivity based marketing automation",
      "whatsapp automation for retail",
      "customer retention automation",
    ],
  },

  "customer-churn-reduction": {
    slug: "customer-churn-reduction",
    cluster: "retention-recovery",
    showMerchantsSection: false,
    seo: {
      title: "Customer Churn Reduction Software for Retail",
      description:
        "Reduce customer churn with Fydo. Identify at-risk customers, improve engagement, reward loyalty, increase repeat purchases, and retain more customers with Free WhatsApp Marketing.",
      keywords: [
        "customer churn reduction",
        "customer churn reduction software",
        "customer churn reduction platform",
        "customer churn reduction solution",
        "customer churn management software",
        "customer churn prevention software",
        "customer retention software",
        "customer retention platform",
        "customer retention solution",
        "customer loyalty software",
        "customer engagement software",
        "customer churn reduction India",
        "customer churn reduction for retail",
        "retail customer churn",
        "customer churn prevention",
        "customer retention strategy",
        "customer retention management",
        "customer re-engagement",
        "inactive customer retention",
        "repeat customer software",
        "customer loyalty program",
        "digital loyalty program",
        "customer rewards platform",
        "personalized customer offers",
        "cashback loyalty program",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Customer Churn Reduction",
      title:
        "Customer Churn Reduction to Retain Customers & Increase Repeat Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Stop losing valuable customers to competitors. Build stronger relationships, identify at-risk shoppers, and encourage more customers to keep coming back with a powerful customer churn reduction solution for offline retail.",
        "Fydo helps retailers understand customer activity, identify disengagement, reward loyal shoppers, personalize offers, and reconnect with customers through digital loyalty and **Free WhatsApp Marketing**.",
        "Turn customer retention into a proactive growth strategy and get more value from the customers you already have.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need Customer Churn Reduction?",
      subHeading:
        "Customer churn doesn't always happen suddenly. A customer may gradually purchase less frequently, stop responding to offers, or eventually move to another business.",
      description: [],
      listLabel:
        "Without a structured approach to preventing customer churn, retailers often face:",
      points: [
        {
          title: "Customers Slowly Become Inactive",
          text: "A once-regular customer may gradually reduce visits without the business noticing the change.",
        },
        {
          title: "Loyal Customers Move to Competitors",
          text: "Without ongoing engagement and relevant incentives, customers can easily choose another store.",
        },
        {
          title: "Warning Signs Are Missed",
          text: "Changes in purchase frequency and customer activity can indicate that a shopper is becoming disengaged.",
        },
        {
          title: "Generic Promotions Have Limited Impact",
          text: "Sending identical offers to everyone may fail to address the needs of customers who are losing interest.",
        },
        {
          title: "No Consistent Customer Engagement",
          text: "Customers who don't hear from your business regularly may simply forget about it.",
        },
        {
          title: "Lost Repeat Revenue",
          text: "When existing customers stop purchasing, businesses lose the recurring sales those relationships could generate.",
        },
      ],
      endingLine:
        "**The result?** Customer relationships weaken, repeat purchases decline, and businesses have to work harder to replace customers they could have retained.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Customer Retention into a Proactive Strategy",
      description: [
        "Fydo's **customer churn reduction** platform helps businesses identify opportunities to retain customers before they become inactive.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Monitor customer activity and purchase behavior",
        "Identify customers who may be becoming inactive",
        "Reward customers for repeat purchases",
        "Create personalized offers and incentives",
        "Launch targeted retention campaigns",
        "Engage customers through **Free WhatsApp Marketing**",
        "Offer cashback, points, discounts, and coupons",
        "Reconnect with customers who show reduced engagement",
        "Track customer loyalty and activity",
        "Build long-term customer relationships",
      ],
      bottomLine:
        "Instead of waiting until customers disappear, Fydo helps retailers continuously engage, reward, and retain their existing customer base.",
    },
    featureSection: {
      heading: "Everything You Need for Effective Customer Churn Reduction",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Monitor Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand how customers interact with your business and identify changes in their purchasing behavior.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Track customer activity",
            "Monitor purchase patterns",
            "Identify repeat shoppers",
            "Understand engagement trends",
            "Find opportunities for retention",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Identify At-Risk Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Recognize customers who may be losing engagement and create opportunities to reconnect with them.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Identify inactive shoppers",
            "Monitor changes in purchase frequency",
            "Spot declining engagement",
            "Create targeted retention campaigns",
            "Encourage customers to return",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Reward Customers for Staying Loyal",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make continued engagement more valuable with flexible digital rewards.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "Cashback rewards",
            "Loyalty points",
            "Discounts",
            "Coupons",
            "Special incentives",
            "Repeat-purchase rewards",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with your customers and keep your business top of mind through **Free WhatsApp Marketing**.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Share personalized offers",
            "Announce new products",
            "Promote special sales",
            "Send campaign messages",
            "Re-engage less active customers",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Create Personalized Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers offers that are more relevant to their activity and relationship with your business.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Target specific customer groups",
            "Create customized promotions",
            "Reward high-value customers",
            "Offer relevant incentives",
            "Encourage repeat purchases",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Digital Loyalty & Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Replace manual loyalty processes with a simple digital customer experience.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [
            "Digital customer records",
            "QR-based engagement",
            "Automated reward tracking",
            "Digital offers",
            "Easy customer participation",
          ],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo is designed for both traditional retailers and businesses already using POS systems.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "No complicated hardware required",
            "Works with existing business processes",
            "Suitable for small and mid-sized retailers",
            "Simple merchant onboarding",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Retention for Every Type of Retail Business",
      subHeading:
        "Every retail business has customers who gradually become less active. Fydo helps businesses create timely opportunities to maintain those relationships.",
      useCases: [
        {
          title: "Grocery & Kirana/ General Stores",
          detail:
            "Encourage regular shoppers to maintain their purchase habits with rewards, cashback, and relevant offers.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Keep previous buyers engaged with new collections, seasonal promotions, and personalized incentives.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Encourage customers to return for their next appointment with timely reminders and rewards.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Strengthen repeat dining habits with cashback, loyalty rewards, and targeted promotions.",
        },
        {
          title: "Pharmacies",
          detail:
            "Maintain engagement with regular shoppers and create incentives for continued visits.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Build stronger relationships with customers through personalized campaigns and digital rewards.",
        },
      ],
      endingLine:
        "By staying connected with customers throughout their journey, businesses can create more opportunities to prevent disengagement and encourage repeat purchases.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Retention & Loyalty",
      description: "",
      benefits: [
        {
          title: "Retain More Existing Customers",
          text: "Give customers more reasons to continue choosing your business instead of allowing valuable relationships to fade.",
        },
        {
          title: "Increase Repeat Purchases",
          text: "Use rewards, offers, and timely engagement to encourage customers to purchase more frequently.",
        },
        {
          title: "Act Before Customers Leave",
          text: "Identify changes in customer activity and create opportunities to reconnect before a customer becomes completely inactive.",
        },
        {
          title: "Protect Repeat Revenue",
          text: "Keeping an existing customer engaged can create additional opportunities for recurring purchases.",
        },
        {
          title: "Personalize Customer Engagement",
          text: "Create more relevant promotions based on customer behavior, activity, and purchase history.",
        },
        {
          title: "Stay Connected with Customers",
          text: "Use digital campaigns and **Free WhatsApp Marketing** to keep your business connected with its customer base.",
        },
        {
          title: "Strengthen Customer Loyalty",
          text: "Reward customers for their continued purchases and make their relationship with your business more valuable.",
        },
        {
          title: "Understand Customer Behavior",
          text: "Use customer insights to identify engagement patterns and make more informed retention decisions.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage rewards, campaigns, customer engagement, and loyalty initiatives from one easy-to-use platform.",
        },
      ],
      endingLine:
        "The goal is simple: **identify disengagement early, strengthen the relationship, and give customers more reasons to stay.**",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps retailers strengthen customer relationships through digital loyalty, rewards, personalized engagement, and retention solutions. Businesses can use Fydo to stay connected with customers, reward repeat purchases, and create ongoing opportunities for customer engagement.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helps us stay connected with our customers, reward them for their purchases, and create more reasons for them to return.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Reduce Customer Churn in 3 Simple Steps",
      description: "Understand. Engage. Retain.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Understand",
          description:
            "Monitor customer activity and identify shoppers whose engagement or purchase frequency may be declining.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Engage",
          description:
            "Create relevant rewards, offers, and campaigns to reconnect with customers and keep them engaged through **Free WhatsApp Marketing**.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Retain",
          description:
            "Customers continue shopping, earning rewards, and engaging with your business—helping you build stronger long-term relationships.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is customer churn reduction?",
          answer:
            "Customer churn reduction refers to the strategies and tools businesses use to prevent existing customers from becoming inactive or switching to competitors.",
        },
        {
          question: "How does Fydo help reduce customer churn?",
          answer:
            "Fydo helps businesses monitor customer activity, reward repeat purchases, personalize offers, run retention campaigns, and engage customers through digital communication.",
        },
        {
          question: "Is Fydo suitable for small retail businesses?",
          answer:
            "Yes. Fydo is designed for small and mid-sized businesses including grocery stores, salons, restaurants, pharmacies, clothing stores, and specialty retailers.",
        },
        {
          question: "How can I identify customers who may stop purchasing?",
          answer:
            "Customer activity and purchase patterns can help businesses identify changes in engagement and create opportunities for timely re-engagement.",
        },
        {
          question: "What rewards can I use to retain customers?",
          answer:
            "Businesses can offer cashback, loyalty points, discounts, coupons, and other incentives based on their customer engagement strategy.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses share offers, promotions, product updates, and other customer communications.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for traditional offline retailers.",
        },
        {
          question: "Can I create personalized retention campaigns?",
          answer:
            "Yes. Businesses can create targeted campaigns and offers based on customer activity and purchasing behavior.",
        },
        {
          question: "Can Fydo help increase repeat purchases?",
          answer:
            "Fydo helps businesses engage customers, reward loyalty, and create incentives that can encourage customers to return and purchase again.",
        },
        {
          question: "Can Fydo also help bring back inactive customers?",
          answer:
            "Yes. In addition to ongoing retention, businesses can use rewards, campaigns, and digital communication to re-engage customers who have already become inactive.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Keep Your Best Customers Coming Back",
      subtitle:
        "Your existing customers are already familiar with your business. Keep them engaged with digital rewards, personalized offers, loyalty benefits, and **Free WhatsApp Marketing**. Identify opportunities early, strengthen customer relationships, and turn more existing shoppers into loyal repeat customers.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "customer churn prevention",
      "at risk customer detection",
      "retail customer retention",
      "visit frequency reporting",
    ],
  },

  "customer-reengagement-software": {
    slug: "customer-reengagement-software",
    cluster: "retention-recovery",
    showMerchantsSection: false,
    seo: {
      title: "Customer Re-Engagement Software for Retail",
      description:
        "Re-engage inactive customers with Fydo. Create targeted campaigns, personalized offers, loyalty rewards, and use Free WhatsApp Marketing to increase repeat purchases.",
      keywords: [
        "Customer Re-Engagement Software",
        "customer reengagement software",
        "customer re-engagement platform",
        "customer re-engagement solution",
        "customer re-engagement tool",
        "customer retention software",
        "customer win-back software",
        "customer reactivation software",
        "customer re-engagement campaigns",
        "inactive customer re-engagement",
        "customer retention strategy",
        "customer win-back campaigns",
        "customer reactivation",
        "customer engagement",
        "repeat customer marketing",
        "customer loyalty program",
        "digital loyalty program",
        "personalized offers",
        "loyalty rewards",
        "repeat purchases",
        "customer retention platform",
        "retail loyalty software",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Customer Reengagement Software",
      title:
        "Customer Re-Engagement Software to Bring Back Inactive Customers & Increase Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Reconnect with customers who have stopped purchasing and give them more reasons to return with **Customer Re-Engagement Software** designed for offline retail businesses.",
        "Fydo helps retailers identify inactive customers, create targeted offers, reward shoppers, and stay connected through **Free WhatsApp Marketing**.",
        "Instead of continuously spending on acquiring new customers, build a structured strategy to reconnect with people who already know your business and encourage them to shop again.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need Customer Re-Engagement Software?",
      subHeading:
        "Not every customer who stops visiting your store is lost forever. Customers can become inactive simply because they haven't received a relevant offer, reminder, reward, or reason to return.",
      description: [
        "Without a structured re-engagement strategy, these customers can quietly disappear from your active customer base.",
      ],
      listLabel: "Common challenges faced by retail businesses:",
      points: [
        {
          title: "Inactive Customers Go Unnoticed",
          text: "Businesses may not know which customers have stopped purchasing or becoming less active.",
        },
        {
          title: "Customers Forget About Your Business",
          text: "Without regular engagement, shoppers may move to competitors or simply stop thinking about your store.",
        },
        {
          title: "Generic Offers Don't Reconnect Customers",
          text: "A blanket discount may not be relevant enough to bring an inactive customer back.",
        },
        {
          title: "No Follow-Up After Purchase",
          text: "Customer relationships often end once the transaction is completed.",
        },
        {
          title: "Customer Data Isn't Used Effectively",
          text: "Purchase activity can provide valuable signals about when and how customers should be re-engaged.",
        },
        {
          title: "Acquisition Gets More Attention Than Retention",
          text: "Businesses often focus on finding new customers while overlooking existing customers who could return.",
        },
      ],
      endingLine:
        "**The result?** Customers who could potentially purchase again become inactive, reducing repeat sales and the value of your existing customer base.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Inactive Customers into Returning Customers",
      description: [
        "Fydo helps retailers create a structured customer re-engagement strategy using customer activity, rewards, personalized offers, campaigns, and digital communication.",
      ],
      listLabel: "With Fydo's **Customer Re-Engagement Software**, you can:",
      steps: [
        "**Identify inactive customers** based on their customer activity.",
        "**Create targeted re-engagement campaigns** for different customer groups.",
        "**Engage customers with Free WhatsApp Marketing.**",
        "**Send personalized offers** designed to encourage customers to return.",
        "**Reward customers** for coming back and making another purchase.",
        "**Promote products and special offers** to inactive shoppers.",
        "**Track customer engagement** after campaigns.",
        "**Recognize changes in customer activity.**",
        "**Encourage repeat purchases** from existing customers.",
        "**Build an ongoing retention strategy** instead of relying on one-time promotions.",
      ],
      bottomLine:
        "The goal is simple: reconnect with customers before they are permanently lost and give them a relevant reason to choose your business again.",
    },
    featureSection: {
      heading: "Everything You Need for Effective Customer Re-Engagement",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Identify Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer activity and identify shoppers who haven't purchased recently, giving you an opportunity to reconnect with them.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Create Targeted Re-Engagement Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Build campaigns specifically for inactive, occasional, frequent, or high-value customers instead of sending the same message to everyone.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Use **Free WhatsApp Marketing** to stay connected with customers and communicate relevant offers, rewards, and promotions.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalize Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create offers that are more relevant to individual customers based on their purchase activity and relationship with your business.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Reward Customers for Returning",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Use cashback, points, discounts, or other incentives to give inactive shoppers an additional reason to make their next purchase.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote Products & Special Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reconnect with customers by highlighting new products, seasonal promotions, special deals, or other relevant offers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Monitor customer activity and understand whether shoppers are responding to your campaigns and returning to your business.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Build a Digital Retention Strategy",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Move beyond manual customer lists and one-time promotions with a digital approach to customer re-engagement.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your retention and engagement activities whether or not your business uses a traditional POS system.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Re-Engagement for Every Type of Retail Business",
      subHeading: "",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reconnect with customers who haven't visited recently and encourage them to return for their regular household purchases.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Bring back customers with new collection updates, seasonal offers, personalized promotions, and loyalty rewards.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Reconnect with customers who haven't booked a service recently and encourage them to schedule their next visit.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Engage customers who haven't visited recently with relevant offers, rewards, and reasons to return.",
        },
        {
          title: "Pharmacies",
          detail:
            "Stay connected with regular customers and encourage continued engagement through loyalty rewards and relevant communication.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Re-engage previous shoppers with new product promotions, personalized offers, and digital rewards.",
        },
      ],
      endingLine:
        "Across retail categories, Fydo helps businesses make better use of their existing customer base and create more opportunities for repeat business.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Re-Engagement",
      description: "",
      benefits: [
        {
          title: "Bring Back Inactive Customers",
          text: "Reconnect with shoppers who have become inactive and create opportunities for them to return.",
        },
        {
          title: "Increase Repeat Purchases",
          text: "Give existing customers relevant reasons and incentives to make another purchase.",
        },
        {
          title: "Improve Customer Retention",
          text: "Focus on keeping customers engaged instead of relying entirely on new customer acquisition.",
        },
        {
          title: "Recover Lost Sales Opportunities",
          text: "Re-engage customers who already know your business and may be more familiar with your products and services.",
        },
        {
          title: "Personalize Customer Experiences",
          text: "Create more relevant offers based on customer activity instead of relying on generic promotions.",
        },
        {
          title: "Strengthen Customer Relationships",
          text: "Stay connected with shoppers beyond individual transactions and build longer-term relationships.",
        },
        {
          title: "Make Better Use of Customer Data",
          text: "Use customer activity and purchase patterns to identify opportunities for engagement and retention.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Stay connected with customers through **Free WhatsApp Marketing** and support your re-engagement campaigns with direct communication.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Fydo makes digital customer retention and engagement accessible to offline retailers without complicated processes.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline retailers reconnect with inactive customers, strengthen customer relationships, improve engagement, and create more opportunities for repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helps us stay connected with customers even after they stop visiting regularly, giving us a better way to bring them back.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Re-Engage Customers in 3 Simple Steps",
      description:
        "This creates a simple cycle of identifying, engaging, and retaining customers.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Identify Inactive Customers",
          description:
            "Use customer activity to identify shoppers who haven't purchased recently and may be ready for re-engagement.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create an Offer or Campaign",
          description:
            "Set up relevant rewards, personalized offers, or targeted campaigns to give customers a reason to return.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use **Free WhatsApp Marketing**, loyalty rewards, and personalized communication to reconnect with customers and encourage their next purchase.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is Customer Re-Engagement Software?",
          answer:
            "Customer Re-Engagement Software helps businesses identify inactive customers and engage them through targeted campaigns, personalized offers, rewards, and communication designed to encourage them to return.",
        },
        {
          question: "How does Fydo help re-engage inactive customers?",
          answer:
            "Fydo helps businesses identify customer activity, create targeted campaigns, offer rewards and personalized promotions, and engage customers through digital loyalty and Free WhatsApp Marketing.",
        },
        {
          question: "Can small retail businesses use this solution?",
          answer:
            "Yes. Fydo is designed for small and mid-sized offline businesses across grocery, fashion, salons, restaurants, pharmacies, and other retail categories.",
        },
        {
          question: "How can I identify inactive customers?",
          answer:
            "Fydo uses customer activity to help businesses identify shoppers who have not purchased recently and create opportunities for re-engagement.",
        },
        {
          question: "What can I offer inactive customers?",
          answer:
            "You can use cashback, points, discounts, coupons, special offers, or other incentives depending on your business and customer retention strategy.",
        },
        {
          question: "Can I personalize re-engagement campaigns?",
          answer:
            "Yes. You can create targeted campaigns and offers based on customer activity and different customer groups.",
        },
        {
          question: "Can I use WhatsApp to re-engage customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses stay connected with customers and support their re-engagement activities.",
        },
        {
          question: "Do I need a POS system to use Fydo?",
          answer:
            "No. Fydo can work with or without a traditional POS system, making it suitable for different types of offline retailers.",
        },
        {
          question: "Can re-engagement campaigns increase repeat purchases?",
          answer:
            "Effective re-engagement campaigns can encourage inactive customers to return by providing relevant communication, rewards, offers, and reasons to shop again.",
        },
        {
          question: "Is Fydo only for retail stores?",
          answer:
            "No. Fydo can be used by Grocery & Kirana/General Stores, clothing and fashion businesses, salons, restaurants, pharmacies, lifestyle stores, and other offline customer-facing businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Bring Back Customers Who Already Know Your Business",
      subtitle:
        "Don't let inactive customers become permanently lost customers. Use Fydo to identify inactive shoppers, create personalized re-engagement campaigns, reward returning customers, and stay connected through **Free WhatsApp Marketing**. Turn your existing customer base into a stronger source of repeat business.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "customer re-engagement strategy",
      "retail customer habit building",
      "whatsapp reminders for shops",
      "personalized customer nudges",
    ],
  },

  "repeat-purchase-software": {
    slug: "repeat-purchase-software",
    cluster: "repeat-growth",
    showMerchantsSection: false,
    seo: {
      title: "Repeat Purchase Software for Retail Businesses",
      description:
        "Increase repeat purchases with Fydo. Reward customers, boost purchase frequency, personalize offers, build loyalty, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "repeat purchase software",
        "repeat purchase platform",
        "repeat purchase solution",
        "repeat customer software",
        "repeat sales software",
        "repeat purchase technology",
        "customer retention software",
        "customer retention platform",
        "customer loyalty software",
        "customer engagement software",
        "customer rewards platform",
        "repeat purchase software India",
        "repeat purchase software for retail",
        "increase repeat purchases",
        "increase customer purchase frequency",
        "repeat customer strategy",
        "repeat sales strategy",
        "customer retention strategy",
        "customer loyalty program",
        "digital loyalty program",
        "customer rewards program",
        "cashback loyalty program",
        "personalized customer offers",
        "customer engagement platform",
        "customer lifetime value",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Repeat Purchase Software",
      title: "Repeat Purchase Software to Increase Customer Frequency & Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn one-time shoppers into regular customers with a powerful repeat purchase solution designed for offline retail businesses.",
        "Fydo helps retailers encourage customers to shop more often through digital loyalty, cashback, rewards, personalized offers, targeted campaigns, and **Free WhatsApp Marketing**.",
        "Give your customers more reasons to return, increase purchase frequency, and build a stronger base of repeat buyers.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need Repeat Purchase Software?",
      subHeading:
        "A customer making one purchase is only the beginning. Long-term retail growth depends on getting customers to return and purchase again.",
      description: [],
      listLabel:
        "Without a structured strategy to encourage repeat purchases, retailers often face:",
      points: [
        {
          title: "Customers Purchase Only Once",
          text: "Shoppers may make an initial purchase but have no clear incentive to return.",
        },
        {
          title: "Low Purchase Frequency",
          text: "Even loyal customers may visit less often when there are no reminders, rewards, or relevant offers.",
        },
        {
          title: "Customers Switch to Competitors",
          text: "Without ongoing engagement, customers can easily choose another business when they are ready to buy again.",
        },
        {
          title: "No Reason to Come Back",
          text: "A good shopping experience may not be enough to create a habit of returning.",
        },
        {
          title: "Generic Promotions",
          text: "Sending the same promotion to every customer can make offers less relevant and less effective.",
        },
        {
          title: "Lost Repeat Revenue",
          text: "Every customer who doesn't return represents a missed opportunity for another sale.",
        },
      ],
      endingLine:
        "**The result?** Businesses continuously need to find new customers instead of maximizing the value of the customers they already have.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Purchase into a Reason to Buy Again",
      description: [
        "Fydo's **repeat purchase software** helps retailers create ongoing engagement that encourages customers to return more frequently.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Reward customers for repeat purchases",
        "Offer cashback and loyalty points",
        "Create discounts and digital coupons",
        "Send personalized offers",
        "Launch targeted customer campaigns",
        "Engage shoppers through **Free WhatsApp Marketing**",
        "Encourage customers to shop more frequently",
        "Reward high-value and loyal customers",
        "Track customer activity and engagement",
        "Build long-term customer relationships",
      ],
      bottomLine:
        "Instead of treating every transaction as a separate sale, Fydo helps you create a continuous customer journey that encourages shoppers to come back again and again.",
    },
    featureSection: {
      heading: "Everything You Need to Drive Repeat Purchases",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers for Coming Back",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give shoppers a tangible reason to choose your business again.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Cashback rewards",
            "Loyalty points",
            "Discounts",
            "Digital coupons",
            "Repeat-purchase rewards",
            "Customizable incentives",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Create Repeat Purchase Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Build campaigns designed to encourage customers to make their next purchase.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Target specific customer groups",
            "Create promotional campaigns",
            "Offer limited-time incentives",
            "Encourage repeat visits",
            "Promote relevant products and offers",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Keep your business connected with customers between purchases through **Free WhatsApp Marketing**.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "Share offers and promotions",
            "Announce new products",
            "Promote seasonal sales",
            "Send campaign messages",
            "Remind customers about your business",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalize Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make promotions more relevant by using customer activity and purchase behavior.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Create targeted offers",
            "Reward loyal shoppers",
            "Engage high-value customers",
            "Promote relevant products",
            "Encourage specific buying behaviors",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand how customers interact with your business and identify opportunities to encourage another purchase.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Monitor purchase activity",
            "Track customer engagement",
            "Identify repeat shoppers",
            "View reward usage",
            "Understand customer behavior",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Digital Loyalty Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Replace traditional loyalty cards and manual customer tracking with a simple digital experience.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [
            "Digital customer records",
            "QR-based engagement",
            "Digital rewards",
            "Easy customer participation",
            "Convenient reward management",
          ],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo is designed for traditional retailers as well as businesses using POS systems.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "No complicated hardware required",
            "Works alongside existing processes",
            "Suitable for small and mid-sized retailers",
            "Simple merchant setup",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Repeat Sales for Every Type of Retail Business",
      subHeading:
        "Customers in different industries have different buying cycles. Fydo helps businesses create relevant reasons for shoppers to return.",
      useCases: [
        {
          title: "Grocery & Kirana/ General Stores",
          detail:
            "Encourage regular household shoppers to visit more frequently with cashback, rewards, and personalized offers.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Bring previous buyers back with new collections, seasonal campaigns, and exclusive promotions.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Encourage customers to return for their next appointment with rewards and timely offers.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Build repeat dining habits with cashback, loyalty benefits, and targeted promotions.",
        },
        {
          title: "Pharmacies",
          detail:
            "Stay connected with regular customers and encourage continued visits through digital rewards.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Encourage previous shoppers to return with personalized promotions and loyalty incentives.",
        },
      ],
      endingLine:
        "By creating consistent engagement between purchases, businesses can turn occasional shoppers into customers who return more often.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Repeat Sales & Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Purchase Frequency",
          text: "Encourage customers to shop more regularly by giving them meaningful reasons to return.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Turn existing customer relationships into additional sales opportunities.",
        },
        {
          title: "Build Customer Loyalty",
          text: "Reward customers for continuing to choose your business and create stronger long-term relationships.",
        },
        {
          title: "Make Every Purchase More Rewarding",
          text: "Use cashback, points, discounts, and coupons to add value to the customer's next visit.",
        },
        {
          title: "Stay Connected Between Purchases",
          text: "Keep your business top of mind through digital communication and **Free WhatsApp Marketing**.",
        },
        {
          title: "Personalize Customer Engagement",
          text: "Create more relevant offers based on customer activity and purchasing behavior.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Encouraging customers to purchase repeatedly can help businesses generate more value from existing relationships.",
        },
        {
          title: "Understand Buying Behavior",
          text: "Use customer activity and engagement insights to better understand your shoppers and identify opportunities for repeat business.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage loyalty, rewards, offers, and customer engagement from one easy-to-use platform.",
        },
      ],
      endingLine:
        "The goal is simple: **make the next purchase easier, more valuable, and more rewarding for your customers.**",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps retailers create stronger customer relationships through digital loyalty, rewards, personalized offers, and customer engagement. Businesses can use Fydo to reward loyal shoppers, stay connected with customers, and create more opportunities for repeat sales.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helps us stay connected with our customers, reward them for their purchases, and create more reasons for them to return.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Increase Repeat Purchases in 3 Simple Steps",
      description: "Shop. Reward. Return. Repeat.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up",
          description:
            "Create your business profile, configure your rewards, and set up your digital loyalty and engagement program.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn",
          description:
            "Customers purchase from your business and receive cashback, points, discounts, or other rewards.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Customers Return & Redeem",
          description:
            "Customers come back to use their rewards while you continue engaging them through offers, campaigns, and **Free WhatsApp Marketing**.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is repeat purchase software?",
          answer:
            "Repeat purchase software helps businesses encourage existing customers to make additional purchases by using loyalty programs, rewards, personalized offers, and customer engagement.",
        },
        {
          question: "How does Fydo encourage customers to purchase again?",
          answer:
            "Fydo helps businesses offer cashback, loyalty points, discounts, coupons, personalized offers, and targeted campaigns that give customers more reasons to return.",
        },
        {
          question: "Is Fydo suitable for small retail businesses?",
          answer:
            "Yes. Fydo is designed for small and mid-sized retailers including grocery stores, salons, restaurants, pharmacies, clothing stores, and specialty businesses.",
        },
        {
          question: "What rewards can I offer returning customers?",
          answer:
            "You can offer cashback, loyalty points, discounts, coupons, and other customized incentives.",
        },
        {
          question: "Can I encourage customers to shop more frequently?",
          answer:
            "Yes. Businesses can use loyalty rewards, targeted campaigns, personalized offers, and customer communication to encourage more frequent purchases.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, promotions, product updates, and campaigns.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for traditional offline retailers.",
        },
        {
          question: "Can I personalize offers for different customers?",
          answer:
            "Yes. Businesses can create targeted campaigns and offers based on customer activity and purchase behavior.",
        },
        {
          question: "Can this help increase customer lifetime value?",
          answer:
            "Encouraging existing customers to return and purchase more frequently can create additional opportunities to increase customer lifetime value.",
        },
        {
          question: "Can Fydo also help with customer retention?",
          answer:
            "Yes. Along with encouraging repeat purchases, Fydo helps businesses reward loyal customers, engage shoppers, personalize offers, and build long-term customer relationships.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Give Customers More Reasons to Come Back",
      subtitle:
        "Your existing customers are your biggest opportunity for repeat sales. Give them more reasons to return with digital rewards, personalized offers, loyalty benefits, and **Free WhatsApp Marketing**. Increase purchase frequency, strengthen customer loyalty, and turn more shoppers into regular customers.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "repeat purchase rate",
      "cashback loyalty program",
      "milestone rewards for customers",
      "coupon expiry urgency",
    ],
  },

  "repeat-customer-platform": {
    slug: "repeat-customer-platform",
    cluster: "repeat-growth",
    showMerchantsSection: false,
    seo: {
      title: "Repeat Customer Platform for Retail Businesses",
      description:
        "Turn shoppers into loyal repeat customers with Fydo's repeat customer platform. Reward purchases, increase repeat sales, personalize offers, and engage customers with Free WhatsApp Marketing.",
      keywords: [
        "repeat customer platform",
        "repeat customer platform India",
        "repeat customer software",
        "repeat customer solution",
        "repeat customer management platform",
        "repeat sales platform",
        "repeat purchase platform",
        "customer retention platform",
        "customer retention software",
        "customer loyalty platform",
        "customer engagement platform",
        "repeat customer platform for retail",
        "increase repeat customers",
        "increase repeat sales",
        "returning customer software",
        "repeat purchase software",
        "customer loyalty software",
        "digital loyalty program",
        "customer rewards platform",
        "customer retention strategy",
        "customer engagement software",
        "customer lifetime value",
        "cashback loyalty program",
        "personalized customer offers",
        "customer loyalty solution",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Repeat Customer Platform",
      title:
        "Repeat Customer Platform to Increase Customer Loyalty & Repeat Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn one-time shoppers into loyal, returning customers with a powerful repeat customer platform designed for offline retail businesses.",
        "Fydo helps retailers build lasting customer relationships, encourage repeat purchases, reward loyal shoppers, and increase customer lifetime value through digital loyalty, cashback, personalized offers, and **Free WhatsApp Marketing**.",
        "Give your customers more reasons to return and make every purchase the beginning of the next one.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need a Repeat Customer Platform?",
      subHeading:
        "Acquiring a customer is only the beginning. Sustainable retail growth comes from encouraging customers to return, purchase more often, and build a long-term relationship with your business.",
      description: [],
      listLabel:
        "Without a structured system for increasing repeat customers, retailers often face:",
      points: [
        {
          title: "Customers Don't Return Regularly",
          text: "A customer may make a purchase once but have no clear reason or incentive to visit again.",
        },
        {
          title: "Low Customer Loyalty",
          text: "Without rewards and ongoing engagement, shoppers can easily move to competitors.",
        },
        {
          title: "No Consistent Customer Follow-Up",
          text: "Once a transaction is completed, businesses often have limited ways to stay connected with customers.",
        },
        {
          title: "Generic Offers Don't Drive Engagement",
          text: "Sending the same promotion to everyone makes it difficult to create relevant reasons for customers to return.",
        },
        {
          title: "Customer Data Goes Unused",
          text: "Purchase history and customer activity can reveal valuable opportunities to encourage another purchase.",
        },
        {
          title: "Overdependence on New Customers",
          text: "Continuously acquiring new shoppers can be challenging when existing customers could generate more repeat revenue.",
        },
      ],
      endingLine:
        "**The result?** Lower purchase frequency, weaker customer relationships, and missed opportunities to turn existing shoppers into regular customers.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Purchase into a Reason to Return",
      description: [
        "Fydo's **repeat customer platform** helps retailers create ongoing engagement that encourages shoppers to return and purchase again.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Build digital loyalty programs",
        "Reward customers for purchases",
        "Offer cashback, points, discounts, and coupons",
        "Create personalized customer offers",
        "Launch targeted campaigns",
        "Engage customers through **Free WhatsApp Marketing**",
        "Encourage more frequent purchases",
        "Reward high-value and loyal customers",
        "Track customer activity and engagement",
        "Build long-term customer relationships",
      ],
      bottomLine:
        "Instead of focusing only on the next transaction, Fydo helps businesses create an ongoing customer experience that encourages shoppers to keep coming back.",
    },
    featureSection: {
      heading: "Everything You Need to Build a Strong Repeat Customer Base",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers for Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a reason to choose your business again.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Cashback rewards",
            "Loyalty points",
            "Digital discounts",
            "Coupons",
            "Repeat-purchase incentives",
            "Customizable rewards",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers between purchases through **Free WhatsApp Marketing**.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share personalized offers",
            "Promote new products",
            "Announce special sales",
            "Send promotional campaigns",
            "Reconnect with less-active customers",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Personalized Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make your promotions more relevant by using customer activity and purchasing behavior.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "Create targeted campaigns",
            "Reward loyal shoppers",
            "Offer customer-specific incentives",
            "Promote relevant products",
            "Encourage repeat purchases",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand how customers interact with your business and identify opportunities to improve engagement.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Monitor customer purchases",
            "Track customer activity",
            "Identify repeat shoppers",
            "View reward usage",
            "Understand engagement trends",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Build Digital Loyalty",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Replace traditional loyalty cards and manual tracking with a convenient digital experience.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Digital customer records",
            "QR-based engagement",
            "Digital rewards",
            "Easy customer participation",
            "Simple reward management",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Create Ongoing Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Keep your customers connected beyond individual transactions.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [
            "Promotional campaigns",
            "Customer rewards",
            "Personalized communication",
            "Digital offers",
            "Loyalty-based engagement",
          ],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo is designed for traditional retailers as well as businesses already using POS systems.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "No complicated hardware required",
            "Works alongside existing processes",
            "Suitable for small and mid-sized retailers",
            "Simple merchant onboarding",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Repeat Customers for Every Type of Retail Business",
      subHeading:
        "Every retail business can benefit from stronger customer relationships and more frequent repeat purchases.",
      useCases: [
        {
          title: "Grocery & Kirana/ General Stores",
          detail:
            "Encourage regular shoppers to return for their everyday purchases with cashback, rewards, and personalized offers.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Bring previous buyers back with new collections, seasonal promotions, and exclusive rewards.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Encourage customers to return for their next service with timely offers and loyalty benefits.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Build repeat dining habits through cashback, digital rewards, and targeted promotions.",
        },
        {
          title: "Pharmacies",
          detail:
            "Maintain relationships with regular shoppers and encourage continued visits through loyalty incentives.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Reconnect with previous customers through relevant offers, personalized campaigns, and digital rewards.",
        },
      ],
      endingLine:
        "By creating consistent reasons to engage, businesses can turn occasional shoppers into customers who return more frequently.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Repeat Sales & Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Returning Customers",
          text: "Give shoppers compelling reasons to choose your business again instead of making every sale a one-time transaction.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Turn existing customer relationships into additional sales opportunities through rewards, offers, and engagement.",
        },
        {
          title: "Build Long-Term Loyalty",
          text: "Create stronger relationships by consistently rewarding and engaging customers.",
        },
        {
          title: "Make Shopping More Rewarding",
          text: "Use cashback, points, discounts, and coupons to add value to every customer interaction.",
        },
        {
          title: "Stay Connected Between Purchases",
          text: "Keep your business top of mind with digital campaigns and **Free WhatsApp Marketing**.",
        },
        {
          title: "Personalize Customer Experiences",
          text: "Create more relevant offers based on customer activity and purchasing behavior.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Encouraging customers to purchase repeatedly can help businesses generate more value from existing relationships.",
        },
        {
          title: "Understand Your Customers Better",
          text: "Use customer activity and engagement insights to identify loyal shoppers and opportunities for additional purchases.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage loyalty, rewards, campaigns, and customer engagement from one easy-to-use platform.",
        },
      ],
      endingLine:
        "The objective is simple: **turn more customers into regular customers and more purchases into long-term relationships.**",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps retailers strengthen customer relationships through digital loyalty, rewards, personalized offers, and customer engagement. Businesses can use Fydo to reward shoppers, stay connected with customers, and create more opportunities for repeat sales.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helps us stay connected with our customers, reward them for their purchases, and create more reasons for them to return.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Build a Repeat Customer Base in 3 Simple Steps",
      description: "Shop. Reward. Return. Repeat.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up",
          description:
            "Create your business profile, configure your rewards, and set up your digital loyalty and engagement program.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn",
          description:
            "Customers purchase from your business and receive cashback, points, discounts, or other rewards.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Customers Return & Redeem",
          description:
            "Customers come back to use their rewards while you continue engaging them through offers, campaigns, and **Free WhatsApp Marketing**.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a repeat customer platform?",
          answer:
            "A repeat customer platform helps businesses encourage existing shoppers to return and purchase again through loyalty programs, rewards, personalized offers, and customer engagement.",
        },
        {
          question: "How does Fydo help increase repeat customers?",
          answer:
            "Fydo combines digital loyalty, rewards, cashback, personalized offers, campaigns, and customer communication to give shoppers more reasons to return.",
        },
        {
          question: "Is Fydo suitable for small retail businesses?",
          answer:
            "Yes. Fydo is designed for small and mid-sized retailers including grocery stores, salons, restaurants, pharmacies, clothing stores, and specialty businesses.",
        },
        {
          question: "What rewards can I offer customers?",
          answer:
            "You can offer cashback, loyalty points, discounts, coupons, and other customized rewards based on your business requirements.",
        },
        {
          question: "Can I encourage customers to purchase more frequently?",
          answer:
            "Yes. Businesses can use rewards, personalized offers, targeted campaigns, and customer communication to encourage more frequent purchases.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, promotions, new products, and campaigns to customers.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for traditional offline retailers.",
        },
        {
          question: "Can I personalize offers for different customers?",
          answer:
            "Yes. Businesses can create targeted campaigns and offers based on customer activity and purchasing behavior.",
        },
        {
          question: "Can this help improve customer lifetime value?",
          answer:
            "Encouraging existing customers to return and purchase more frequently can create additional opportunities to increase customer lifetime value.",
        },
        {
          question: "Can Fydo also help with inactive customers?",
          answer:
            "Yes. Businesses can use campaigns, rewards, personalized offers, and digital communication to re-engage customers who have become less active.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn More Shoppers into Regular Customers",
      subtitle:
        "Your existing customers are one of your biggest opportunities for sustainable growth. Give them more reasons to return with digital rewards, personalized offers, loyalty benefits, and **Free WhatsApp Marketing**. Build stronger customer relationships, increase repeat sales, and turn occasional shoppers into loyal customers.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "repeat customer tracking",
      "customer segmentation for retail",
      "customer retention dashboard",
      "repeat purchase rate",
    ],
  },

  "automated-loyalty-campaigns": {
    slug: "automated-loyalty-campaigns",
    cluster: "repeat-growth",
    showMerchantsSection: false,
    seo: {
      title: "Automated Loyalty Campaigns for Retail Businesses",
      description:
        "Automate customer loyalty with Fydo. Create targeted campaigns, reward customers, personalize offers, increase repeat purchases, and engage customers with Free WhatsApp Marketing.",
      keywords: [
        "Automated Loyalty Campaigns",
        "automated loyalty program",
        "automated loyalty software",
        "automated customer loyalty",
        "loyalty campaign automation",
        "automated customer engagement",
        "loyalty campaign software",
        "automated rewards program",
        "customer loyalty automation",
        "customer loyalty program",
        "digital loyalty program",
        "customer retention software",
        "customer engagement",
        "loyalty rewards",
        "repeat purchase marketing",
        "customer re-engagement",
        "personalized offers",
        "digital rewards platform",
        "retail loyalty software",
        "customer rewards platform",
        "Free WhatsApp Marketing",
        "repeat customer strategy",
        "loyalty automation",
      ],
    },
    heroSection: {
      badgeText: "Automated Loyalty Campaigns",
      title: "Automated Loyalty Campaigns to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Make customer loyalty easier to manage with **Automated Loyalty Campaigns** that help you reward customers, engage them at the right time, and encourage repeat purchases.",
        "Fydo helps offline retail businesses automate customer loyalty and engagement through digital rewards, personalized offers, targeted campaigns, and **Free WhatsApp Marketing**.",
        "Instead of manually managing every customer promotion, use Fydo to create a consistent loyalty experience that keeps customers engaged and gives them more reasons to return.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need Automated Loyalty Campaigns?",
      subHeading:
        "Customer loyalty doesn't happen with a single discount or one-time promotion. Businesses need to consistently engage customers and give them relevant reasons to shop again.",
      description: [
        "However, managing customer campaigns manually can become difficult as your customer base grows.",
      ],
      listLabel: "Common challenges faced by retail businesses:",
      points: [
        {
          title: "Manual Campaign Management",
          text: "Creating and managing individual customer campaigns can take significant time and effort.",
        },
        {
          title: "Customers Are Easily Forgotten",
          text: "Without ongoing engagement, customers may purchase once and never return.",
        },
        {
          title: "Generic Offers Lack Impact",
          text: "Sending the same promotion to every customer may not deliver the right incentive to each shopper.",
        },
        {
          title: "Inactive Customers Go Unnoticed",
          text: "Businesses often don't know which customers have stopped purchasing until it is too late.",
        },
        {
          title: "Inconsistent Customer Engagement",
          text: "Irregular communication makes it harder to build long-term relationships.",
        },
        {
          title: "Loyal Customers Aren't Recognized",
          text: "Frequent shoppers should receive rewards and offers that acknowledge their continued business.",
        },
      ],
      endingLine:
        "**The result?** Your business may spend money attracting customers while missing opportunities to retain and re-engage the customers you already have.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Customer Activity into Automated Loyalty Opportunities",
      description: [
        "Fydo helps you simplify loyalty campaign management by combining customer data, rewards, offers, and communication in one platform.",
      ],
      listLabel: "With Fydo's **Automated Loyalty Campaigns**, you can:",
      steps: [
        "**Reward customers automatically** based on their purchases and loyalty.",
        "**Create targeted customer campaigns** for different customer groups.",
        "**Engage customers through Free WhatsApp Marketing.**",
        "**Personalize offers** based on customer activity.",
        "**Encourage repeat purchases** with relevant rewards and incentives.",
        "**Re-engage inactive customers** with timely offers.",
        "**Recognize frequent customers** with loyalty rewards.",
        "**Track customer activity and engagement.**",
        "**Reduce manual campaign management.**",
        "**Create an ongoing customer engagement cycle** instead of relying on one-time promotions.",
      ],
      bottomLine:
        "With the right automation, your loyalty strategy can continue working in the background while you focus on running your business.",
    },
    featureSection: {
      heading: "Everything You Need to Run Automated Loyalty Campaigns",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Automate Customer Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create reward structures that encourage customers to continue shopping and increase their engagement with your business.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Create Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Build campaigns for new customers, repeat shoppers, inactive customers, high-value customers, and other customer segments.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Use **Free WhatsApp Marketing** to stay connected with customers and communicate relevant offers, rewards, and promotions.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalize Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers offers that are more relevant to their purchase behavior and relationship with your business.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Identify customers who have become less active and give them a timely reason to return.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Reward Repeat Purchases",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Encourage customers to shop more frequently by connecting purchases with cashback, points, discounts, or other rewards.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Monitor customer activity and understand how shoppers interact with your loyalty initiatives.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Digital & Easy to Manage",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Move away from paper-based loyalty programs and manual customer records with a digital solution.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your customer loyalty strategy even if your business does not use a traditional POS system.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading:
        "Automated Customer Engagement for Every Type of Retail Business",
      subHeading: "",
      useCases: [
        {
          title: "Grocery & Kirana/ General Stores",
          detail:
            "Reward regular shoppers, promote relevant offers, and encourage customers to return for their everyday purchases.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Create campaigns around new collections, seasonal shopping, special offers, and repeat purchases.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Encourage customers to return for their next service with rewards, personalized offers, and timely engagement.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Reward frequent diners and promote offers that encourage customers to visit again.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger relationships with regular customers through rewards and ongoing customer communication.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Engage customers with personalized promotions, digital rewards, and campaigns based on their shopping activity.",
        },
      ],
      endingLine:
        "From neighborhood retailers to growing multi-location businesses, Fydo helps create a more consistent and scalable customer engagement strategy.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Automated Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give customers consistent reasons to return through rewards, offers, and timely engagement.",
        },
        {
          title: "Reduce Manual Work",
          text: "Simplify campaign management and spend less time handling customer engagement activities manually.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Encourage customers to purchase more frequently through relevant incentives and loyalty rewards.",
        },
        {
          title: "Improve Customer Retention",
          text: "Build ongoing relationships instead of relying only on acquiring new customers.",
        },
        {
          title: "Personalize Customer Engagement",
          text: "Create more relevant experiences by using customer activity to guide your offers and campaigns.",
        },
        {
          title: "Re-Engage Inactive Customers",
          text: "Bring back customers who have stopped purchasing with targeted incentives and communication.",
        },
        {
          title: "Build Stronger Customer Loyalty",
          text: "Make customers feel recognized and rewarded for choosing your business repeatedly.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Stay connected with customers through **Free WhatsApp Marketing** and support your loyalty and retention campaigns on a familiar communication channel.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Fydo is designed to make digital loyalty and customer engagement accessible to offline retailers.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline retailers build stronger customer relationships, reward shoppers, improve engagement, and create more opportunities for repeat business.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo makes it easier to stay connected with customers, reward their loyalty, and encourage them to keep coming back.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Automated Loyalty Campaigns in 3 Simple Steps",
      description:
        "A simple setup can help you build a continuous customer engagement cycle without managing every interaction manually.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Loyalty Strategy",
          description:
            "Configure your rewards, offers, customer segments, and campaign requirements on Fydo.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Engage",
          description:
            "Customers make purchases, earn eligible rewards, and interact with your digital loyalty experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Encourage the Next Purchase",
          description:
            "Use automated campaigns, personalized offers, and **Free WhatsApp Marketing** to keep customers engaged and encourage them to return.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What are Automated Loyalty Campaigns?",
          answer:
            "Automated Loyalty Campaigns are customer engagement campaigns that use customer activity, purchases, rewards, and targeted communication to consistently encourage loyalty and repeat purchases with less manual effort.",
        },
        {
          question: "How does Fydo automate customer loyalty?",
          answer:
            "Fydo combines digital rewards, customer activity, targeted offers, campaigns, and WhatsApp-based engagement to help businesses manage ongoing customer retention activities.",
        },
        {
          question: "Can small businesses use automated loyalty campaigns?",
          answer:
            "Yes. Fydo is designed for small and mid-sized offline retailers as well as growing businesses across multiple retail categories.",
        },
        {
          question: "What types of loyalty rewards can I offer?",
          answer:
            "Depending on your strategy, you can offer cashback, points, discounts, coupons, and other incentives to encourage repeat purchases.",
        },
        {
          question: "Can I create campaigns for different types of customers?",
          answer:
            "Yes. Businesses can create targeted campaigns for customer groups such as new shoppers, frequent customers, inactive customers, and high-value customers.",
        },
        {
          question:
            "Can automated campaigns help bring back inactive customers?",
          answer:
            "Yes. Targeted re-engagement campaigns can give inactive shoppers relevant offers and incentives that encourage them to return.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo's loyalty solution can work with or without a traditional POS system.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Free WhatsApp Marketing is a key Fydo benefit that helps businesses communicate with customers and support their engagement campaigns.",
        },
        {
          question: "Can these campaigns increase repeat purchases?",
          answer:
            "Loyalty campaigns can encourage repeat purchases by giving customers relevant rewards, offers, and reasons to continue shopping with your business.",
        },
        {
          question: "Is Fydo suitable only for retail stores?",
          answer:
            "No. Fydo can be used by grocery stores, salons, restaurants, pharmacies, fashion businesses, lifestyle stores, and other offline customer-facing businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Customer Engagement into Repeat Business",
      subtitle:
        "Stop relying on manual promotions and one-time discounts. Use Fydo to automate your loyalty strategy, reward customers, personalize engagement, and stay connected through **Free WhatsApp Marketing**. Give every customer more reasons to come back.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "automated whatsapp marketing",
      "customer retention automation",
      "birthday reward program",
      "customer win-back campaign",
    ],
  },

  "customer-engagement-software": {
    slug: "customer-engagement-software",
    cluster: "engagement-marketing",
    showMerchantsSection: false,
    seo: {
      title: "Customer Engagement Software for Retail Businesses",
      description:
        "Improve customer engagement with Fydo. Build loyalty, personalize offers, reward customers, increase repeat purchases, and stay connected with Free WhatsApp Marketing.",
      keywords: [
        "customer engagement software",
        "customer engagement platform",
        "customer engagement solution",
        "customer engagement tools",
        "customer engagement system",
        "customer engagement technology",
        "customer relationship engagement software",
        "customer loyalty software",
        "customer retention software",
        "customer retention platform",
        "customer loyalty platform",
        "customer engagement software India",
        "customer engagement software for retail",
        "retail customer engagement",
        "customer engagement platform for retail",
        "customer communication software",
        "customer engagement strategy",
        "customer loyalty program",
        "digital loyalty program",
        "customer rewards platform",
        "customer retention strategy",
        "repeat customer software",
        "repeat purchase software",
        "personalized customer offers",
        "cashback loyalty program",
        "WhatsApp customer engagement",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Customer Engagement Software",
      title:
        "Customer Engagement Software to Build Stronger Customer Relationships & Increase Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Keep your customers connected, engaged, and coming back with powerful customer engagement software designed for offline retail businesses.",
        "Fydo helps retailers build meaningful customer relationships through digital loyalty, cashback, rewards, personalized offers, targeted campaigns, and **Free WhatsApp Marketing**.",
        "From the first purchase to every interaction that follows, Fydo helps you create more opportunities to engage customers, increase repeat purchases, and build long-term loyalty.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need Customer Engagement Software?",
      subHeading:
        "A customer relationship shouldn't end when a transaction is completed. Regular communication and meaningful engagement can help businesses stay connected with customers and encourage them to return.",
      description: [],
      listLabel:
        "Without a structured engagement strategy, retailers often face:",
      points: [
        {
          title: "Customers Forget About Your Business",
          text: "Without regular communication, customers may simply move on to competitors after making a purchase.",
        },
        {
          title: "Low Customer Interaction",
          text: "Many businesses have limited ways to communicate with customers between purchases.",
        },
        {
          title: "Generic Offers Get Ignored",
          text: "Sending the same promotion to every customer can make marketing less relevant.",
        },
        {
          title: "Customer Loyalty Is Difficult to Build",
          text: "Without rewards or ongoing engagement, customers may have little reason to choose your business repeatedly.",
        },
        {
          title: "Customer Data Goes Unused",
          text: "Purchase history and customer activity can provide valuable insights, but many retailers don't use them to improve engagement.",
        },
        {
          title: "Missed Repeat Sales Opportunities",
          text: "Every customer interaction is an opportunity to encourage another purchase, but businesses often don't have the tools to do so consistently.",
        },
      ],
      endingLine:
        "**The result?** Customers become less engaged, repeat purchases decline, and businesses miss opportunities to build lasting customer relationships.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Customer Interaction into an Opportunity to Engage",
      description: [
        "Fydo's **customer engagement software** helps retailers connect with customers before, during, and after their purchases.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Create digital loyalty and rewards programs",
        "Reward customers for their purchases",
        "Offer cashback, points, discounts, and coupons",
        "Create personalized customer offers",
        "Launch targeted campaigns",
        "Engage customers through **Free WhatsApp Marketing**",
        "Promote new products and special offers",
        "Re-engage less-active customers",
        "Track customer activity and engagement",
        "Encourage repeat purchases",
      ],
      bottomLine:
        "Instead of relying only on one-time promotions, Fydo helps you create an ongoing customer experience that keeps your business connected with its shoppers.",
    },
    featureSection: {
      heading: "Everything You Need for Effective Customer Engagement",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers beyond the point of purchase through **Free WhatsApp Marketing**.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Share offers and promotions",
            "Announce new products",
            "Promote seasonal sales",
            "Send campaign messages",
            "Re-engage inactive customers",
            "Keep customers updated",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Customers for Their Purchases",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make every customer interaction more valuable with flexible digital rewards.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Cashback rewards",
            "Loyalty points",
            "Discounts",
            "Digital coupons",
            "Repeat-purchase incentives",
            "Customizable rewards",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Personalized Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make your communication more relevant by using customer activity and purchase behavior.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "Create targeted campaigns",
            "Offer personalized rewards",
            "Reward loyal customers",
            "Promote relevant products",
            "Create customer-specific incentives",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Run Targeted Customer Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reach different customer groups with campaigns designed around your business objectives.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Promotional campaigns",
            "Special offers",
            "One-day sales",
            "Customer-specific campaigns",
            "Multiple coupon codes",
            "Digital promotions",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand how customers interact with your business.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Monitor customer activity",
            "Track purchases",
            "Identify repeat shoppers",
            "View reward usage",
            "Understand engagement patterns",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Create a Digital Customer Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a convenient way to interact with your business.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [
            "QR-based engagement",
            "Digital rewards",
            "Easy customer identification",
            "Convenient loyalty experience",
            "Simple customer participation",
          ],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo works for traditional retailers as well as businesses already using POS systems.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "No complicated hardware required",
            "Works alongside existing processes",
            "Suitable for small and mid-sized retailers",
            "Easy merchant onboarding",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Engagement for Every Type of Retail Business",
      subHeading:
        "Every retail business can use consistent communication and meaningful rewards to build stronger relationships with its customers.",
      useCases: [
        {
          title: "Grocery & Kirana/ General Stores",
          detail:
            "Stay connected with regular shoppers, promote offers, and encourage customers to return for their everyday purchases.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Engage previous buyers with new collections, seasonal promotions, and personalized offers.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Keep customers connected between visits and encourage them to return for their next service.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Build stronger relationships with diners through cashback, loyalty rewards, special offers, and targeted campaigns.",
        },
        {
          title: "Pharmacies",
          detail:
            "Maintain communication with regular shoppers and create opportunities for continued engagement.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Use personalized promotions and loyalty rewards to keep previous customers connected with your business.",
        },
      ],
      endingLine:
        "By maintaining consistent engagement, businesses can turn occasional interactions into stronger and longer-lasting customer relationships.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Engagement & Loyalty",
      description: "",
      benefits: [
        {
          title: "Stay Connected with Customers",
          text: "Keep your business top of mind through regular digital communication, offers, and **Free WhatsApp Marketing**.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Create meaningful interactions beyond individual transactions and develop long-term customer connections.",
        },
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return through rewards, personalized offers, and targeted campaigns.",
        },
        {
          title: "Make Customers Feel Valued",
          text: "Reward shoppers for their purchases and continued engagement with cashback, points, discounts, and coupons.",
        },
        {
          title: "Deliver More Relevant Offers",
          text: "Create targeted promotions based on customer activity and purchasing behavior.",
        },
        {
          title: "Improve Customer Retention",
          text: "Consistent engagement can help businesses maintain relationships with existing customers and reduce customer inactivity.",
        },
        {
          title: "Understand Your Customers Better",
          text: "Use customer activity and engagement information to identify loyal shoppers and opportunities for further interaction.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Stronger engagement and more frequent purchases can create greater value from existing customer relationships.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage loyalty, rewards, campaigns, and customer communication from one easy-to-use platform.",
        },
      ],
      endingLine:
        "The goal is simple: **keep customers connected today so they have more reasons to shop with you tomorrow.**",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps retailers build stronger customer relationships through digital loyalty, rewards, personalized offers, and ongoing engagement. Businesses can use Fydo to stay connected with shoppers, reward loyalty, and create more opportunities for repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helps us stay connected with our customers, reward them for their purchases, and create more reasons for them to return.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Engaging Customers in 3 Simple Steps",
      description: "Engage. Reward. Connect. Repeat.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up",
          description:
            "Create your business profile, configure your loyalty rewards, and set up your digital customer engagement program.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Engage",
          description:
            "Customers make purchases and interact with your business while earning cashback, points, discounts, or other rewards.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Stay Connected & Bring Them Back",
          description:
            "Continue engaging customers through offers, campaigns, loyalty benefits, and **Free WhatsApp Marketing** to encourage future purchases.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is customer engagement software?",
          answer:
            "Customer engagement software helps businesses communicate with customers, personalize interactions, provide rewards, and build stronger relationships throughout the customer journey.",
        },
        {
          question: "How does Fydo help businesses engage customers?",
          answer:
            "Fydo combines digital loyalty, rewards, personalized offers, campaigns, customer insights, and Free WhatsApp Marketing to help retailers stay connected with shoppers.",
        },
        {
          question: "Is Fydo suitable for small retail businesses?",
          answer:
            "Yes. Fydo is designed for small and mid-sized retailers including grocery stores, salons, restaurants, pharmacies, clothing stores, and specialty businesses.",
        },
        {
          question: "How can I communicate offers to my customers?",
          answer:
            "Businesses can use campaigns and Free WhatsApp Marketing to communicate promotions, offers, product updates, and other customer messages.",
        },
        {
          question: "What types of rewards can I offer?",
          answer:
            "You can offer cashback, loyalty points, discounts, coupons, and other customized rewards.",
        },
        {
          question: "Can I personalize customer offers?",
          answer:
            "Yes. Businesses can create targeted campaigns and offers based on customer activity and purchasing behavior.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for traditional offline retailers.",
        },
        {
          question: "Can I use Fydo to re-engage inactive customers?",
          answer:
            "Yes. Businesses can use targeted campaigns, personalized offers, rewards, and digital communication to reconnect with customers who have become less active.",
        },
        {
          question: "Can better engagement increase repeat purchases?",
          answer:
            "Consistent communication, relevant offers, and loyalty rewards can create more opportunities for customers to return and purchase again.",
        },
        {
          question: "Is Fydo only for customer engagement?",
          answer:
            "No. Fydo also helps businesses manage loyalty rewards, cashback, offers, campaigns, customer retention, and repeat-purchase initiatives.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Keep Your Customers Connected & Coming Back",
      subtitle:
        "Customer relationships grow through consistent engagement. Give shoppers more reasons to stay connected with your business through digital rewards, personalized offers, loyalty benefits, and **Free WhatsApp Marketing**. Build stronger relationships, increase repeat purchases, and turn customer engagement into long-term business growth.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "customer engagement strategies for retail",
      "loyalty program software",
      "whatsapp marketing for shops",
      "customer segmentation retail",
    ],
  },

  "loyalty-marketing-software": {
    slug: "loyalty-marketing-software",
    cluster: "engagement-marketing",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Marketing Software for Retail Businesses",
      description:
        "Grow customer loyalty with Fydo's loyalty marketing software. Reward customers, personalize offers, increase repeat purchases, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Marketing Software",
        "loyalty marketing software for retail",
        "customer loyalty marketing software",
        "retail loyalty software",
        "loyalty program software",
        "loyalty campaign software",
        "customer loyalty platform",
        "loyalty marketing platform",
        "digital loyalty software",
        "customer loyalty program",
        "digital loyalty program",
        "customer retention software",
        "customer engagement",
        "customer rewards platform",
        "loyalty rewards",
        "personalized offers",
        "customer re-engagement",
        "repeat customer marketing",
        "repeat purchase strategy",
        "retail customer engagement",
        "loyalty campaigns",
        "digital rewards platform",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Marketing Software",
      title: "Loyalty Marketing Software to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Build stronger customer relationships and turn more shoppers into loyal, returning customers with **Loyalty Marketing Software** designed for offline retail businesses.",
        "Fydo combines digital loyalty, customer rewards, personalized offers, targeted campaigns, customer insights, and **Free WhatsApp Marketing** to help retailers engage customers beyond the point of purchase.",
        "Instead of relying only on discounts or traditional loyalty cards, Fydo helps you create an ongoing customer experience that encourages repeat purchases and long-term loyalty.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need Loyalty Marketing Software?",
      subHeading:
        "Customer acquisition can bring shoppers to your store, but consistent engagement is what can turn them into repeat customers.",
      description: [
        "Many offline retailers still manage loyalty and customer promotions manually, making it difficult to consistently communicate with customers and reward them effectively.",
      ],
      listLabel: "Common challenges faced by retail businesses:",
      points: [
        {
          title: "Customers Don't Return Regularly",
          text: "First-time shoppers may not have enough reasons to choose your business again.",
        },
        {
          title: "Limited Customer Communication",
          text: "Businesses often struggle to stay connected with customers after a purchase.",
        },
        {
          title: "Generic Promotions",
          text: "The same offer for every customer may not create meaningful engagement.",
        },
        {
          title: "Manual Loyalty Management",
          text: "Physical cards, spreadsheets, and manual records can make loyalty programs difficult to manage.",
        },
        {
          title: "Inactive Customers Go Unnoticed",
          text: "Without customer activity insights, businesses may miss opportunities to re-engage shoppers.",
        },
        {
          title: "Loyal Customers Aren't Properly Rewarded",
          text: "Frequent shoppers should receive relevant rewards that recognize their continued loyalty.",
        },
      ],
      endingLine:
        "**The result?** You may have many customers but still struggle to generate consistent repeat purchases and build lasting customer relationships.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Customer Purchases into Marketing Opportunities",
      description: [
        "Fydo brings loyalty and customer marketing together in one easy-to-use platform, helping retailers engage customers before, during, and after every purchase.",
      ],
      listLabel: "With Fydo's **Loyalty Marketing Software**, you can:",
      steps: [
        "**Reward customers** for their purchases and continued loyalty.",
        "**Create targeted loyalty campaigns** for different customer groups.",
        "**Engage customers with Free WhatsApp Marketing.**",
        "**Personalize offers** based on customer activity.",
        "**Promote products, offers, and special campaigns.**",
        "**Encourage repeat purchases** through cashback, points, discounts, and rewards.",
        "**Re-engage inactive customers** with timely incentives.",
        "**Track customer activity and engagement.**",
        "**Recognize frequent and high-value customers.**",
        "**Build long-term customer relationships** instead of relying on one-time promotions.",
      ],
      bottomLine:
        "This helps transform loyalty from a simple reward mechanism into a continuous customer marketing strategy.",
    },
    featureSection: {
      heading: "Everything You Need in Powerful Loyalty Marketing Software",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers for Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create cashback, points, discounts, and other rewards that give customers a reason to keep choosing your business.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with shoppers through **Free WhatsApp Marketing** and share relevant offers, rewards, promotions, and updates.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Personalized Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make your promotions more relevant by creating offers based on customer activity, purchase behavior, and loyalty.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Run Targeted Marketing Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create campaigns for new customers, regular shoppers, inactive customers, high-value customers, and other customer groups.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Identify customers who have become less active and give them timely offers or rewards to encourage them to return.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote Products & Special Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Use customer engagement campaigns to promote new products, seasonal promotions, special deals, and other business initiatives.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Monitor customer activity and understand how customers interact with your loyalty and marketing initiatives.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Build a Digital Loyalty Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Replace physical loyalty cards and manual records with a convenient digital rewards experience.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo supports businesses whether they use a traditional POS system or manage transactions without one.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty Marketing for Every Type of Retail Business",
      subHeading: "",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward regular shoppers, promote relevant offers, and encourage customers to return for their everyday purchases.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Engage customers with new collections, seasonal promotions, personalized offers, and loyalty rewards.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Encourage customers to return for their next service through rewards, special offers, and timely communication.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Reward frequent diners, promote special deals, and encourage customers to visit again.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger relationships with regular customers through rewards, relevant communication, and ongoing engagement.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Promote products, offers, and rewards while building a stronger base of returning customers.",
        },
      ],
      endingLine:
        "From neighborhood retailers to growing businesses, Fydo helps create a consistent customer engagement and retention strategy across different retail categories.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Loyalty & Marketing",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give shoppers more reasons to return with relevant rewards, offers, and ongoing engagement.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Encourage customers to purchase more frequently by connecting loyalty with targeted marketing.",
        },
        {
          title: "Build Long-Term Customer Loyalty",
          text: "Create meaningful customer relationships instead of relying only on transactional discounts.",
        },
        {
          title: "Improve Customer Retention",
          text: "Keep existing customers engaged and create more opportunities for repeat business.",
        },
        {
          title: "Personalize Customer Experiences",
          text: "Deliver more relevant offers by understanding customer activity and purchase behavior.",
        },
        {
          title: "Re-Engage Inactive Customers",
          text: "Bring back customers who haven't purchased recently with targeted incentives and communication.",
        },
        {
          title: "Recognize Loyal Customers",
          text: "Reward frequent shoppers and make them feel valued for their continued relationship with your business.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Stay connected with customers through **Free WhatsApp Marketing** and support your loyalty, promotional, and retention campaigns.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Fydo is designed for offline retailers, making digital loyalty and customer marketing easier to manage.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline retailers strengthen customer relationships, reward shoppers, improve engagement, and create more opportunities for repeat sales.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo gives us a simple way to reward customers, communicate with them, and keep them coming back to our business.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Loyalty Marketing in 3 Simple Steps",
      description:
        "A simple setup gives your business a continuous way to engage customers and build loyalty.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Loyalty Program",
          description:
            "Configure your rewards, offers, customer segments, and marketing campaigns on Fydo.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn",
          description:
            "Customers purchase from your business and receive eligible rewards through Fydo's digital loyalty experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Encourage Them to Return",
          description:
            "Use personalized offers, loyalty rewards, targeted campaigns, and **Free WhatsApp Marketing** to stay connected and encourage the next purchase.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is Loyalty Marketing Software?",
          answer:
            "Loyalty Marketing Software helps businesses combine customer rewards and marketing activities to engage shoppers, encourage repeat purchases, and build long-term customer relationships.",
        },
        {
          question: "How does Fydo's loyalty marketing solution work?",
          answer:
            "Fydo allows businesses to reward customers, create personalized offers, run targeted campaigns, track customer activity, and engage shoppers through digital loyalty and Free WhatsApp Marketing.",
        },
        {
          question: "Is Fydo suitable for small retail businesses?",
          answer:
            "Yes. Fydo is designed for small and mid-sized offline businesses across grocery, fashion, salons, restaurants, pharmacies, and other retail categories.",
        },
        {
          question: "What types of rewards can I offer?",
          answer:
            "You can offer cashback, points, discounts, coupons, and other incentives based on your loyalty strategy.",
        },
        {
          question: "Can I create different offers for different customers?",
          answer:
            "Yes. You can create targeted offers and campaigns for different customer groups based on their activity and relationship with your business.",
        },
        {
          question: "Can the platform help bring back inactive customers?",
          answer:
            "Yes. Businesses can use targeted re-engagement campaigns, personalized offers, and rewards to encourage inactive customers to return.",
        },
        {
          question: "Do I need a POS system to use Fydo?",
          answer:
            "No. Fydo can work with or without a traditional POS system, making it suitable for a wide range of offline retailers.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Free WhatsApp Marketing is a key Fydo benefit that helps businesses stay connected with customers and support their loyalty and engagement campaigns.",
        },
        {
          question: "Can loyalty marketing help increase repeat purchases?",
          answer:
            "A well-designed loyalty strategy can encourage customers to purchase more frequently by providing relevant rewards, personalized offers, and reasons to return.",
        },
        {
          question: "Is Fydo only for retail stores?",
          answer:
            "No. Fydo can be used by Grocery & Kirana/General Stores, clothing and fashion businesses, salons, restaurants, pharmacies, lifestyle stores, and other offline customer-facing businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Customer Loyalty into Repeat Business",
      subtitle:
        "Don't let customer relationships end after a purchase. Use Fydo to reward customers, create personalized offers, run targeted campaigns, and stay connected through **Free WhatsApp Marketing**. Give your customers more reasons to return and build a stronger base of loyal shoppers.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "loyalty program software",
      "whatsapp marketing for shops",
      "customer retention offers",
      "cashback loyalty program",
    ],
  },

  "loyalty-app-for-businesses": {
    slug: "loyalty-app-for-businesses",
    cluster: "app-general",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty App for Businesses in India",
      description:
        "Grow customer loyalty with Fydo's Loyalty App for Businesses. Reward customers, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty App for Businesses",
        "loyalty app for business",
        "loyalty app for small business",
        "customer loyalty app",
        "business loyalty app",
        "loyalty app India",
        "digital loyalty app",
        "customer rewards app",
        "loyalty program app",
        "digital rewards platform",
        "customer retention app",
        "retail loyalty app",
        "customer loyalty",
        "digital rewards",
        "loyalty rewards",
        "cashback rewards",
        "customer engagement",
        "repeat purchases",
        "customer retention",
        "personalized offers",
        "loyalty campaigns",
        "re-engagement",
        "returning customers",
        "digital offers",
        "customer rewards",
        "WhatsApp marketing",
        "loyalty program",
      ],
    },
    heroSection: {
      badgeText: "Loyalty App for Businesses",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty App for Businesses",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "A **Loyalty App for Businesses** helps you reward customers, increase repeat purchases, and build stronger relationships with the people who already shop with you.",
        "With Fydo, businesses can create digital rewards, offer cashback and discounts, run targeted campaigns, engage customers through **Free WhatsApp Marketing**, and encourage shoppers to come back more often.",
        "Turn everyday purchases into long-term customer relationships.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Business Need a Loyalty App for Businesses?",
      subHeading:
        "Getting customers to make their first purchase is only the beginning. The bigger challenge is giving them enough reasons to return.",
      description: [
        "Traditional loyalty methods often depend on physical cards, manual records, and generic offers. This makes it difficult for businesses to consistently engage existing customers.",
      ],
      listLabel: "Common challenges include:",
      points: [
        "Customers not returning after their first purchase",
        "Physical loyalty cards being lost or forgotten",
        "Manual tracking of customer rewards",
        "Generic discounts that do not encourage long-term loyalty",
        "Limited customer purchase information",
        "Difficulty reaching inactive customers",
        "No structured way to run loyalty campaigns",
        "Customer relationships ending after the transaction",
      ],
      endingLine:
        "A digital loyalty solution helps businesses move from one-time transactions to ongoing customer engagement.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Grow Customer Loyalty with a Simple Loyalty App for Businesses",
      description: [
        "Fydo provides businesses with a digital platform to manage customer rewards, offers, engagement, and retention from one place.",
        "Instead of relying on traditional loyalty cards or disconnected marketing activities, businesses can create a simple digital loyalty experience for their customers.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Reward customers for their purchases",
        "Offer cashback, points, discounts, and coupons",
        "Create personalized customer offers",
        "Run targeted loyalty campaigns",
        "Engage customers with **Free WhatsApp Marketing**",
        "Re-engage inactive customers",
        "Promote products and special offers",
        "Track customer activity",
        "Encourage repeat purchases",
        "Build long-term customer relationships",
      ],
      bottomLine: "One Platform to Reward, Engage & Retain Customers",
    },
    featureSection: {
      heading: "Powerful Features of Fydo's Loyalty App for Businesses",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Digital Customer Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers rewards for their purchases and create a reason to choose your business again.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Cashback & Points",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Choose reward mechanisms that work for your business, including cashback, points, discounts, and coupons.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** and share offers, rewards, promotions, and important updates.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Run campaigns for new customers, frequent shoppers, inactive customers, or specific customer groups.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Keep customers connected beyond the point of purchase with digital offers, rewards, and timely communication.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Customer Activity Tracking",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer interactions and identify opportunities to improve engagement and retention.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Product & Offer Promotion",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Promote new products, seasonal offers, special discounts, and exclusive deals.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can be used by businesses whether they have a POS system or manage transactions without one.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Digital Customer Loyalty for Every Type of Business",
      subHeading:
        "A modern rewards and engagement platform can help businesses across different offline retail categories.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward regular shoppers and encourage customers to make their next purchase from your store.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Use exclusive rewards and personalized offers to encourage customers to return for future purchases.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Reward repeat visits and use targeted offers to build stronger customer relationships.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Encourage customers to return with digital rewards, special offers, and personalized promotions.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build relationships with regular customers through convenient rewards and ongoing engagement.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Turn occasional shoppers into returning customers through personalized rewards and promotions.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers meaningful reasons to shop with you again.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Continue engaging customers even after their purchase.",
        },
        {
          title: "Reduce Dependence on Discounts",
          text: "Use loyalty rewards and personalized benefits instead of relying only on broad discounts.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Connect with shoppers through offers, rewards, campaigns, and WhatsApp communication.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Identify customers who have stopped purchasing and run targeted re-engagement campaigns.",
        },
        {
          title: "Make Loyalty Easier to Manage",
          text: "Manage customer rewards and engagement digitally from one platform.",
        },
        {
          title: "Create Personalized Experiences",
          text: "Use customer activity to deliver more relevant offers and promotions.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers directly through WhatsApp without requiring a separate marketing solution.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline businesses build stronger customer relationships through digital loyalty, rewards, engagement, and retention. With **10,000+ merchants and 1.6 lakh customers**, Fydo is helping businesses create more connected customer experiences and encourage repeat business.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Customer Loyalty Program in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Choose cashback, points, discounts, coupons, or other rewards that suit your business.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Set Up Your Reward Rule",
          description:
            "Choose cashback, points, or coupons directly from your phone",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Retain",
          description:
            "Use personalized offers, campaigns, rewards, and **Free WhatsApp Marketing** to encourage customers to return.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty App for Businesses?",
          answer:
            "A Loyalty App for Businesses is a digital solution that helps businesses reward customers, manage loyalty programs, engage shoppers, and encourage repeat purchases.",
        },
        {
          question: "How can a loyalty app help my business?",
          answer:
            "It can help you reward existing customers, increase repeat purchases, improve engagement, personalize offers, and build long-term customer relationships.",
        },
        {
          question: "Can small businesses use a digital loyalty platform?",
          answer:
            "Yes. Digital loyalty solutions can be used by businesses of different sizes, including local retailers, salons, restaurants, pharmacies, and specialty stores.",
        },
        {
          question: "What types of rewards can I offer customers?",
          answer:
            "You can offer cashback, points, discounts, coupons, and other promotional rewards depending on your business strategy.",
        },
        {
          question: "Does Fydo work without a POS system?",
          answer: "Yes. Fydo is designed to work with or without a POS system.",
        },
        {
          question: "Can I communicate with customers through WhatsApp?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, and campaigns to customers.",
        },
        {
          question: "Can I create different offers for different customers?",
          answer:
            "Yes. Businesses can create personalized and targeted offers based on customer activity and engagement.",
        },
        {
          question:
            "Can a loyalty platform help bring back inactive customers?",
          answer:
            "Yes. Targeted campaigns, personalized offers, and rewards can be used to re-engage customers who have not purchased recently.",
        },
        {
          question:
            "Is a digital loyalty solution suitable for offline businesses?",
          answer:
            "Yes. It is particularly useful for offline retailers that want to build customer relationships beyond individual transactions.",
        },
        {
          question: "How do I get started with Fydo?",
          answer:
            "You can start by setting up your customer rewards and engagement strategy with Fydo. Get Started Free or Book a Demo to learn more.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn More Customers into Regular Customers",
      subtitle:
        "Your customers already know your business. Give them more reasons to come back. With Fydo, reward purchases, personalize offers, engage customers, and build a loyalty strategy that keeps your business connected with its customers.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "loyalty program software",
      "customer loyalty app",
      "cashback loyalty program",
      "whatsapp marketing for shops",
    ],
  },

  "loyalty-software-with-pos-integration": {
    slug: "loyalty-software-with-pos-integration",
    cluster: "pos-tech",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Software with POS Integration",
      description:
        "Connect your POS with loyalty software from Fydo. Reward customers, personalize offers, increase repeat purchases, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Software with POS Integration",
        "POS integrated loyalty software",
        "POS loyalty software",
        "POS loyalty integration",
        "loyalty program with POS integration",
        "POS-integrated loyalty program",
        "retail loyalty software with POS",
        "point of sale loyalty software",
        "customer loyalty software",
        "digital loyalty program",
        "retail loyalty program",
        "customer rewards platform",
        "loyalty rewards software",
        "cashback loyalty program",
        "customer retention software",
        "customer engagement software",
        "customer retention",
        "repeat purchase strategy",
        "personalized offers",
        "customer re-engagement",
        "retail customer engagement",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Software with POS Integration",
      title:
        "Loyalty Software with POS Integration to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Connect your POS system with customer loyalty and turn everyday transactions into opportunities to increase repeat purchases with **Loyalty Software with POS Integration** from Fydo.",
        "Fydo helps offline retailers combine their point-of-sale operations with digital rewards, cashback, loyalty points, personalized offers, customer engagement, and **Free WhatsApp Marketing**.",
        "Whether you operate a single store or a growing retail business, Fydo helps you create a connected loyalty experience around your existing customer transactions.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need Loyalty Software with POS Integration?",
      subHeading:
        "Your POS system captures valuable information every time a customer makes a purchase. But billing data alone doesn't build customer loyalty.",
      description: [
        "When transactions, rewards, customer engagement, and marketing are managed separately, businesses can miss valuable opportunities to turn existing shoppers into repeat customers.",
      ],
      listLabel: "Common challenges faced by retail businesses:",
      points: [
        {
          title: "Customer Data Stays in the POS",
          text: "Purchase information may not be effectively used for loyalty and customer retention.",
        },
        {
          title: "Loyalty Is Managed Separately",
          text: "Running a separate manual loyalty process can create additional work for your staff.",
        },
        {
          title: "Manual Reward Tracking",
          text: "Calculating cashback, points, or other rewards manually can be time-consuming and prone to errors.",
        },
        {
          title: "Limited Post-Purchase Engagement",
          text: "Once the bill is generated, there may be little ongoing interaction with the customer.",
        },
        {
          title: "Generic Customer Offers",
          text: "Without using purchase activity, it can be difficult to create relevant promotions.",
        },
        {
          title: "Missed Repeat Sales Opportunities",
          text: "Every transaction is an opportunity to encourage the customer's next purchase.",
        },
      ],
      endingLine:
        "**The result?** Your POS handles billing efficiently, but your business may not be making full use of those transactions to build customer relationships and loyalty.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Your POS into a Customer Loyalty Opportunity",
      description: [
        "Fydo brings your transaction and loyalty experience closer together, helping you use customer purchases to drive engagement and retention.",
      ],
      listLabel: "With **Loyalty Software with POS Integration**, you can:",
      steps: [
        "**Connect customer purchases with loyalty rewards.**",
        "**Reward customers** for eligible transactions.",
        "**Offer cashback, points, discounts, and coupons.**",
        "**Engage customers with Free WhatsApp Marketing.**",
        "**Create personalized offers** based on customer activity.",
        "**Run targeted loyalty campaigns.**",
        "**Track customer purchase and engagement activity.**",
        "**Encourage repeat purchases** through relevant incentives.",
        "**Re-engage inactive customers** with targeted offers.",
        "**Build a connected digital loyalty experience** around your existing retail operations.",
      ],
      bottomLine:
        "This helps turn the POS from simply a billing tool into an important part of your customer retention strategy.",
    },
    featureSection: {
      heading: "Everything You Need in Loyalty Software with POS Integration",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Connect POS Purchases with Loyalty",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Link eligible customer transactions with your loyalty program so purchases can contribute to rewards and customer engagement.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Customers Automatically",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Configure cashback, points, discounts, and other rewards based on your loyalty program rules.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** and share relevant rewards, offers, promotions, and updates.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Create Personalized Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Use customer purchase activity to create more relevant offers for different customer groups.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Run Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create campaigns for new customers, frequent shoppers, high-value customers, inactive customers, or other segments.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Encourage Repeat Purchases",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a reason to return by connecting their purchases with meaningful rewards and incentives.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Monitor customer purchases and engagement to understand loyalty behavior and identify retention opportunities.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Reduce Manual Loyalty Management",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Bring customer transactions and loyalty activities closer together to simplify reward tracking and program management.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Digital Loyalty Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Replace physical loyalty cards and manual records with a convenient digital customer rewards experience.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo supports POS-connected loyalty as well as businesses that operate without a traditional POS system.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading:
        "POS-Connected Customer Loyalty for Every Type of Retail Business",
      subHeading: "",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Connect everyday purchases with loyalty rewards, encourage frequent shopping, and promote relevant offers to regular customers.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Reward purchases, promote new collections, and create personalized offers based on customer shopping activity.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Connect customer visits with rewards and encourage customers to return for their next service.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Reward frequent diners, promote special offers, and encourage repeat visits through digital loyalty.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger relationships with regular customers through rewards, customer engagement, and relevant communication.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Combine transaction activity with loyalty rewards, promotions, and ongoing customer engagement.",
        },
      ],
      endingLine:
        "From single-location retailers to growing businesses, Fydo helps connect customer transactions with a long-term loyalty strategy.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for POS-Connected Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Turn everyday purchases into opportunities to give customers a reason to return.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Use rewards and personalized engagement to encourage customers to purchase more frequently.",
        },
        {
          title: "Make Better Use of Transaction Data",
          text: "Use customer purchase activity to support more informed loyalty and engagement strategies.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Bring transactions, rewards, and customer activity closer together and reduce manual processes.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Continue the customer relationship beyond the billing counter with offers, rewards, and communication.",
        },
        {
          title: "Personalize Customer Experiences",
          text: "Create more relevant offers and rewards based on customer behavior and purchase activity.",
        },
        {
          title: "Build Stronger Customer Loyalty",
          text: "Give customers consistent value and recognition for choosing your business repeatedly.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Stay connected with customers through **Free WhatsApp Marketing** and support your loyalty and retention activities through WhatsApp.",
        },
        {
          title: "Complement Your Existing POS",
          text: "Enhance your existing retail operations with digital loyalty rather than requiring you to completely change your billing process.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline retailers connect customer purchases with digital loyalty, rewards, engagement, and retention strategies.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helps us make every customer transaction more valuable by connecting purchases with rewards and ongoing engagement.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your POS-Connected Loyalty Program in 3 Simple Steps",
      description: "Purchase → Reward → Engagement → Repeat Purchase",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Loyalty Program",
          description:
            "Configure your rewards, offers, customer segments, and loyalty rules on Fydo.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn",
          description:
            "Customers make eligible purchases and receive rewards through the digital loyalty experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Encourage Them to Return",
          description:
            "Use personalized offers, loyalty rewards, targeted campaigns, and **Free WhatsApp Marketing** to keep customers engaged and encourage their next purchase. This creates a simple cycle:",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is Loyalty Software with POS Integration?",
          answer:
            "Loyalty Software with POS Integration connects a business's point-of-sale system with its customer loyalty program, allowing eligible purchases to be linked with rewards, customer activity, and engagement.",
        },
        {
          question: "How does Fydo's POS-integrated loyalty software work?",
          answer:
            "Fydo helps connect customer transactions with digital rewards, cashback, loyalty points, personalized offers, campaigns, and customer engagement activities.",
        },
        {
          question: "Can I use Fydo with my existing POS system?",
          answer:
            "Fydo is designed to support POS-connected loyalty experiences. The specific integration process can depend on the POS system and business setup.",
        },
        {
          question: "What rewards can I offer customers?",
          answer:
            "You can create cashback, points, discounts, coupons, and other loyalty incentives based on your business requirements.",
        },
        {
          question: "Can customers earn rewards from POS purchases?",
          answer:
            "Eligible transactions can be connected with your loyalty program so customers can earn rewards according to the rules configured for your business.",
        },
        {
          question: "Can I engage customers after they make a purchase?",
          answer:
            "Yes. You can use customer activity, loyalty rewards, personalized offers, campaigns, and Free WhatsApp Marketing to continue engaging customers after a transaction.",
        },
        {
          question: "Do I need a POS system to use Fydo?",
          answer:
            "No. Fydo can also support businesses without a traditional POS system, making it suitable for different types of offline retailers.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Free WhatsApp Marketing is a key Fydo benefit that helps businesses communicate with customers and support their loyalty, engagement, and retention campaigns.",
        },
        {
          question:
            "Can POS-integrated loyalty help increase repeat purchases?",
          answer:
            "Connecting customer purchases with rewards and ongoing engagement can give shoppers additional reasons to return and make future purchases.",
        },
        {
          question:
            "Is Fydo suitable for different types of retail businesses?",
          answer:
            "Yes. Fydo can be used by Grocery & Kirana/General Stores, clothing and fashion businesses, salons, restaurants, pharmacies, lifestyle stores, and other offline retailers.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Every POS Transaction into a Reason to Return",
      subtitle:
        "Your POS already records customer purchases. Now turn those transactions into opportunities to build stronger customer relationships. Use Fydo to connect purchases with digital rewards, personalized offers, customer engagement, and **Free WhatsApp Marketing**. Give your customers more reasons to return and turn more transactions into repeat business.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "loyalty software comparison",
      "loyalty program for any pos",
      "choosing loyalty software",
      "loyalty program without hardware",
    ],
  },

  "digital-loyalty-card": {
    slug: "digital-loyalty-card",
    cluster: "pos-tech",
    showMerchantsSection: false,
    seo: {
      title: "Digital Loyalty Card for Retail Businesses",
      description:
        "Create a Digital Loyalty Card with Fydo. Reward customers, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Digital Loyalty Card",
        "digital loyalty card for business",
        "digital loyalty card India",
        "digital customer loyalty card",
        "digital rewards card",
        "online loyalty card",
        "digital loyalty program",
        "customer loyalty card",
        "digital rewards program",
        "loyalty rewards platform",
        "customer retention solution",
        "retail loyalty program",
        "digital customer rewards",
        "customer loyalty",
        "digital rewards",
        "loyalty rewards",
        "cashback rewards",
        "customer engagement",
        "repeat purchases",
        "customer retention",
        "personalized offers",
        "customer campaigns",
        "re-engagement",
        "loyalty program",
        "digital offers",
        "returning customers",
        "WhatsApp marketing",
      ],
    },
    heroSection: {
      badgeText: "Digital Loyalty Card",
      title: "Turn Every Purchase into a Reason to Come Back",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "A **Digital Loyalty Card** helps retail businesses replace traditional paper or plastic loyalty cards with a simple, digital customer loyalty experience.",
        "With Fydo, businesses can reward customers for purchases, offer personalized deals, engage shoppers through **Free WhatsApp Marketing**, and encourage customers to return more often.",
        "No complicated setup. No physical cards. No unnecessary paperwork.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need a Digital Loyalty Card?",
      subHeading:
        "Traditional loyalty cards are easy to lose, forget, or stop using. Businesses also struggle to maintain customer records and consistently engage their existing customers.",
      description: [
        "A **Digital Loyalty Card** makes customer rewards easier to access while helping businesses build ongoing relationships with their shoppers.",
      ],
      listLabel: "Common challenges include:",
      points: [
        "Customers forgetting or losing physical loyalty cards",
        "Limited visibility into customer purchase behaviour",
        "Manual management of loyalty rewards",
        "Difficulty encouraging repeat purchases",
        "Generic offers that do not match customer interests",
        "No consistent way to reconnect with inactive customers",
        "Dependence on physical cards and paperwork",
      ],
      endingLine:
        "Your customers are already using their phones every day. Your loyalty experience should be digital too.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Make Customer Loyalty Simple with a Digital Loyalty Card",
      description: [
        "Fydo gives businesses a simple way to create a **Digital Loyalty Card** experience without relying on physical cards.",
        "Customers can access rewards digitally while businesses can manage customer engagement, offers, rewards, and campaigns from one platform.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Reward customers for their purchases",
        "Create cashback, points, discounts, and other rewards",
        "Share personalized offers",
        "Engage customers through **Free WhatsApp Marketing**",
        "Run targeted customer campaigns",
        "Encourage repeat purchases",
        "Re-engage customers who have stopped visiting",
        "Track customer activity and engagement",
        "Build long-term customer loyalty",
      ],
      bottomLine: "One Digital Experience. More Reasons to Return.",
    },
    featureSection: {
      heading: "Powerful Features of Fydo's Digital Loyalty Card",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Go Digital with Customer Loyalty",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Replace traditional loyalty cards with a convenient digital experience that customers can access through their phones.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Customers for Purchases",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers cashback, points, discounts, or other rewards to encourage them to shop with you again.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** and share relevant offers, rewards, and updates.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Create Personalized Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give different customers relevant offers based on their purchase activity and engagement.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Run Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create campaigns for new customers, frequent shoppers, inactive customers, or specific customer segments.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Encourage Repeat Purchases",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a reason to return by connecting purchases with meaningful rewards and offers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and identify opportunities to improve retention and repeat sales.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Promote Products & Special Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Use digital offers and campaigns to promote products, seasonal promotions, special sales, and exclusive deals.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support businesses whether they use a POS system or manage transactions without one.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Digital Customer Loyalty for Every Type of Retail Business",
      subHeading:
        "A digital rewards experience can work across different offline retail categories.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward regular shoppers and encourage customers to choose your store for their next purchase.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Offer rewards and exclusive deals to encourage customers to return for their next shopping trip.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Build customer loyalty by rewarding repeat visits and promoting relevant offers.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Encourage customers to return through digital rewards, offers, and customer engagement campaigns.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger relationships with regular customers through convenient digital rewards and communication.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Use personalized offers and loyalty rewards to turn occasional shoppers into returning customers.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Digital Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Replace Physical Loyalty Cards",
          text: "Move from traditional cards to a convenient digital loyalty experience.",
        },
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return and shop again.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Stay connected with customers through offers, rewards, and ongoing communication.",
        },
        {
          title: "Personalize Customer Offers",
          text: "Deliver more relevant promotions based on customer activity.",
        },
        {
          title: "Reduce Manual Loyalty Management",
          text: "Manage rewards and customer engagement digitally from a single platform.",
        },
        {
          title: "Reconnect with Inactive Customers",
          text: "Use targeted campaigns to bring back customers who have not purchased recently.",
        },
        {
          title: "Build Long-Term Customer Relationships",
          text: "Turn individual transactions into an ongoing customer loyalty strategy.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers directly through WhatsApp without adding a separate marketing cost.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline businesses build stronger relationships with their customers through digital loyalty, rewards, engagement, and retention. With **10,000+ merchants and 1.6 lakh customers**, Fydo is helping businesses move beyond traditional loyalty methods and build more connected customer experiences.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Digital Loyalty Program in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Choose the rewards, cashback, points, discounts, or offers you want to provide.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect with Customers",
          description:
            "Customers interact with your digital loyalty experience and can receive rewards based on their purchases.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, campaigns, rewards, and **Free WhatsApp Marketing** to encourage customers to return.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Digital Loyalty Card?",
          answer:
            "A Digital Loyalty Card is a digital alternative to traditional physical loyalty cards. It allows businesses to provide rewards, offers, and loyalty benefits to customers without requiring a physical card.",
        },
        {
          question: "How does a Digital Loyalty Card work?",
          answer:
            "Customers interact with the digital loyalty system while making purchases. Their rewards and loyalty benefits can then be managed digitally instead of using a physical card.",
        },
        {
          question: "Can I use Fydo without a POS system?",
          answer:
            "Yes. Fydo is designed to work with or without a POS system, making it suitable for different types of offline retail businesses.",
        },
        {
          question: "What types of rewards can I offer?",
          answer:
            "Businesses can use cashback, points, discounts, coupons, and other promotional rewards to encourage customers to return.",
        },
        {
          question: "Can I communicate with customers through WhatsApp?",
          answer:
            "Yes. Fydo includes Free WhatsApp Marketing to help businesses stay connected with customers and share relevant offers and campaigns.",
        },
        {
          question: "Can I personalize offers for different customers?",
          answer:
            "Yes. Businesses can create targeted offers and campaigns based on customer activity and engagement.",
        },
        {
          question:
            "Is a digital loyalty card better than a physical loyalty card?",
          answer:
            "A digital approach eliminates the need for customers to carry a physical card and makes it easier for businesses to manage rewards and engage customers.",
        },
        {
          question:
            "Can a Digital Loyalty Card help increase repeat purchases?",
          answer:
            "Yes. Rewards, personalized offers, and targeted customer engagement can give shoppers additional reasons to return and purchase again.",
        },
        {
          question: "Is Fydo suitable for small retail businesses?",
          answer:
            "Yes. Fydo is designed for offline businesses of different sizes, including grocery stores, salons, restaurants, pharmacies, clothing stores, and specialty retailers.",
        },
        {
          question: "How can I get started with Fydo?",
          answer:
            "You can start by setting up your loyalty rewards and customer engagement experience with Fydo. Get Started Free or Book a Demo to learn more.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn More Shoppers into Regular Customers",
      subtitle:
        "Your customers should not need a physical card to stay connected with your business. Create a simple digital loyalty experience, reward customers for coming back, personalize their offers, and build stronger customer relationships with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "digital loyalty card",
      "replace punch card",
      "loyalty card app",
      "lost loyalty card solution",
    ],
  },

  "loyalty-program-for-supermarket": {
    slug: "loyalty-program-for-supermarket",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Supermarket | Customer Rewards & Retention",
      description:
        "Build a Loyalty Program for Supermarkets with Fydo. Reward shoppers, increase repeat purchases, personalize offers, and engage customers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Supermarket",
        "supermarket loyalty program",
        "loyalty program for supermarkets",
        "supermarket customer loyalty program",
        "supermarket loyalty software",
        "supermarket rewards program",
        "loyalty software for supermarket",
        "digital loyalty program for supermarket",
        "supermarket customer retention",
        "supermarket rewards platform",
        "customer loyalty software for supermarket",
        "loyalty solution for supermarket",
        "supermarket loyalty app",
        "customer loyalty",
        "digital rewards",
        "cashback rewards",
        "points-based rewards",
        "customer engagement",
        "repeat purchases",
        "purchase frequency",
        "customer retention",
        "personalized offers",
        "loyalty campaigns",
        "customer re-engagement",
        "returning customers",
        "digital offers",
        "supermarket promotions",
        "customer rewards",
        "WhatsApp marketing",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Supermarket",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Supermarket",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "A **Loyalty Program for Supermarkets** helps supermarkets reward shoppers, encourage frequent purchases, and build stronger relationships with their customers.",
        "With Fydo, supermarkets can offer cashback, points, discounts, and personalized rewards, run targeted campaigns, engage shoppers with **Free WhatsApp Marketing**, and give customers more reasons to return.",
        "Turn everyday supermarket shoppers into loyal, returning customers.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Supermarket Need a Loyalty Program?",
      subHeading:
        "Supermarket customers often have multiple stores to choose from. Even regular shoppers may switch based on convenience, offers, pricing, or promotions.",
      description: [
        "Simply offering discounts may not be enough to build long-term customer loyalty.",
      ],
      listLabel: "Supermarkets commonly face challenges such as:",
      points: [
        "Customers shopping at multiple competing stores",
        "Difficulty increasing purchase frequency",
        "Limited visibility into customer buying behaviour",
        "Customers forgetting about available offers",
        "Generic promotions that do not suit every shopper",
        "Manual management of loyalty rewards",
        "Difficulty re-engaging customers who have stopped visiting",
        "No structured strategy for retaining existing shoppers",
      ],
      endingLine:
        "A digital loyalty strategy can help supermarkets turn everyday purchases into ongoing customer relationships.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Everyday Supermarket Purchases into Loyalty Opportunities",
      description: [
        "Fydo provides a simple digital solution for creating and managing a **Loyalty Program for Supermarket** businesses.",
        "Reward customers for their purchases, provide relevant offers, engage shoppers between visits, and build a structured customer retention strategy from one platform.",
      ],
      listLabel: "With Fydo, your supermarket can:",
      steps: [
        "Reward customers for purchases",
        "Offer cashback and points",
        "Create discounts and coupons",
        "Provide personalized offers",
        "Run targeted customer campaigns",
        "Engage shoppers with **Free WhatsApp Marketing**",
        "Promote products and special offers",
        "Re-engage inactive customers",
        "Track customer activity",
        "Encourage more frequent purchases",
        "Build long-term customer loyalty",
      ],
      bottomLine: "Reward Every Visit. Encourage the Next Purchase.",
    },
    featureSection: {
      heading: "Powerful Features for Your Supermarket Loyalty Program",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give shoppers cashback or points on eligible purchases and create an incentive to continue shopping with your supermarket.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Frequent Shoppers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Recognize customers who purchase regularly and give them additional reasons to keep choosing your store.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** and share rewards, offers, promotions, and important updates.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer purchase activity and engagement.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Run different campaigns for frequent shoppers, new customers, inactive customers, or specific customer segments.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote Products & Special Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Highlight new products, seasonal promotions, festive offers, and special deals to your customer base.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Customer Re-Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers who have not visited recently a reason to return through targeted rewards and offers.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and identify opportunities to improve retention and purchase frequency.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Digital Loyalty Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Move away from traditional physical cards and manual processes with a convenient digital customer experience.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support supermarkets whether they use a POS system or operate without one.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Supermarket",
      subHeading:
        "A flexible rewards and engagement strategy can support different supermarket business models.",
      useCases: [
        {
          title: "Local Supermarkets",
          detail:
            "Build stronger relationships with neighbourhood shoppers and encourage them to make your store their regular choice.",
        },
        {
          title: "Independent Supermarkets",
          detail:
            "Reward loyal customers and use personalized offers to compete more effectively for repeat business.",
        },
        {
          title: "Supermarket Chains",
          detail:
            "Create consistent customer engagement and reward strategies across multiple locations.",
        },
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Use digital rewards and customer engagement to encourage more frequent everyday purchases.",
        },
        {
          title: "Specialty Supermarkets",
          detail:
            "Promote relevant products and offers while rewarding customers for repeat shopping.",
        },
        {
          title: "Premium Grocery Stores",
          detail:
            "Use personalized rewards and exclusive offers to create a differentiated shopping experience.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Supermarkets Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Purchase Frequency",
          text: "Give shoppers more reasons to visit and purchase from your supermarket regularly.",
        },
        {
          title: "Encourage Repeat Purchases",
          text: "Connect purchases with rewards and offers that motivate customers to return.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Continue engaging shoppers beyond individual transactions.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use digital rewards, personalized offers, campaigns, and WhatsApp communication to stay connected.",
        },
        {
          title: "Increase Customer Retention",
          text: "Create an ongoing strategy to retain valuable shoppers instead of constantly relying on new customer acquisition.",
        },
        {
          title: "Bring Back Inactive Shoppers",
          text: "Identify customers who have not purchased recently and run targeted re-engagement campaigns.",
        },
        {
          title: "Promote Offers More Effectively",
          text: "Reach relevant customers with product promotions, seasonal deals, and special offers.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage customer rewards and engagement digitally from one platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Stay connected with your supermarket customers through WhatsApp without needing a separate marketing solution.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline businesses build stronger customer relationships through digital rewards, customer engagement, and retention. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Supermarket Loyalty Program in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Choose cashback, points, discounts, coupons, or other rewards that suit your supermarket.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect with Your Customers",
          description:
            "Customers interact with your digital loyalty experience and receive rewards based on eligible purchases.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, loyalty campaigns, rewards, and **Free WhatsApp Marketing** to encourage customers to shop again.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Supermarkets?",
          answer:
            "A Loyalty Program for Supermarket is a customer loyalty strategy that rewards shoppers for their purchases and encourages them to continue shopping with the same supermarket.",
        },
        {
          question:
            "How can a supermarket loyalty program increase repeat purchases?",
          answer:
            "Cashback, points, personalized offers, and targeted campaigns can give customers additional reasons to return and shop more frequently.",
        },
        {
          question: "What rewards can a supermarket offer?",
          answer:
            "Supermarkets can offer cashback, points, discounts, coupons, promotional rewards, and other incentives based on their loyalty strategy.",
        },
        {
          question: "Can small and independent supermarkets use Fydo?",
          answer:
            "Yes. Fydo can be used by supermarkets and other offline retail businesses of different sizes.",
        },
        {
          question: "Does Fydo require a POS system?",
          answer:
            "No. Fydo can work with or without a POS system, making it suitable for different supermarket setups.",
        },
        {
          question: "Can supermarkets use WhatsApp to engage customers?",
          answer:
            "Yes. Fydo includes Free WhatsApp Marketing to help supermarkets communicate offers, rewards, promotions, and campaigns.",
        },
        {
          question: "Can I give different offers to different customers?",
          answer:
            "Yes. Businesses can create targeted and personalized offers based on customer activity and engagement.",
        },
        {
          question:
            "Can a loyalty program help bring back inactive supermarket customers?",
          answer:
            "Yes. Supermarkets can use targeted rewards, offers, and re-engagement campaigns to encourage inactive shoppers to return.",
        },
        {
          question: "Can Fydo help promote supermarket offers?",
          answer:
            "Yes. Businesses can use digital campaigns and customer communication to promote products, seasonal offers, special deals, and promotions.",
        },
        {
          question: "How can I start a supermarket loyalty program with Fydo?",
          answer:
            "You can set up your rewards and customer engagement strategy with Fydo. Get Started Free or Book a Demo to learn more.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Everyday Shoppers into Loyal Supermarket Customers",
      subtitle:
        "Your supermarket already has customers. The next step is giving them more reasons to return. Reward purchases, personalize offers, promote relevant deals, and stay connected with shoppers through digital customer engagement with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "supermarket customer retention",
      "cashback loyalty program for supermarkets",
      "family loyalty rewards program",
      "whatsapp marketing for supermarkets",
    ],
  },

  "loyalty-program-for-footwear-store": {
    slug: "loyalty-program-for-footwear-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Footwear Store",
      description:
        "Build a Loyalty Program for Footwear Store with Fydo. Reward customers, increase repeat purchases, promote new collections, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Footwear Store",
        "footwear store loyalty program",
        "loyalty program for footwear stores",
        "footwear customer loyalty program",
        "footwear loyalty software",
        "footwear store rewards program",
        "loyalty software for footwear store",
        "digital loyalty program for footwear store",
        "footwear customer retention",
        "footwear rewards platform",
        "customer loyalty software for footwear stores",
        "loyalty solution for footwear retailers",
        "footwear loyalty app",
        "customer loyalty",
        "digital rewards",
        "cashback rewards",
        "points-based rewards",
        "customer engagement",
        "repeat purchases",
        "customer retention",
        "personalized offers",
        "loyalty campaigns",
        "customer re-engagement",
        "new product promotions",
        "returning customers",
        "digital offers",
        "customer rewards",
        "WhatsApp marketing",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Footwear Store",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Footwear Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "A **Loyalty Program for Footwear Store** helps footwear retailers reward customers, encourage repeat purchases, and build stronger relationships with shoppers.",
        "With Fydo, footwear stores can offer cashback, points, discounts, and personalized rewards, run targeted campaigns, engage customers through **Free WhatsApp Marketing**, and give shoppers more reasons to return.",
        "Turn one-time footwear shoppers into loyal, returning customers.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Footwear Store Need a Loyalty Program?",
      subHeading:
        "Footwear purchases may not happen as frequently as everyday grocery or convenience purchases. This makes it important for footwear retailers to stay connected with customers and remain top-of-mind when they are ready for their next purchase.",
      description: [
        "Traditional loyalty methods often make this difficult. Physical cards can be lost, customer information can remain disconnected, and generic discounts may not create lasting loyalty.",
      ],
      listLabel: "Footwear businesses commonly face challenges such as:",
      points: [
        "Customers purchasing only when they have an immediate need",
        "Long gaps between repeat purchases",
        "Customers switching between competing footwear stores",
        "Limited customer purchase information",
        "Physical loyalty cards being lost or forgotten",
        "Generic promotions with limited personalization",
        "Difficulty reaching customers after their purchase",
        "No structured strategy for encouraging repeat business",
      ],
      endingLine:
        "A digital loyalty strategy can help footwear retailers stay connected with customers long after the initial sale.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Footwear Purchase into a Loyalty Opportunity",
      description: [
        "Fydo provides a simple digital solution for creating and managing a **Loyalty Program for Footwear Store** businesses.",
        "Reward customers when they shop, provide relevant offers, promote new collections, and reconnect with shoppers when they are ready for another purchase.",
      ],
      listLabel: "With Fydo, your footwear store can:",
      steps: [
        "Reward customers for their purchases",
        "Offer cashback, points, discounts, and coupons",
        "Create personalized customer offers",
        "Run targeted loyalty campaigns",
        "Engage customers with **Free WhatsApp Marketing**",
        "Promote new arrivals and collections",
        "Re-engage inactive customers",
        "Track customer activity",
        "Encourage repeat purchases",
        "Build stronger customer relationships",
      ],
      bottomLine: "Reward Their Purchase. Stay Connected. Bring Them Back.",
    },
    featureSection: {
      heading: "Powerful Features for Your Footwear Store Loyalty Program",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback or points and create incentives for future purchases.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Turn every footwear purchase into an opportunity to build customer loyalty and encourage the next visit.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** and share offers, rewards, new arrivals, and promotional campaigns.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and previous purchases.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create campaigns for new customers, regular shoppers, inactive customers, or specific customer segments.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote New Collections & Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Keep customers informed about new footwear collections, seasonal products, festive promotions, and special offers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a reason to return with targeted rewards, offers, and personalized communication.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and identify opportunities to improve retention and repeat sales.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Digital Loyalty Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Move beyond physical loyalty cards with a convenient digital way to manage rewards and customer engagement.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support footwear businesses whether they use a POS system or operate without one.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Footwear Store",
      subHeading:
        "Whether you operate a neighbourhood footwear shop or a larger retail outlet, a digital rewards strategy can help you build stronger customer relationships.",
      useCases: [
        {
          title: "Independent Footwear Stores",
          detail:
            "Reward your regular customers and encourage them to return when they need their next pair.",
        },
        {
          title: "Family Footwear Stores",
          detail:
            "Build loyalty across different customer groups with relevant offers and rewards.",
        },
        {
          title: "Sports Footwear Stores",
          detail:
            "Promote new arrivals, seasonal collections, and relevant products to engaged customers.",
        },
        {
          title: "Fashion & Lifestyle Footwear Stores",
          detail:
            "Use personalized rewards and exclusive offers to encourage repeat shopping.",
        },
        {
          title: "Kids' Footwear Stores",
          detail:
            "Reconnect with parents and families through targeted offers when they may need new footwear.",
        },
        {
          title: "Multi-Category Footwear Retailers",
          detail:
            "Create customer engagement campaigns across different footwear categories and product segments.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Footwear Businesses Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return when they are ready for their next footwear purchase.",
        },
        {
          title: "Stay Connected Between Purchases",
          text: "Keep your store relevant even when customers have longer gaps between shopping visits.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Turn individual transactions into ongoing customer relationships.",
        },
        {
          title: "Promote New Arrivals",
          text: "Reach existing customers with relevant product launches, collections, and special offers.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use rewards, digital offers, campaigns, and WhatsApp communication to stay connected.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Re-engage customers who have not purchased recently with targeted offers and rewards.",
        },
        {
          title: "Personalize Customer Promotions",
          text: "Create more relevant campaigns based on customer activity and engagement.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, offers, and customer engagement digitally from one platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Communicate directly with customers through WhatsApp without requiring a separate marketing solution.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline businesses build stronger customer relationships through digital rewards, customer engagement, and retention. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Footwear Store Loyalty Program in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Choose cashback, points, discounts, coupons, or other rewards that fit your footwear business.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect with Customers",
          description:
            "Customers interact with your digital loyalty experience and receive rewards based on their purchases.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, new product promotions, loyalty campaigns, and **Free WhatsApp Marketing** to stay connected with customers.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Footwear Store?",
          answer:
            "A Loyalty Program for Footwear Store is a customer retention strategy that rewards shoppers for purchases and encourages them to return for future footwear needs.",
        },
        {
          question: "How can a loyalty program help a footwear store?",
          answer:
            "It can help footwear stores increase repeat purchases, stay connected with customers between purchases, promote new collections, and build long-term customer relationships.",
        },
        {
          question: "What rewards can I offer footwear customers?",
          answer:
            "You can offer cashback, points, discounts, coupons, and other promotional rewards based on your business strategy.",
        },
        {
          question: "Can a footwear store use Fydo without a POS system?",
          answer:
            "Yes. Fydo can work with or without a POS system, making it suitable for different footwear retail setups.",
        },
        {
          question:
            "Can I promote new footwear collections to existing customers?",
          answer:
            "Yes. Businesses can use customer campaigns and digital communication to promote new arrivals, seasonal collections, and special offers.",
        },
        {
          question: "Can I use WhatsApp to engage footwear customers?",
          answer:
            "Yes. Fydo includes Free WhatsApp Marketing to help businesses communicate offers, rewards, campaigns, and product updates.",
        },
        {
          question: "Can I create personalized offers for different customers?",
          answer:
            "Yes. Businesses can create targeted offers and campaigns based on customer activity and engagement.",
        },
        {
          question: "Can the program help bring back inactive customers?",
          answer:
            "Yes. Targeted rewards, offers, and re-engagement campaigns can give inactive customers a reason to return.",
        },
        {
          question:
            "Is digital loyalty useful for footwear stores with longer purchase cycles?",
          answer:
            "Yes. Staying connected through relevant offers, new-arrival updates, and rewards can help keep your store top-of-mind between purchases.",
        },
        {
          question: "How can I start a loyalty program for my footwear store?",
          answer:
            "You can set up your rewards and customer engagement strategy with Fydo. Get Started Free or Book a Demo to learn more.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Footwear Shoppers into Returning Customers",
      subtitle:
        "A customer's first purchase does not have to be their last. Reward your shoppers, stay connected between purchases, promote new collections, and give customers more reasons to choose your footwear store again with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "footwear store customer retention",
      "whatsapp marketing for footwear stores",
      "cashback loyalty program for shoe stores",
      "seasonal customer engagement retail",
    ],
  },

  "loyalty-program-for-electronics-store": {
    slug: "loyalty-program-for-electronics-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Electronics Store",
      description:
        "Build a Loyalty Program for Electronics Store with Fydo. Reward customers, promote new products, increase repeat purchases, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Electronics Store",
        "electronics store loyalty program",
        "loyalty program for electronics stores",
        "electronics customer loyalty program",
        "electronics loyalty software",
        "electronics store rewards program",
        "loyalty software for electronics store",
        "digital loyalty program for electronics store",
        "electronics customer retention",
        "electronics rewards platform",
        "customer loyalty software for electronics stores",
        "loyalty solution for electronics retailers",
        "electronics loyalty app",
        "customer rewards program for electronics store",
        "customer loyalty",
        "digital rewards",
        "cashback rewards",
        "points-based rewards",
        "customer engagement",
        "repeat purchases",
        "customer retention",
        "personalized offers",
        "loyalty campaigns",
        "customer re-engagement",
        "product promotions",
        "new product launches",
        "cross-selling",
        "returning customers",
        "digital offers",
        "customer rewards",
        "WhatsApp marketing",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Electronics Store",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Electronics Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "A **Loyalty Program for Electronics Store** helps electronics retailers build stronger customer relationships, encourage repeat purchases, and stay connected with customers long after the initial sale.",
        "With Fydo, electronics stores can offer cashback, points, discounts, and personalized rewards, promote new products, run targeted campaigns, and engage customers through **Free WhatsApp Marketing**.",
        "Turn one-time electronics buyers into returning customers.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Electronics Store Need a Loyalty Program?",
      subHeading:
        "Electronics purchases are often less frequent than everyday retail purchases. Customers may take months or even years before buying another major device, but they can still make smaller purchases, upgrades, accessories, or replacement purchases in between.",
      description: [
        "The challenge for electronics retailers is staying connected and remaining relevant until the customer's next buying decision.",
      ],
      listLabel: "Common challenges include:",
      points: [
        "Long gaps between repeat purchases",
        "Customers comparing multiple electronics retailers",
        "Customers switching stores for their next purchase",
        "Limited engagement after the initial sale",
        "Difficulty promoting new products and launches",
        "Generic discounts with limited personalization",
        "Limited customer purchase insights",
        "Difficulty re-engaging inactive customers",
        "No structured strategy for encouraging repeat business",
      ],
      endingLine:
        "A digital loyalty strategy helps electronics retailers maintain relationships beyond individual transactions.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Electronics Purchase into a Loyalty Opportunity",
      description: [
        "Fydo provides a simple digital solution for creating and managing a **Loyalty Program for Electronics Store** businesses.",
        "Reward customers for purchases, promote relevant products, share personalized offers, and stay connected with shoppers throughout their customer journey.",
      ],
      listLabel: "With Fydo, your electronics store can:",
      steps: [
        "Reward customers for purchases",
        "Offer cashback, points, discounts, and coupons",
        "Create personalized customer offers",
        "Promote new products and launches",
        "Run targeted loyalty campaigns",
        "Engage customers with **Free WhatsApp Marketing**",
        "Promote accessories and complementary products",
        "Re-engage inactive customers",
        "Track customer activity",
        "Encourage repeat purchases",
        "Build long-term customer relationships",
      ],
      bottomLine: "Reward the Purchase. Stay Connected for the Next One.",
    },
    featureSection: {
      heading: "Powerful Features for Your Electronics Store Loyalty Program",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback or points and create incentives for future purchases.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Turn every transaction, from major electronics purchases to accessories, into an opportunity to build customer loyalty.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected through **Free WhatsApp Marketing** and share offers, rewards, product updates, promotions, and campaigns.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create campaigns for new customers, regular shoppers, inactive customers, or specific customer groups.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote New Products & Launches",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Keep existing customers informed about new devices, product launches, seasonal promotions, and special offers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Promote Accessories & Complementary Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Engage customers with relevant offers for accessories and related products after their primary purchase.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a reason to return through targeted rewards, offers, and personalized communication.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and identify opportunities to improve retention and repeat sales.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support electronics retailers whether they use a POS system or operate without one.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Electronics Store",
      subHeading:
        "Whether you operate a neighbourhood electronics shop or a larger multi-category retailer, digital customer engagement can help build stronger relationships.",
      useCases: [
        {
          title: "Mobile & Smartphone Stores",
          detail:
            "Reward customers and stay connected for future device upgrades, accessories, and related purchases.",
        },
        {
          title: "Computer & Laptop Stores",
          detail:
            "Engage customers after their purchase and promote accessories, upgrades, and relevant products.",
        },
        {
          title: "Consumer Electronics Stores",
          detail:
            "Build long-term relationships with customers purchasing televisions, audio systems, appliances, and other electronics.",
        },
        {
          title: "Home Appliance Stores",
          detail:
            "Encourage customers to return for additional products, upgrades, and related purchases.",
        },
        {
          title: "Electronics & Accessories Stores",
          detail:
            "Use rewards and personalized offers to encourage more frequent purchases of accessories and complementary products.",
        },
        {
          title: "Multi-Brand Electronics Retailers",
          detail:
            "Engage different customer segments with relevant rewards, offers, product promotions, and campaigns.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Electronics Businesses Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return when they need another device, accessory, upgrade, or electronics product.",
        },
        {
          title: "Stay Connected Between Purchases",
          text: "Maintain customer engagement even when there are longer gaps between major purchases.",
        },
        {
          title: "Promote New Products",
          text: "Keep existing customers informed about new launches, products, collections, and special offers.",
        },
        {
          title: "Encourage Cross-Selling",
          text: "Promote relevant accessories and complementary products based on customer activity.",
        },
        {
          title: "Build Long-Term Customer Relationships",
          text: "Turn individual purchases into an ongoing relationship with your customers.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use rewards, personalized offers, campaigns, and WhatsApp communication to stay connected.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Re-engage customers who have not purchased recently with targeted offers and rewards.",
        },
        {
          title: "Personalize Customer Promotions",
          text: "Create more relevant campaigns based on customer activity and engagement.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, offers, and customer engagement digitally from one platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Communicate directly with customers through WhatsApp without requiring a separate marketing solution.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline businesses build stronger customer relationships through digital rewards, customer engagement, and retention. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Electronics Store Loyalty Program in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Choose cashback, points, discounts, coupons, or other rewards that suit your electronics business.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect with Customers",
          description:
            "Customers interact with your digital loyalty experience and receive rewards based on their purchases.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, product promotions, loyalty campaigns, and **Free WhatsApp Marketing** to stay connected with customers.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for an Electronics Store?",
          answer:
            "A Loyalty Program for Electronics Store is a customer retention strategy that rewards customers for purchases and helps electronics retailers build long-term relationships with shoppers.",
        },
        {
          question: "How can a loyalty program help an electronics store?",
          answer:
            "It can help increase repeat purchases, promote new products, encourage accessory sales, re-engage inactive customers, and maintain customer relationships between purchases.",
        },
        {
          question: "What rewards can I offer electronics customers?",
          answer:
            "You can offer cashback, points, discounts, coupons, and other promotional rewards depending on your business strategy.",
        },
        {
          question: "Can an electronics store use Fydo without a POS system?",
          answer:
            "Yes. Fydo can work with or without a POS system, making it suitable for different electronics retail setups.",
        },
        {
          question:
            "Can I promote new electronics products to existing customers?",
          answer:
            "Yes. Businesses can use customer campaigns and digital communication to promote new products, launches, upgrades, seasonal offers, and special deals.",
        },
        {
          question: "Can I use WhatsApp to engage electronics customers?",
          answer:
            "Yes. Fydo includes Free WhatsApp Marketing to help businesses communicate offers, rewards, product updates, and campaigns.",
        },
        {
          question: "Can I create personalized offers for different customers?",
          answer:
            "Yes. Businesses can create targeted offers and campaigns based on customer activity and engagement.",
        },
        {
          question: "Can the program help bring back inactive customers?",
          answer:
            "Yes. Targeted rewards, personalized offers, and re-engagement campaigns can encourage inactive customers to return.",
        },
        {
          question:
            "Can a loyalty program help electronics stores sell accessories?",
          answer:
            "Yes. Businesses can use customer engagement and targeted offers to promote relevant accessories and complementary products.",
        },
        {
          question:
            "How can I start a loyalty program for my electronics store?",
          answer:
            "You can set up your rewards and customer engagement strategy with Fydo. Get Started Free or Book a Demo to learn more.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Electronics Buyers into Returning Customers",
      subtitle:
        "The relationship with a customer should not end after the sale. Reward purchases, promote relevant products, stay connected between buying cycles, and give customers more reasons to return to your electronics store with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "electronics store customer retention",
      "cashback loyalty program for electronics stores",
      "accessory cross sell retail",
      "whatsapp marketing for electronics stores",
    ],
  },

  "coupon-loyalty-program": {
    slug: "coupon-loyalty-program",
    cluster: "core-loyalty",
    showMerchantsSection: false,
    seo: {
      title: "Coupon Loyalty Program for Retail Businesses in India",
      description:
        "Grow repeat sales with Fydo's coupon loyalty program. Create digital coupons, engage customers, increase repeat purchases, and connect with customers through Free WhatsApp Marketing.",
      keywords: [
        "coupon loyalty program",
        "coupon loyalty program software",
        "digital coupon loyalty program",
        "coupon loyalty software",
        "digital coupon program",
        "customer coupon program",
        "loyalty coupon software",
        "digital loyalty coupons",
        "coupon rewards program",
        "retail coupon loyalty program",
        "customer loyalty program",
        "loyalty program software",
        "coupon loyalty program India",
        "coupon loyalty program for small businesses",
        "coupon loyalty program for retail businesses",
        "digital coupons for retail",
        "digital coupon software India",
        "retail coupon software",
        "customer retention software",
        "digital loyalty program",
        "digital customer rewards",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Coupon Loyalty Program",
      title: "Coupon Loyalty Program to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading:
        "Turn digital coupons into repeat purchases and stronger customer loyalty.",
      microSubHeading: "",
      description: [
        "Fydo is a simple **coupon loyalty program** that helps retail businesses create digital coupons, reward customers, increase repeat purchases, and build long-term customer relationships.",
        "Move beyond traditional paper coupons and one-time discounts. With Fydo's **coupon loyalty program**, you can create personalized coupon offers, reward customers, track customer engagement, and connect with customers through **Free WhatsApp Marketing**.",
        "No complicated setup. No expensive loyalty systems. **Works with or without a POS.**",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need a Coupon Loyalty Program?",
      subHeading:
        "Coupons can attract customers, but traditional coupon systems often fail to create long-term customer relationships.",
      description: [
        "Without a **coupon loyalty program**, businesses may rely on paper coupons, generic discounts, or manual coupon tracking to encourage customers to return.",
        "Traditional coupon methods can be difficult to manage, difficult to track, and provide limited opportunities for ongoing customer engagement.",
      ],
      points: [
        {
          title: "Paper-Based Coupons",
          text: "Physical coupons can be lost, forgotten, or damaged, making them less convenient for customers.",
        },
        {
          title: "Low Customer Retention",
          text: "Without a **coupon loyalty program**, businesses may struggle to turn one-time buyers into repeat customers.",
        },
        {
          title: "Generic Discounts",
          text: "Offering the same coupon to every customer may not create relevant or personalized customer experiences.",
        },
        {
          title: "Manual Coupon Management",
          text: "Creating, distributing, and tracking physical coupons can become difficult as your customer base grows.",
        },
        {
          title: "Limited Customer Engagement",
          text: "Traditional coupons often end after redemption, making it difficult to maintain an ongoing relationship with customers.",
        },
        {
          title: "No Customer Insights",
          text: "Without digital coupon data, businesses may struggle to understand customer behavior and identify which offers drive repeat purchases.",
        },
      ],
      endingLine:
        "**The result?** Businesses continue spending on customer acquisition instead of using a **coupon loyalty program** to retain existing customers, increase repeat purchases, and improve customer lifetime value.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Coupon into a Customer Loyalty Opportunity",
      description: [
        "Fydo's **coupon loyalty program** helps retail businesses move from traditional paper coupons and one-time discounts to a simple digital customer loyalty experience.",
      ],
      listLabel: "With Fydo's **coupon loyalty program**, you can:",
      steps: [
        "Create digital coupons for your customers",
        "Offer discounts and special deals",
        "Create customized coupon campaigns",
        "Reward repeat customers",
        "Set coupon validity and usage conditions",
        "Create multiple coupon codes",
        "Encourage customers to make repeat purchases",
        "Engage customers through **Free WhatsApp Marketing**",
        "Track customer activity and coupon engagement",
        "Manage your coupon loyalty program from your mobile",
      ],
      bottomLine:
        "Instead of using coupons only as a short-term promotional tool, use a **coupon loyalty program** to create an ongoing connection with every customer.",
    },
    featureSection: {
      heading: "Everything You Need to Build a Powerful Coupon Loyalty Program",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Create Digital Coupons",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make every offer easy to access with a digital **coupon loyalty program**.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Create digital coupons",
            "Offer percentage or fixed-value discounts",
            "Set coupon validity",
            "Define minimum purchase requirements",
            "Create customized coupon rules",
            "Run special promotional campaigns",
            "Encourage customers to make their next purchase",
          ],
          closing: [
            "Turn traditional coupons into a simple digital customer loyalty experience.",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Don't let your customer relationship end after the coupon is redeemed.",
            "Fydo's **coupon loyalty program** works with **Free WhatsApp Marketing** to help businesses stay connected with their customers.",
            "You can:",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share digital coupons",
            "Promote special offers",
            "Announce new products",
            "Promote seasonal campaigns",
            "Remind customers about available coupons",
            "Re-engage inactive customers",
            "Share festival offers",
            "Encourage customers to return",
          ],
          closing: [
            "Keep your customers connected with your business beyond every transaction.",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Personalized Coupon Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Every customer has different purchasing habits. A **coupon loyalty program** makes it easier to create relevant offers for different customer groups.",
            "Create personalized coupons for:",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "New customers",
            "Repeat customers",
            "High-value customers",
            "Inactive customers",
            "Frequent shoppers",
            "Festival shoppers",
            "Customers who haven't visited recently",
          ],
          closing: [
            "Give customers offers that are relevant to their shopping behavior and encourage them to return.",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Create Multiple Coupon Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Run different coupon campaigns for different business objectives.",
            "With Fydo, you can create coupons for:",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "New customer acquisition",
            "Repeat purchases",
            "Seasonal promotions",
            "Festival offers",
            "Product launches",
            "Special occasions",
            "Customer-specific promotions",
          ],
          closing: [
            "Manage different campaigns through one **coupon loyalty program**.",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "A digital **coupon loyalty program** gives businesses greater visibility into customer activity.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Track coupon usage",
            "Monitor customer activity",
            "Identify repeat customers",
            "Understand purchase behavior",
            "Monitor customer engagement",
            "Measure campaign performance",
            "Use customer insights to improve retention",
          ],
          closing: [
            "Make better customer retention decisions using real customer activity.",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Digital Coupons Without Paper",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Move away from traditional printed coupons and manual coupon management.",
            "Fydo's **coupon loyalty program** allows businesses to create and manage digital coupons, making customer offers easier to manage and more convenient for customers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "You don't need an expensive POS system to start your **coupon loyalty program**.",
            "Fydo is designed to work with businesses **with or without a POS**, making digital coupon loyalty accessible to local shops, independent retailers, and growing businesses.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Digital Coupons for Every Type of Retail Business",
      subHeading:
        "Fydo helps businesses across different industries use digital offers and rewards to increase repeat purchases, promote products, and build stronger customer relationships.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Offer discounts on repeat purchases, monthly shopping, festive campaigns, and special customer offers.",
        },
        {
          title: "Clothing Stores",
          detail:
            "Create digital coupons to encourage customers to return for new collections, seasonal shopping, and special promotions.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Offer coupons for repeat appointments, selected services, birthdays, festivals, and special occasions.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Use digital offers to increase repeat visits, promote special meals, and encourage customers to order again.",
        },
        {
          title: "Pharmacies",
          detail:
            "Create relevant customer offers and promotions to encourage repeat visits and build stronger customer relationships.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Use personalized digital coupons to promote products, reward customers, and increase purchase frequency.",
        },
      ],
      endingLine:
        "Whether you're running a neighborhood store or a growing retail business, a digital coupon strategy can help turn promotional offers into stronger, long-term customer relationships.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Loyalty & Rewards",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give customers a convenient reason to return and make another purchase with relevant offers and rewards.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Turn every coupon into an opportunity for another sale by encouraging customers to shop again.",
        },
        {
          title: "Improve Customer Retention",
          text: "Keep existing customers engaged with relevant offers instead of relying only on new customer acquisition.",
        },
        {
          title: "Move Beyond Traditional Coupons",
          text: "Replace paper-based coupons and manual tracking with a simple digital rewards solution that is easier to manage and scale.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Keep customers engaged with personalized coupons, promotional campaigns, rewards, and **Free WhatsApp Marketing**.",
        },
        {
          title: "Personalize Customer Offers",
          text: "Create relevant campaigns for different customer segments based on purchasing behavior and engagement.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Encourage existing customers to purchase more frequently and stay connected with your business.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Use **Free WhatsApp Marketing** to communicate coupons, offers, promotions, and campaigns directly with customers.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage your digital offers and rewards from your mobile without complicated loyalty technology.",
        },
      ],
      endingLine:
        "Fydo helps you move from traditional coupons to a simple, digital way of building customer loyalty.",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Thousands of businesses use Fydo's loyalty and customer engagement solutions to reward customers, promote offers, and encourage repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo makes it easier to engage customers with digital offers and rewards.",
          description:
            "Our customers have another reason to come back and shop with us.",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Creating Digital Coupons in 3 Simple Steps",
      description:
        "Use **Free WhatsApp Marketing** to keep customers informed about available offers, promotions, and upcoming campaigns.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Create Your Digital Coupon",
          description:
            "Use Fydo to create your coupon, set the offer value, validity, usage conditions, and customer eligibility.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Share Offers With Customers",
          description:
            "Reach your customers with digital coupons, promotional offers, and **Free WhatsApp Marketing**.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Customers Redeem & Return",
          description:
            "Customers redeem their offers and have another reason to make their next purchase from your business.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a coupon loyalty program?",
          answer:
            "A coupon loyalty program is a digital customer loyalty system that uses coupons, offers, and rewards to encourage customers to make repeat purchases and stay engaged with a business.",
        },
        {
          question: "How does Fydo's coupon rewards system work?",
          answer:
            "Businesses can create digital coupons through Fydo and define their offers, validity, and usage conditions. Customers can use eligible coupons while shopping, giving them an additional reason to return.",
        },
        {
          question: "Is this suitable for small businesses?",
          answer:
            "Yes. Fydo is designed for small and medium-sized businesses, including local retail shops, and can be managed easily from a mobile device.",
        },
        {
          question: "What types of coupons can I create with Fydo?",
          answer:
            "Businesses can create different digital coupon offers, including fixed-value discounts, percentage discounts, promotional offers, customer-specific coupons, and special campaign offers.",
        },
        {
          question: "Do I need a POS system to use Fydo?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for a wide range of retail businesses.",
        },
        {
          question: "Can I customize my coupon program?",
          answer:
            "Yes. Businesses can customize coupon values, validity periods, usage conditions, customer eligibility, and promotional campaigns according to their business requirements.",
        },
        {
          question: "Can I create multiple coupon codes?",
          answer:
            "Yes. Fydo allows businesses to create and manage multiple coupon codes for different campaigns, customer segments, products, or promotional activities.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate coupons, offers, promotions, and campaigns directly with customers.",
        },
        {
          question: "Can digital coupons increase repeat customers?",
          answer:
            "Yes. Relevant coupons and rewards can give customers a convenient reason to return, redeem offers, and make future purchases, helping businesses improve customer retention.",
        },
        {
          question: "Is Fydo only for retail stores?",
          answer:
            "No. Fydo can be used by different business categories, including grocery stores, clothing stores, salons, restaurants, pharmacies, lifestyle stores, and other local businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Every Coupon into a Reason to Come Back",
      subtitle:
        "Don't let your coupons become one-time discounts. Start creating digital coupons, engage customers with **Free WhatsApp Marketing**, encourage repeat purchases, and build stronger customer relationships with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "digital coupons",
      "WhatsApp coupon marketing",
      "customer segmentation",
      "win-back offers",
    ],
  },

  "customer-retention-management": {
    slug: "customer-retention-management",
    cluster: "retention-platform",
    showMerchantsSection: false,
    seo: {
      title: "Customer Retention Management Software for Retail",
      description:
        "Improve customer retention management with Fydo. Reward customers, increase repeat purchases, personalize offers, and engage customers with Free WhatsApp Marketing.",
      keywords: [
        "customer retention management",
        "customer retention management software",
        "customer retention platform",
        "customer retention solution",
        "customer retention system",
        "customer retention tools",
        "customer loyalty software",
        "customer engagement software",
        "retail customer retention",
        "customer retention technology",
        "customer relationship management for retail",
        "customer retention management India",
        "customer retention management for retail businesses",
        "retail customer retention software",
        "customer loyalty program",
        "repeat customer software",
        "customer engagement platform",
        "customer rewards platform",
        "digital loyalty program",
        "customer retention strategy",
        "repeat purchase strategy",
        "customer loyalty solution",
        "cashback loyalty program",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Customer Retention Management",
      title:
        "Customer Retention Management to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn one-time shoppers into loyal, repeat customers with a simple and powerful customer retention solution built for offline retail businesses.",
        "Fydo helps retailers build stronger customer relationships, encourage repeat purchases, and increase customer lifetime value through digital loyalty, rewards, personalized offers, and customer engagement.",
        "With Fydo, you can manage customer relationships, reward loyal shoppers, run targeted campaigns, and bring customers back-all from one easy-to-use platform.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need Customer Retention Management?",
      subHeading:
        "Getting customers through the door is only the first step. The real challenge is getting them to return regularly and choose your business over competitors.",
      description: [],
      listLabel:
        "Without a structured retention strategy, retailers often face:",
      points: [
        {
          title: "Customers Don't Return Regularly",
          text: "Customers may make a purchase once but have no reason or incentive to come back.",
        },
        {
          title: "Low Customer Loyalty",
          text: "Without rewards or personalized engagement, customers can easily switch to another store offering better value.",
        },
        {
          title: "No Customer Follow-Up",
          text: "Retailers often lose opportunities to reconnect with customers after their purchase.",
        },
        {
          title: "Generic Offers Don't Work",
          text: "Sending the same offer to every customer can result in low engagement and wasted promotional efforts.",
        },
        {
          title: "Limited Customer Insights",
          text: "Without useful customer data, it becomes difficult to understand buying patterns and identify your most valuable customers.",
        },
        {
          title: "High Dependence on New Customers",
          text: "Continuously acquiring new shoppers can be more difficult and expensive than encouraging existing customers to return.",
        },
      ],
      endingLine:
        "**The result?** Missed repeat sales, weaker customer relationships, and lower long-term revenue.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Purchase into a Customer Retention Opportunity",
      description: [
        "Fydo's **customer retention management** platform helps businesses stay connected with their customers beyond the point of purchase.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Create digital loyalty and rewards programs",
        "Offer cashback, points, discounts, and coupons",
        "Encourage customers to make repeat purchases",
        "Send personalized offers based on customer behavior",
        "Engage customers through **Free WhatsApp Marketing**",
        "Track customer activity and engagement",
        "Identify valuable and frequently returning customers",
        "Run targeted promotional campaigns",
        "Use digital QR-based customer engagement",
        "Build long-term customer relationships",
      ],
      bottomLine:
        "Instead of simply completing a transaction, Fydo helps you create an ongoing relationship that gives customers more reasons to return.",
    },
    featureSection: {
      heading:
        "Everything You Need for Effective Customer Retention Management",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers for Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a reason to return by offering meaningful rewards.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Cashback rewards",
            "Loyalty points",
            "Digital discounts",
            "Coupons and promotional offers",
            "Flexible reward configurations",
            "Rewards for repeat purchases",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers after they leave your store with **Free WhatsApp Marketing**.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share offers and promotions",
            "Communicate new products and services",
            "Re-engage inactive customers",
            "Promote special sales and campaigns",
            "Build regular customer communication",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Personalized Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make your promotions more relevant by engaging customers based on their purchase behavior and preferences.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "Create targeted campaigns",
            "Offer personalized rewards",
            "Promote relevant products",
            "Reward high-value customers",
            "Encourage specific purchase behaviors",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand how customers interact with your business.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Monitor customer activity",
            "Track purchases and rewards",
            "View customer engagement",
            "Identify repeat shoppers",
            "Understand loyalty trends",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Replace Traditional Loyalty Methods",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Move beyond paper cards, manual registers, and disconnected customer records.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Digital loyalty experience",
            "QR-based engagement",
            "Easy customer identification",
            "Automated reward tracking",
            "Convenient customer experience",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo is designed for traditional retailers as well as businesses already using POS systems.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [
            "No complicated hardware required",
            "Easy merchant onboarding",
            "Works alongside existing business processes",
            "Suitable for small and mid-sized retailers",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Retention for Every Type of Retail Business",
      subHeading:
        "Whether you run a neighborhood store or a growing retail business, Fydo helps you create stronger relationships with your customers.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward frequent shoppers and encourage customers to return for their regular purchases.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Re-engage customers with new collections, personalized offers, and seasonal promotions.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Encourage customers to return for their next appointment with rewards and timely offers.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Build repeat dining habits with cashback, loyalty rewards, and targeted promotions.",
        },
        {
          title: "Pharmacies",
          detail:
            "Strengthen customer relationships and encourage regular visits through digital rewards and engagement.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Create personalized shopping experiences and reward customers who regularly engage with your business.",
        },
      ],
      endingLine:
        "Fydo makes it easier for businesses across different retail categories to turn existing shoppers into loyal customers.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Retention & Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give shoppers compelling reasons to return instead of relying only on new customer acquisition.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Encourage customers to purchase more frequently through rewards, offers, and targeted campaigns.",
        },
        {
          title: "Build Long-Term Customer Loyalty",
          text: "Create an ongoing relationship with customers rather than treating every purchase as a one-time transaction.",
        },
        {
          title: "Encourage More Frequent Purchases",
          text: "Use cashback, points, coupons, and personalized incentives to create stronger purchase habits.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Stay connected with your audience through digital communication and **Free WhatsApp Marketing**.",
        },
        {
          title: "Personalize Customer Experiences",
          text: "Deliver more relevant offers by understanding customer activity and purchase behavior.",
        },
        {
          title: "Understand Your Customers Better",
          text: "Use customer insights to identify repeat shoppers, valuable customers, and opportunities for re-engagement.",
        },
        {
          title: "Move to Digital Loyalty",
          text: "Replace traditional loyalty cards and manual processes with a simple digital rewards experience.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Fydo is designed to be easy for retailers to set up and manage without complicated processes.",
        },
      ],
      endingLine:
        "With Fydo, customer loyalty becomes an ongoing business strategy rather than a one-time promotional activity.",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps retailers build stronger customer relationships through digital loyalty, rewards, engagement, and retention solutions. From local stores to growing retail businesses, Fydo enables merchants to create meaningful customer experiences and encourage more repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helps us stay connected with our customers, reward them for their purchases, and give them more reasons to come back.",
          description: "",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Improve Customer Retention in 3 Simple Steps",
      description:
        "Simple setup. Stronger relationships. More repeat purchases.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up",
          description:
            "Create your business profile, configure your rewards, and set up your digital customer engagement program.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn",
          description:
            "Customers make purchases and receive cashback, points, discounts, or other rewards through the Fydo experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Customers Return & Redeem",
          description:
            "Customers come back to use their rewards, while you continue engaging them through offers, campaigns, and **Free WhatsApp Marketing**.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is customer retention management?",
          answer:
            "Customer retention management is the process of engaging existing customers, encouraging repeat purchases, rewarding loyalty, and building long-term customer relationships.",
        },
        {
          question: "How does Fydo's platform help with retention?",
          answer:
            "Fydo combines digital loyalty, rewards, personalized offers, customer engagement, and marketing tools to help businesses encourage customers to return.",
        },
        {
          question: "Is Fydo suitable for small retail businesses?",
          answer:
            "Yes. Fydo is designed for small and mid-sized retailers including grocery stores, salons, restaurants, pharmacies, clothing stores, and specialty businesses.",
        },
        {
          question: "What types of rewards can I offer?",
          answer:
            "You can offer cashback, points, discounts, coupons, and other customized rewards based on your business requirements.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo can work with or without a POS system, making it suitable for traditional offline retailers.",
        },
        {
          question: "Can I personalize customer offers?",
          answer:
            "Yes. Businesses can create targeted campaigns and offers to make customer engagement more relevant.",
        },
        {
          question: "Can Fydo help bring inactive customers back?",
          answer:
            "Yes. Businesses can use campaigns, rewards, offers, and customer communication to re-engage customers who have not purchased recently.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo includes Free WhatsApp Marketing to help businesses communicate offers, promotions, and updates to their customers.",
        },
        {
          question: "Can this help increase repeat purchases?",
          answer:
            "A structured loyalty and engagement strategy can give customers more reasons to return and make repeat purchases.",
        },
        {
          question: "Is Fydo only for retail stores?",
          answer:
            "No. Fydo can support different offline businesses that want to improve customer loyalty, engagement, and retention.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Existing Customers into Repeat Customers",
      subtitle:
        "Your existing customers are one of your most valuable growth opportunities. Give them reasons to return with digital rewards, personalized offers, loyalty programs, and **Free WhatsApp Marketing**. Build stronger customer relationships and make every purchase an opportunity for the next one.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "customer retention management",
      "retention dashboard",
      "repeat customer tracking",
      "win-back campaigns",
    ],
  },

  "customer-retention-software": {
    slug: "customer-retention-software",
    cluster: "retention-platform",
    showMerchantsSection: false,
    seo: {
      title: "Customer Retention Software for Retail Businesses in India",
      description:
        "Increase repeat customers with Fydo's customer retention software. Reward customers, personalize offers, reduce churn, and engage customers with Free WhatsApp Marketing.",
      keywords: [
        "customer retention software",
        "customer retention software for small businesses",
        "customer retention platform",
        "customer retention management software",
        "customer retention solution",
        "customer loyalty software",
        "customer engagement software",
        "customer retention tools",
        "customer loyalty platform",
        "customer retention technology",
        "retail customer retention software",
        "customer retention software India",
        "customer retention software for retail businesses",
        "customer retention platform India",
        "customer retention solution for retail",
        "customer loyalty software India",
        "customer churn reduction",
        "repeat customer software",
        "customer engagement platform",
        "digital loyalty program",
        "digital customer rewards",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Customer Retention Software",
      title: "Customer Retention Software to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading: "Turn one-time buyers into loyal, repeat customers.",
      microSubHeading: "",
      description: [
        "Fydo is simple **customer retention software** that helps retail businesses increase repeat purchases, engage existing customers, build loyalty, and improve customer lifetime value.",
        "Instead of constantly spending to acquire new customers, Fydo helps you retain the customers you already have through cashback, loyalty points, discounts, personalized offers, campaigns, and **Free WhatsApp Marketing**.",
        "No complicated setup. No expensive infrastructure. **Works with or without a POS.**",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need Customer Retention Software?",
      subHeading:
        "Getting a customer to make their first purchase is only the beginning. The real challenge is getting them to return, purchase more frequently, and stay connected with your business.",
      description: [
        "Without **customer retention software**, businesses may rely heavily on discounts, advertising, and manual follow-ups to bring customers back.",
        "Traditional methods also make it difficult to understand customer behavior and identify which customers are at risk of becoming inactive.",
      ],
      points: [
        {
          title: "Low Repeat Purchases",
          text: "Customers may make a single purchase and move to competitors without a strong reason to return.",
        },
        {
          title: "Customer Churn",
          text: "Businesses may lose existing customers without realizing that their engagement is declining.",
        },
        {
          title: "High Acquisition Costs",
          text: "Continuously acquiring new customers can be more expensive than retaining existing ones.",
        },
        {
          title: "Generic Promotions",
          text: "Sending the same offer to every customer may not be effective for different customer segments.",
        },
        {
          title: "Limited Customer Engagement",
          text: "Businesses may struggle to stay connected with customers after their purchase.",
        },
        {
          title: "No Retention Insights",
          text: "Without customer data, it can be difficult to understand purchase frequency, customer behavior, and loyalty.",
        },
      ],
      endingLine:
        "**The result?** Businesses spend more on acquiring customers while missing opportunities to increase repeat sales and build long-term customer relationships.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Customer Purchases into Long-Term Relationships",
      description: [
        "Fydo's **customer retention software** helps businesses create a structured digital loyalty and engagement strategy that encourages customers to return.",
      ],
      listLabel: "With Fydo's **customer retention software**, you can:",
      steps: [
        "Reward customers for purchases",
        "Offer cashback and loyalty points",
        "Create discounts and digital coupons",
        "Create personalized offers",
        "Segment customers based on activity",
        "Re-engage inactive customers",
        "Create targeted loyalty campaigns",
        "Track customer engagement",
        "Understand customer purchase behavior",
        "Engage customers through **Free WhatsApp Marketing**",
        "Manage your retention activities from your mobile",
      ],
      bottomLine:
        "Instead of focusing only on acquiring new customers, build a system that continuously gives existing customers reasons to return.",
    },
    featureSection: {
      heading:
        "Everything You Need to Build a Powerful Customer Retention Strategy",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers for Repeat Purchases",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a reason to keep coming back with digital rewards.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Offer cashback on purchases",
            "Award loyalty points",
            "Create discounts and coupons",
            "Set customized reward rules",
            "Reward repeat customers",
            "Create reward milestones",
            "Run special promotional campaigns",
            "Encourage customers to purchase again",
          ],
          closing: [
            "Turn everyday purchases into opportunities to strengthen customer loyalty.",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Customer retention doesn't stop after checkout.",
            "Fydo's **customer retention software** works with **Free WhatsApp Marketing** to help businesses stay connected with existing customers.",
            "You can:",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share personalized offers",
            "Promote rewards and coupons",
            "Send promotional campaigns",
            "Announce new products",
            "Share festival offers",
            "Remind customers about available rewards",
            "Re-engage inactive customers",
            "Encourage customers to return",
          ],
          closing: [
            "Keep your business connected with customers beyond every transaction.",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Identify & Segment Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Different customers require different retention strategies.",
            "With Fydo, businesses can identify groups such as:",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "New customers",
            "Repeat customers",
            "Frequent shoppers",
            "High-value customers",
            "Inactive customers",
            "Recently active customers",
            "Customers who haven't purchased recently",
            "Festival shoppers",
          ],
          closing: [
            "Use customer segments to create more relevant engagement and retention campaigns.",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Don't let inactive customers disappear without taking action.",
            "Create targeted campaigns to:",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Identify customers who haven't returned",
            "Offer relevant incentives",
            "Share personalized promotions",
            "Remind customers about rewards",
            "Promote new products",
            "Encourage another purchase",
            "Bring inactive customers back",
          ],
          closing: [
            "Turn declining customer engagement into an opportunity for reactivation.",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand how customers interact with your business and loyalty activities.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Track customer activity",
            "Monitor repeat purchases",
            "Understand purchase frequency",
            "Identify loyal customers",
            "Monitor reward usage",
            "Measure campaign performance",
            "Analyze customer engagement",
          ],
          closing: [
            "Use customer insights to make better decisions about your retention strategy.",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Create Personalized Retention Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Not every customer needs the same offer.",
            "Create campaigns based on:",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [
            "Customer activity",
            "Purchase frequency",
            "Customer value",
            "Previous purchases",
            "Loyalty status",
            "Inactivity",
            "Special occasions",
            "Seasonal shopping behavior",
          ],
          closing: [
            "Deliver more relevant experiences and give customers a stronger reason to return.",
          ],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "You don't need an expensive POS system to start using **customer retention software**.",
            "Fydo works with businesses **with or without a POS**, making digital customer retention accessible to local shops, independent retailers, salons, restaurants, cafes, and growing businesses.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Retention for Every Type of Retail Business",
      subHeading:
        "Fydo helps businesses across different industries improve repeat purchases, strengthen customer relationships, and keep existing customers engaged.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward frequent shoppers, promote relevant offers, and encourage customers to return for their regular purchases.",
        },
        {
          title: "Clothing Stores",
          detail:
            "Re-engage customers for new collections, seasonal shopping, festivals, and special promotions.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Encourage repeat appointments with rewards, personalized offers, and timely customer engagement.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Increase repeat visits and orders with digital rewards, promotions, and targeted campaigns.",
        },
        {
          title: "Pharmacies",
          detail:
            "Maintain stronger relationships with regular customers through convenient rewards and relevant engagement.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Use customer insights and personalized offers to increase purchase frequency and build loyalty.",
        },
      ],
      endingLine:
        "Whether you're running a neighborhood store or a growing retail business, retaining existing customers can help create more sustainable and predictable growth.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Retention & Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give existing customers a consistent reason to return through rewards, personalized offers, and engaging campaigns.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Encourage customers to purchase more frequently by rewarding their continued relationship with your business.",
        },
        {
          title: "Reduce Customer Churn",
          text: "Identify declining engagement and use targeted offers and campaigns to encourage customers to return.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Stay connected with customers through rewards, promotions, personalized campaigns, and **Free WhatsApp Marketing**.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Encourage customers to shop more frequently and build stronger relationships over a longer period.",
        },
        {
          title: "Personalize Customer Experiences",
          text: "Create relevant offers and rewards for different customer groups based on their activity and purchasing behavior.",
        },
        {
          title: "Reduce Dependence on New Customer Acquisition",
          text: "Build stronger relationships with your existing customer base instead of relying entirely on continuous new customer acquisition.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Use **Free WhatsApp Marketing** to share offers, rewards, promotions, and re-engagement campaigns directly with customers.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage your loyalty, engagement, and retention activities from your mobile without complicated technology.",
        },
      ],
      endingLine:
        "Fydo helps businesses turn existing customers into loyal customers and repeat buyers.",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Thousands of businesses use Fydo's loyalty and customer engagement solutions to reward customers, improve engagement, and encourage repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo makes it easier to stay connected with our customers after every purchase.",
          description:
            "The rewards and engagement tools give customers more reasons to return to our business.",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Improve Customer Retention in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Loyalty & Retention Strategy",
          description:
            "Create your rewards, offers, customer segments, and campaigns based on your business goals.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Engage & Reward Customers",
          description:
            "Customers make purchases, earn rewards, and receive relevant offers based on your loyalty strategy.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Bring Customers Back",
          description:
            "Use personalized campaigns, rewards, and **Free WhatsApp Marketing** to encourage customers to return and make their next purchase.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is customer retention software?",
          answer:
            "Customer retention software is a digital solution that helps businesses engage existing customers, encourage repeat purchases, reduce customer churn, and build long-term loyalty.",
        },
        {
          question: "How does Fydo help with customer retention?",
          answer:
            "Fydo helps businesses reward customers, create personalized offers, segment customers, run campaigns, track engagement, and use Free WhatsApp Marketing to encourage customers to return.",
        },
        {
          question: "Is this suitable for small businesses?",
          answer:
            "Yes. Fydo is designed for small and medium-sized businesses, including local retail stores, salons, restaurants, cafes, pharmacies, and other customer-facing businesses.",
        },
        {
          question: "How can I increase repeat customers?",
          answer:
            "Businesses can use rewards, cashback, loyalty points, personalized offers, targeted campaigns, and customer engagement to give shoppers more reasons to return.",
        },
        {
          question: "Can Fydo help reduce customer churn?",
          answer:
            "Fydo helps businesses identify customer activity and engagement patterns and create targeted campaigns to re-engage customers who may not have purchased recently.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for businesses with different technology setups.",
        },
        {
          question: "Can I create personalized retention campaigns?",
          answer:
            "Yes. Businesses can create targeted campaigns based on customer activity, purchase behavior, customer segments, and other business requirements.",
        },
        {
          question: "Can I reward repeat customers?",
          answer:
            "Yes. Fydo allows businesses to use cashback, points, discounts, coupons, and other rewards to encourage repeat purchases.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, promotions, and re-engagement campaigns directly with customers.",
        },
        {
          question: "Is Fydo only for retail stores?",
          answer:
            "No. Fydo can be used by different business categories, including grocery stores, clothing stores, salons, restaurants, pharmacies, lifestyle stores, and other local businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Existing Customers into Repeat Customers",
      subtitle:
        "Don't let your customers become one-time buyers. Reward customers, personalize their experience, engage them with **Free WhatsApp Marketing**, and build stronger long-term relationships with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "customer churn",
      "win-back campaign",
      "customer retention",
      "repeat customers",
    ],
  },

  "customer-reward-program": {
    slug: "customer-reward-program",
    cluster: "rewards-platform",
    showMerchantsSection: false,
    seo: {
      title: "Customer Reward Program for Retail Businesses in India",
      description:
        "Increase repeat sales with Fydo's customer reward program. Offer cashback, points, discounts, and digital rewards while engaging customers through Free WhatsApp Marketing.",
      keywords: [
        "customer reward program",
        "customer reward program software",
        "customer rewards software",
        "digital customer rewards program",
        "customer loyalty rewards program",
        "customer rewards platform",
        "customer loyalty program",
        "digital rewards program",
        "loyalty rewards software",
        "retail rewards program",
        "customer reward software",
        "customer reward program India",
        "customer reward program for small businesses",
        "customer reward program for retail businesses",
        "digital customer rewards",
        "customer loyalty software India",
        "retail loyalty program",
        "digital rewards platform",
        "customer retention software",
        "loyalty program software",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Customer Reward Program",
      title: "Customer Reward Program to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading:
        "Turn every purchase into a rewarding experience and every reward into a reason to return.",
      microSubHeading: "",
      description: [
        "Fydo is a simple **customer reward program** that helps retail businesses reward customers, encourage repeat purchases, increase customer engagement, and build long-term loyalty.",
        "Create a rewarding experience with cashback, points, discounts, coupons, and personalized offers. Engage customers through **Free WhatsApp Marketing**, encourage them to return, and build stronger relationships with your business.",
        "No complicated setup. No expensive loyalty infrastructure. **Works with or without a POS.**",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need a Customer Reward Program?",
      subHeading:
        "Customers have more choices than ever, making it difficult for businesses to turn one-time buyers into regular customers.",
      description: [
        "Without a structured **customer reward program**, businesses may rely on discounts and promotions without creating a consistent reason for customers to return.",
        "Traditional loyalty methods can also be difficult to manage, track, and personalize as the customer base grows.",
      ],
      points: [
        {
          title: "Low Repeat Purchases",
          text: "Customers may shop once and move to competitors if there is no ongoing reason to return.",
        },
        {
          title: "No Structured Rewards",
          text: "Without a **customer reward program**, businesses may struggle to consistently recognize and reward loyal customers.",
        },
        {
          title: "Traditional Loyalty Methods",
          text: "Paper cards and manual reward tracking can become difficult to manage and scale.",
        },
        {
          title: "Generic Offers",
          text: "Giving the same discount to every customer may not create relevant or personalized experiences.",
        },
        {
          title: "Limited Customer Engagement",
          text: "Businesses may have few ways to stay connected with customers after a purchase.",
        },
        {
          title: "No Customer Insights",
          text: "Without digital customer data, it can be difficult to understand purchasing behavior and identify your most valuable customers.",
        },
      ],
      endingLine:
        "**The result?** Businesses lose opportunities to increase repeat sales, strengthen customer relationships, and improve customer lifetime value.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Purchase into a Customer Reward Opportunity",
      description: [
        "Fydo's **customer reward program** helps businesses create a simple digital loyalty experience that encourages customers to shop again.",
      ],
      listLabel: "With Fydo's **customer reward program**, you can:",
      steps: [
        "Reward customers for eligible purchases",
        "Offer cashback and loyalty points",
        "Create discounts and special offers",
        "Set customized reward rules",
        "Create personalized customer campaigns",
        "Reward repeat customers",
        "Create multiple coupon codes",
        "Track customer activity and engagement",
        "Engage customers through **Free WhatsApp Marketing**",
        "Manage your rewards from your mobile",
      ],
      bottomLine:
        "Instead of using discounts only as short-term promotions, create a rewarding customer experience that gives shoppers a clear reason to return.",
    },
    featureSection: {
      heading:
        "Everything You Need to Build a Powerful Customer Reward Program",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers for Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make everyday shopping more rewarding with a simple digital rewards system.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Give cashback on eligible purchases",
            "Award loyalty points",
            "Offer discounts and coupons",
            "Set customized reward rules",
            "Create reward milestones",
            "Reward repeat purchases",
            "Create special promotional rewards",
            "Give customers a reason to return",
          ],
          closing: [
            "Turn regular transactions into opportunities to build customer loyalty.",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Don't let your customer relationship end after checkout.",
            "Fydo's **customer reward program** works with **Free WhatsApp Marketing** to help businesses stay connected with customers.",
            "You can:",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share available rewards",
            "Promote special offers",
            "Announce new products",
            "Send reward reminders",
            "Promote seasonal campaigns",
            "Re-engage inactive customers",
            "Share festival offers",
            "Encourage customers to shop again",
          ],
          closing: [
            "Keep customers connected with your business beyond every transaction.",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Personalized Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Different customers have different shopping habits. Create relevant rewards for different customer groups.",
            "Offer personalized incentives for:",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "New customers",
            "Repeat customers",
            "High-value customers",
            "Frequent shoppers",
            "Inactive customers",
            "Festival shoppers",
            "Customers who haven't visited recently",
            "Special occasions",
          ],
          closing: [
            "Give customers offers that match their engagement and purchasing behavior.",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Create Multiple Reward Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Run different campaigns based on your business objectives.",
            "Create rewards for:",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Customer acquisition",
            "Repeat purchases",
            "Seasonal promotions",
            "Festival campaigns",
            "Product launches",
            "Special occasions",
            "Customer-specific offers",
            "Promotional periods",
          ],
          closing: [
            "Manage different campaigns from one centralized platform.",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand how customers interact with your rewards and offers.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Track customer activity",
            "Monitor reward usage",
            "Identify repeat customers",
            "Understand purchase frequency",
            "Monitor engagement",
            "Measure campaign performance",
            "Use customer insights to improve retention",
          ],
          closing: [
            "Use customer activity to make better decisions about your loyalty and retention strategy.",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Digital Rewards Without Physical Cards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Move beyond paper loyalty cards and manual reward tracking.",
            "Fydo gives customers a convenient digital rewards experience while helping businesses manage customer participation and rewards more efficiently.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "You don't need an expensive POS system to start using a **customer reward program**.",
            "Fydo works with businesses **with or without a POS**, making digital customer loyalty accessible to local shops, independent retailers, salons, restaurants, cafes, and growing businesses.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Digital Customer Rewards for Every Type of Retail Business",
      subHeading:
        "Fydo helps businesses across different industries reward customers, encourage repeat purchases, and build stronger customer relationships.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward regular shoppers with cashback, points, discounts, and special offers to encourage repeat purchases.",
        },
        {
          title: "Clothing Stores",
          detail:
            "Use personalized rewards to encourage customers to return for new collections, seasonal shopping, and special promotions.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Reward customers after services and encourage them to return for their next appointment.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Use digital rewards to increase repeat visits, promote offers, and encourage customers to order again.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger relationships with regular customers through convenient rewards and personalized offers.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Create customized incentives to encourage repeat purchases and improve customer engagement.",
        },
      ],
      endingLine:
        "Whether you're running a neighborhood store or a growing business, digital rewards can help turn regular customers into loyal customers.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Loyalty & Rewards",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give customers a consistent reason to return by rewarding their purchases and engagement.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Turn every purchase into an opportunity for another sale by giving customers rewards they can use on future purchases.",
        },
        {
          title: "Improve Customer Retention",
          text: "Keep existing customers engaged with relevant rewards, offers, and campaigns instead of relying only on new customer acquisition.",
        },
        {
          title: "Build Long-Term Loyalty",
          text: "Create a rewarding experience that encourages customers to continue choosing your business.",
        },
        {
          title: "Move Beyond Traditional Loyalty Cards",
          text: "Replace physical cards and manual tracking with a simple digital loyalty solution that is easier to manage.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Keep customers connected through personalized rewards, campaigns, offers, and **Free WhatsApp Marketing**.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Encourage customers to shop more frequently and build stronger relationships over time.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Use **Free WhatsApp Marketing** to share rewards, offers, promotions, and campaigns directly with your customers.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage your loyalty and rewards activities from your mobile without complicated technology.",
        },
      ],
      endingLine:
        "Fydo helps businesses turn everyday purchases into rewarding customer relationships.",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Thousands of businesses use Fydo's loyalty and customer engagement solutions to reward customers, increase engagement, and encourage repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo makes it easy to reward our customers and keep them connected with our business.",
          description:
            "The digital rewards experience gives customers another reason to come back.",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Rewarding Customers in 3 Simple Steps",
      description:
        "Use **Free WhatsApp Marketing** to remind customers about their rewards, promote new offers, and encourage them to return.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Choose how customers earn rewards and configure cashback, points, discounts, coupons, or other offers according to your business goals.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn",
          description:
            "Customers make eligible purchases and receive rewards based on the rules you have created.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Customers Return & Redeem",
          description:
            "Customers come back to your business and use their available rewards on future purchases.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a customer reward program?",
          answer:
            "A customer reward program is a loyalty system that gives customers incentives such as cashback, points, discounts, coupons, or other rewards for purchases and engagement with a business.",
        },
        {
          question: "How does Fydo's rewards system work?",
          answer:
            "Businesses can configure their reward rules through Fydo. Customers make eligible purchases and receive applicable rewards, which can encourage them to return for future purchases.",
        },
        {
          question: "Is this suitable for small businesses?",
          answer:
            "Yes. Fydo is designed for small and medium-sized businesses, including local retail stores, salons, restaurants, cafes, pharmacies, and other customer-facing businesses.",
        },
        {
          question: "What types of rewards can I offer?",
          answer:
            "Businesses can offer cashback, loyalty points, discounts, coupons, promotional offers, and other rewards based on their customer engagement strategy.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for businesses with different technology setups.",
        },
        {
          question: "Can I customize my rewards?",
          answer:
            "Yes. Businesses can customize reward rules, eligibility, offers, campaigns, and other conditions according to their requirements.",
        },
        {
          question: "Can I reward repeat customers?",
          answer:
            "Yes. You can create reward strategies specifically designed to encourage repeat purchases and recognize customers who regularly shop with your business.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate rewards, offers, promotions, and campaigns directly with customers.",
        },
        {
          question: "Can rewards increase repeat purchases?",
          answer:
            "Yes. A well-designed rewards strategy can give customers an additional reason to return and make future purchases, helping improve customer retention.",
        },
        {
          question: "Is Fydo only for retail stores?",
          answer:
            "No. Fydo can be used by different business categories, including grocery stores, clothing stores, salons, restaurants, pharmacies, lifestyle stores, and other local businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Every Purchase into a Reason to Return",
      subtitle:
        "Don't let your customers be one-time buyers. Reward customers for their purchases, engage them with **Free WhatsApp Marketing**, encourage repeat sales, and build stronger customer relationships with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "customer reward program",
      "cashback vs points",
      "reward types for retail shops",
      "coupon vs discount loyalty",
    ],
  },

  "customer-rewards-platform": {
    slug: "customer-rewards-platform",
    cluster: "rewards-platform",
    showMerchantsSection: false,
    seo: {
      title: "Customer Rewards Platform for Retail Businesses in India",
      description:
        "Reward customers and increase repeat sales with Fydo. Offer cashback, points, discounts, and engage customers with Free WhatsApp Marketing.",
      keywords: [
        "customer rewards platform",
        "customer rewards platform software",
        "digital customer rewards platform",
        "customer reward software",
        "customer rewards program",
        "customer loyalty platform",
        "loyalty rewards platform",
        "digital rewards platform",
        "customer rewards software",
        "customer rewards platform India",
        "customer rewards platform for small businesses",
        "customer rewards platform for retail businesses",
        "digital customer rewards",
        "retail rewards platform",
        "customer loyalty software India",
        "digital rewards program",
        "retail loyalty program",
        "customer retention software",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Customer Rewards Platform",
      title: "Customer Rewards Platform to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading:
        "Reward your customers, increase repeat purchases, and build lasting customer loyalty.",
      microSubHeading: "",
      description: [
        "Fydo is a simple **customer rewards platform** that helps retail businesses create digital rewards, engage customers, increase repeat purchases, and build stronger customer relationships.",
        "Move beyond traditional loyalty cards, paper coupons, and manual reward systems. With Fydo's **customer rewards platform**, you can offer cashback, points, discounts, and personalized rewards while connecting with customers through **Free WhatsApp Marketing**.",
        "No complicated setup. No expensive loyalty systems. **Works with or without a POS.**",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need a Customer Rewards Platform?",
      subHeading:
        "Customers appreciate businesses that recognize and reward their loyalty. However, many local retailers still rely on traditional discounts, paper cards, and manual customer records.",
      description: [
        "Without a **customer rewards platform**, businesses may struggle to consistently reward customers, encourage repeat purchases, and maintain ongoing customer engagement.",
        "Traditional reward methods can be difficult to manage, difficult to track, and provide limited customer insights.",
      ],
      points: [
        {
          title: "No Structured Rewards System",
          text: "Customers may not have a convenient way to earn and receive rewards after making purchases.",
        },
        {
          title: "Low Customer Retention",
          text: "Without a **customer rewards platform**, businesses can miss opportunities to turn one-time buyers into repeat customers.",
        },
        {
          title: "Manual Reward Management",
          text: "Physical cards and manual records become difficult to maintain as the customer base grows.",
        },
        {
          title: "Limited Customer Engagement",
          text: "Traditional reward systems often stop at the transaction, making it difficult to stay connected with customers.",
        },
        {
          title: "Dependence on Discounts",
          text: "Businesses may rely on discounts instead of building a structured rewards and retention strategy.",
        },
        {
          title: "Missed Customer Insights",
          text: "Without digital customer activity, businesses may struggle to identify loyal customers and understand purchasing behavior.",
        },
      ],
      endingLine:
        "**The result?** Businesses continue spending on customer acquisition instead of using a **customer rewards platform** to retain existing customers, increase engagement, and improve customer lifetime value.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Purchase into a Reward Opportunity",
      description: [
        "Fydo's **customer rewards platform** helps retail businesses create a simple digital rewards experience that encourages customers to return and shop again.",
      ],
      listLabel: "With Fydo's **customer rewards platform**, you can:",
      steps: [
        "Reward customers after eligible purchases",
        "Offer cashback and loyalty points",
        "Create customized customer rewards",
        "Reward repeat and high-value customers",
        "Create personalized offers",
        "Encourage customers to return and shop again",
        "Engage customers through **Free WhatsApp Marketing**",
        "Track customer activity and reward engagement",
        "Manage your rewards program easily from your mobile",
      ],
      bottomLine:
        "Instead of relying only on discounts or physical loyalty cards, use a **customer rewards platform** to create an ongoing relationship with every customer.",
    },
    featureSection: {
      heading:
        "Everything You Need to Build a Powerful Customer Rewards Platform",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers Digitally",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make every purchase more rewarding with a simple **customer rewards platform**.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Reward customers after eligible purchases",
            "Offer cashback and loyalty points",
            "Create customized reward rules",
            "Set up special reward campaigns",
            "Reward customers during festivals and special occasions",
            "Encourage repeat purchases",
            "Give customers a digital reason to return",
          ],
          closing: [
            "Turn everyday purchases into meaningful customer rewards.",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Don't let your customer relationship end after checkout.",
            "Fydo's **customer rewards platform** works with **Free WhatsApp Marketing** to help businesses stay connected with customers.",
            "You can:",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share reward updates",
            "Promote cashback and loyalty points",
            "Announce special offers",
            "Promote new products",
            "Remind customers about available rewards",
            "Re-engage inactive customers",
            "Send festival and seasonal campaigns",
            "Encourage customers to return",
          ],
          closing: [
            "Keep customers connected with your business beyond every transaction.",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Personalized Customer Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Every customer has different purchasing habits. A **customer rewards platform** makes it easier to create relevant rewards and offers for different customer segments.",
            "Create personalized rewards for:",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "New customers",
            "Repeat customers",
            "High-value customers",
            "Inactive customers",
            "Frequent shoppers",
            "Festival shoppers",
            "Customers who haven't visited recently",
          ],
          closing: [
            "Give customers relevant rewards that encourage them to return.",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Offer Cashback, Points & Discounts",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers different ways to earn value from their purchases.",
            "With Fydo, businesses can create:",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Cashback rewards",
            "Loyalty points",
            "Discounts",
            "Special offers",
            "Purchase-based rewards",
            "Customer-specific promotions",
          ],
          closing: [
            "Build a **customer rewards platform** that fits your business and customer needs.",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "A **customer rewards platform** gives businesses greater visibility into customer activity.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Track repeat purchases",
            "Identify loyal customers",
            "Monitor customer activity",
            "Understand purchase frequency",
            "Track reward engagement",
            "Measure campaign performance",
            "Use customer insights to improve retention",
          ],
          closing: [
            "Use real customer activity to make better loyalty and retention decisions.",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Digital Rewards Without Physical Cards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Forget paper-based reward cards and manual tracking.",
            "Fydo's **customer rewards platform** helps businesses manage rewards digitally, making the experience easier for both businesses and customers.",
            "Customers can participate in your rewards program without carrying a physical loyalty card.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "You don't need an expensive POS system to start your **customer rewards platform**.",
            "Fydo is designed to work with businesses **with or without a POS**, making digital customer rewards accessible to local shops, independent retailers, and growing businesses.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Digital Customer Rewards for Every Type of Retail Business",
      subHeading:
        "Fydo helps businesses across different industries reward customers, increase repeat purchases, and build stronger customer relationships.",
      useCases: [
        {
          title: "Grocery & Kirana/ General Stores",
          detail:
            "Reward regular purchases and encourage customers to keep shopping with you through digital rewards.",
        },
        {
          title: "Clothing Stores",
          detail:
            "Reward customers and bring them back for new collections, seasonal shopping, and special offers.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Reward customers after services and encourage repeat appointments.",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Use digital rewards to increase repeat visits and orders.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger relationships with regular customers through convenient digital rewards.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Create personalized offers and rewards to encourage repeat purchases and increase engagement.",
        },
      ],
      endingLine:
        "Whether you're running a neighborhood store or a growing retail business, digital rewards can help you build a stronger base of repeat customers.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Rewards",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give customers a convenient and consistent reason to return with rewarding experiences after every purchase.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Turn every purchase into an opportunity for another sale by giving customers rewards they can use on future purchases.",
        },
        {
          title: "Build Long-Term Customer Loyalty",
          text: "Create an ongoing connection with customers instead of relying only on one-time discounts.",
        },
        {
          title: "Move Beyond Traditional Loyalty Cards",
          text: "Replace physical reward cards and manual tracking with a simple digital loyalty solution that is easier to manage and scale.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Keep customers engaged with personalized rewards, offers, campaigns, and **Free WhatsApp Marketing**.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Encourage existing customers to purchase more frequently and stay connected with your business.",
        },
        {
          title: "Reward High-Value Customers",
          text: "Recognize your most valuable customers with targeted rewards and personalized offers.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Use **Free WhatsApp Marketing** to communicate rewards, offers, promotions, and campaigns directly with customers.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage your rewards program from your mobile without complicated loyalty technology.",
        },
      ],
      endingLine:
        "Fydo helps you move from traditional reward methods to simple, digital customer loyalty.",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Thousands of businesses use Fydo to reward customers, increase engagement, and encourage repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo makes it easy to reward customers and keep them connected with our business.",
          description:
            "The digital rewards experience gives customers an extra reason to come back.",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Rewarding Customers in 3 Simple Steps",
      description:
        "Use **Free WhatsApp Marketing** to keep customers informed about their rewards, offers, and campaigns.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Use Fydo to configure your cashback, points, discounts, offers, and customer engagement rules.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn",
          description:
            "Customers make eligible purchases and receive applicable rewards based on your loyalty settings.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Customers Return & Redeem",
          description:
            "Customers use their rewards on future purchases, giving them another reason to return to your business.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a customer rewards platform?",
          answer:
            "A customer rewards platform is a digital system that allows businesses to reward customers for purchases and engagement through cashback, points, discounts, and other rewards.",
        },
        {
          question: "How does Fydo's rewards system work?",
          answer:
            "Businesses can create digital rewards through Fydo. Customers make eligible purchases and receive applicable rewards, encouraging them to return and shop again.",
        },
        {
          question: "Is this suitable for small businesses?",
          answer:
            "Yes. Fydo is designed for small and medium-sized businesses, including local retail shops, and can be managed easily from a mobile device.",
        },
        {
          question: "What types of rewards can I offer through Fydo?",
          answer:
            "Businesses can create different rewards, including cashback, loyalty points, discounts, special offers, and purchase-based rewards.",
        },
        {
          question: "Do I need a POS system to use Fydo?",
          answer:
            "No. Fydo's loyalty and rewards system works with or without a POS system, making it suitable for a wide range of retail businesses.",
        },
        {
          question: "Can I customize my rewards program?",
          answer:
            "Yes. Businesses can customize reward rules, offers, campaigns, and customer segments based on their business goals.",
        },
        {
          question: "Can I reward repeat customers?",
          answer:
            "Yes. Fydo allows businesses to create rewards and offers specifically for repeat and high-value customers.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate rewards, promotions, offers, and campaigns directly with customers.",
        },
        {
          question: "Can digital rewards increase repeat customers?",
          answer:
            "Yes. Digital rewards can give customers a convenient reason to return, earn benefits, and make future purchases, helping businesses improve customer retention.",
        },
        {
          question: "Is Fydo only for retail stores?",
          answer:
            "No. Fydo can be used by different business categories, including grocery stores, clothing stores, salons, restaurants, pharmacies, lifestyle stores, and other local businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Every Purchase into a Reason to Return",
      subtitle:
        "Don't let your customers be one-time buyers. Start rewarding customers digitally, engage them with **Free WhatsApp Marketing**, and build stronger customer relationships with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "multiple reward types",
      "cashback and points together",
      "loyalty platform for retail",
      "customer rewards system",
    ],
  },

  "digital-loyalty-program": {
    slug: "digital-loyalty-program",
    cluster: "core-loyalty",
    showMerchantsSection: false,
    seo: {
      title: "Digital Loyalty Program for Retail Businesses in India",
      description:
        "Build customer loyalty with Fydo's digital loyalty program. Reward customers, increase repeat purchases, manage digital rewards, and engage customers with Free WhatsApp Marketing.",
      keywords: [
        "digital loyalty program",
        "digital loyalty program for retail",
        "digital loyalty software",
        "digital customer loyalty program",
        "customer loyalty program",
        "loyalty program software",
        "digital loyalty program India",
        "digital loyalty program for small businesses",
        "digital loyalty program for retail businesses",
        "digital rewards program",
        "digital customer rewards",
        "digital loyalty software India",
        "retail loyalty program",
      ],
    },
    heroSection: {
      badgeText: "Digital Loyalty Program",
      title: "Digital Loyalty Program to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading:
        "Build stronger customer relationships with a digital loyalty program.",
      microSubHeading: "",
      description: [
        "Fydo is a simple **digital loyalty program** that helps retail businesses reward customers, increase repeat purchases, and build long-term customer loyalty.",
        "Move beyond traditional loyalty cards and manual reward systems. With Fydo's **digital loyalty program**, you can reward customers, create personalized offers, track customer engagement, and connect with customers through **Free WhatsApp Marketing**.",
        "No complicated setup. No expensive loyalty systems. **Works with or without a POS.**",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need a Digital Loyalty Program?",
      subHeading:
        "Customers have more choices than ever. A customer who shops with you today can easily choose another store tomorrow.",
      description: [
        "Without a **digital loyalty program**, businesses often rely on discounts, physical loyalty cards, or manual customer records to encourage repeat purchases.",
        "Many traditional loyalty methods are difficult to manage, difficult to track, and provide limited customer engagement.",
      ],
      points: [
        {
          title: "No Digital Loyalty Program",
          text: "Customers don't have a convenient digital way to earn and receive rewards.",
        },
        {
          title: "Low Customer Retention",
          text: "Without a **digital loyalty program**, businesses miss opportunities to turn one-time buyers into repeat customers.",
        },
        {
          title: "Manual Loyalty Management",
          text: "Physical cards and manual records can be difficult to maintain as your customer base grows.",
        },
        {
          title: "Limited Customer Engagement",
          text: "Traditional loyalty methods often end after the purchase, making it difficult to stay connected with customers.",
        },
        {
          title: "Dependence on Discounts",
          text: "Businesses may rely heavily on discounts instead of building a structured **digital loyalty program**.",
        },
        {
          title: "Missed Customer Insights",
          text: "Without digital customer data, businesses may struggle to understand purchase behavior and identify loyal customers.",
        },
      ],
      endingLine:
        "**The result?** Businesses continue spending on customer acquisition instead of using a **digital loyalty program** to retain existing customers, improve engagement, and increase customer lifetime value.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Purchase into a Digital Loyalty Experience",
      description: [
        "Fydo's **digital loyalty program** helps retail businesses move from traditional loyalty methods to a simple, digital way of rewarding and engaging customers.",
      ],
      listLabel: "With Fydo's **digital loyalty program**, you can:",
      steps: [
        "Reward customers digitally after eligible purchases",
        "Create customized loyalty offers",
        "Offer cashback, points, and rewards",
        "Reward repeat and high-value customers",
        "Encourage customers to return and shop again",
        "Engage customers through **Free WhatsApp Marketing**",
        "Track customer activity and engagement",
        "Manage your loyalty program easily from your mobile",
      ],
      bottomLine:
        "Instead of relying on physical cards or one-time discounts, use a **digital loyalty program** to create an ongoing connection with every customer.",
    },
    featureSection: {
      heading:
        "Everything You Need to Build a Powerful Digital Loyalty Program",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers Digitally",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make every purchase more rewarding with a simple **digital loyalty program**.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Reward customers after eligible purchases",
            "Offer cashback and loyalty rewards",
            "Create customized reward rules",
            "Create special loyalty campaigns",
            "Reward customers during festivals and special occasions",
            "Encourage repeat purchases",
            "Give customers a digital reason to return to your store",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Don't let your customer relationship end after checkout.",
            "Fydo's **digital loyalty program** works with **Free WhatsApp Marketing** to help businesses stay connected with customers.",
            "You can:",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share digital loyalty offers",
            "Promote cashback and rewards",
            "Announce special deals",
            "Promote new products",
            "Remind customers about available rewards",
            "Re-engage inactive customers",
            "Send festival and seasonal campaigns",
            "Encourage customers to return",
          ],
          closing: [
            "Keep your customers connected with your business beyond every transaction.",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Personalized Loyalty Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Every customer has different purchasing habits. A **digital loyalty program** makes it easier to create targeted offers based on customer behavior.",
            "Create personalized offers for:",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "New customers",
            "Repeat customers",
            "High-value customers",
            "Inactive customers",
            "Frequent shoppers",
            "Festival shoppers",
            "Customers who haven't visited recently",
          ],
          closing: [
            "Give customers relevant rewards and offers that encourage them to return.",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Track Customer Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "A **digital loyalty program** gives businesses greater visibility into customer activity.",
            "With Fydo, you can:",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Track repeat purchases",
            "Identify loyal customers",
            "Monitor customer activity",
            "Understand purchase frequency",
            "Track reward engagement",
            "Measure campaign performance",
            "Use customer insights to improve retention",
          ],
          closing: [
            "Make better customer retention decisions using real customer activity.",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Digital Customer Loyalty Without Physical Cards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Forget paper-based loyalty cards and manual tracking.",
            "Fydo's **digital loyalty program** helps businesses manage customer rewards digitally, making loyalty easier for both businesses and customers.",
            "Customers can participate in your loyalty program without carrying a physical loyalty card.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "You don't need an expensive POS system to start your **digital loyalty program**.",
            "Fydo is designed to work with businesses **with or without a POS**, making digital customer loyalty accessible to local shops, independent retailers, and growing businesses.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Digital Loyalty Program for Every Type of Retail Business",
      subHeading:
        "Fydo's **digital loyalty program** can help businesses across different industries increase repeat purchases and build stronger customer relationships.",
      useCases: [
        {
          title: "Grocery & Kirana/ General Stores",
          detail:
            "Use a **digital loyalty program** to reward regular purchases and encourage customers to keep shopping with you.",
        },
        {
          title: "Clothing Stores",
          detail:
            "Reward customers digitally and bring them back for new collections, seasonal shopping, and special offers.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Reward customers after services and encourage repeat appointments.",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Use digital rewards to increase repeat visits and orders.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger relationships with regular customers through digital loyalty rewards.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Create personalized digital loyalty offers to encourage customers to return.",
        },
      ],
      endingLine:
        "Whether you're running a neighborhood store or a growing retail business, a **digital loyalty program** can help you build a stronger base of repeat customers.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo's Digital Loyalty Program",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Use a **digital loyalty program** to give customers a convenient and consistent reason to return.",
        },
        {
          title: "Boost Repeat Sales",
          text: "Turn every purchase into an opportunity for another sale by rewarding customers digitally.",
        },
        {
          title: "Build Long-Term Customer Loyalty",
          text: "Create an ongoing connection with customers instead of relying only on one-time discounts.",
        },
        {
          title: "Move Beyond Traditional Loyalty Cards",
          text: "Replace physical loyalty cards and manual tracking with a simple **digital loyalty program** that is easier to manage and scale.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Keep customers engaged with personalized rewards, offers, campaigns, and **Free WhatsApp Marketing**.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Encourage existing customers to purchase more frequently and stay connected with your business.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Use **Free WhatsApp Marketing** to communicate digital rewards, offers, promotions, and campaigns directly with customers.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage your **digital loyalty program** from your mobile without complicated loyalty technology.",
        },
      ],
      endingLine:
        "Fydo helps you move from traditional loyalty methods to simple, digital customer loyalty.",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Thousands of businesses use Fydo's **digital loyalty program** to reward customers, increase engagement, and encourage repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo makes it easy to reward customers and keep them connected with our store.",
          description:
            "The digital loyalty experience gives customers an extra reason to come back.",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Digital Loyalty Program in 3 Simple Steps",
      description:
        "Use **Free WhatsApp Marketing** to keep customers informed about their rewards, offers, and campaigns.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Digital Loyalty Program",
          description:
            "Use Fydo to configure your loyalty rewards, cashback, points, offers, and customer engagement rules.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn Rewards",
          description:
            "Customers make eligible purchases and receive their applicable digital rewards based on your loyalty program.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Customers Return & Redeem Rewards",
          description:
            "Customers use their rewards on future purchases, giving them another reason to return to your business.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a digital loyalty program?",
          answer:
            "A digital loyalty program is a technology-based customer loyalty system that allows businesses to reward customers digitally for purchases and engagement instead of relying on physical loyalty cards or manual records.",
        },
        {
          question: "How does Fydo's digital loyalty program work?",
          answer:
            "Businesses can create digital loyalty offers through Fydo. Customers make eligible purchases and receive applicable rewards such as cashback or loyalty points, encouraging them to return and shop again.",
        },
        {
          question:
            "Is a digital loyalty program suitable for small businesses?",
          answer:
            "Yes. Fydo's digital loyalty program is designed for small and medium-sized businesses, including local retail shops, and can be managed easily from a mobile device.",
        },
        {
          question:
            "What types of rewards can I offer through a digital loyalty program?",
          answer:
            "Businesses can use Fydo to create different loyalty offers, including cashback, loyalty points, discounts, and other customer rewards based on their business requirements.",
        },
        {
          question: "Do I need a POS system to use Fydo?",
          answer:
            "No. Fydo's digital loyalty program works with or without a POS system, making it suitable for a wide range of retail businesses.",
        },
        {
          question: "Can I customize my digital loyalty program?",
          answer:
            "Yes. Businesses can customize their loyalty rules, rewards, offers, and campaigns based on their business goals and customer segments.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate loyalty rewards, promotions, offers, and campaigns directly with customers.",
        },
        {
          question: "Can a digital loyalty program increase repeat customers?",
          answer:
            "Yes. A digital loyalty program can give customers a convenient reason to return, earn rewards, and make future purchases, helping businesses improve customer retention.",
        },
        {
          question: "Is Fydo's digital loyalty program only for retail stores?",
          answer:
            "No. Fydo can be used by different business categories, including grocery stores, clothing stores, salons, restaurants, pharmacies, lifestyle stores, and other local businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Every Purchase into a Digital Loyalty Experience",
      subtitle:
        "Don't let your customers be one-time buyers. Start rewarding customers digitally, engage them with **Free WhatsApp Marketing**, and build a loyal customer base with Fydo's **digital loyalty program**.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "digital loyalty program",
      "phone number loyalty",
      "paperless loyalty card",
      "replace punch card",
    ],
  },

  "digital-stamp-card": {
    slug: "digital-stamp-card",
    cluster: "core-loyalty",
    showMerchantsSection: false,
    seo: {
      title: "Digital Stamp Card for Retail Businesses in India",
      description:
        "Increase repeat customers with Fydo's digital stamp card. Reward purchases, replace paper loyalty cards, engage customers with Free WhatsApp Marketing, and build customer loyalty.",
      keywords: [
        "digital stamp card",
        "digital stamp card software",
        "digital loyalty card",
        "digital loyalty program",
        "digital rewards program",
        "stamp card software",
        "digital stamp card India",
        "digital stamp card for retail",
        "digital stamp card for small businesses",
        "digital stamp card software India",
        "digital loyalty stamp card",
        "online stamp card",
        "digital reward card",
        "electronic stamp card",
        "loyalty stamp card",
      ],
    },
    heroSection: {
      badgeText: "Digital Stamp Card",
      title: "Digital Stamp Card to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading:
        "Turn every purchase into a digital stamp and every stamp into a reason to return.",
      microSubHeading: "",
      description: [
        "Fydo is a simple **digital stamp card** solution that helps retail businesses reward customers, encourage repeat purchases, and build long-term customer loyalty.",
        "Replace traditional paper stamp cards with a convenient **digital stamp card** that customers can use without carrying a physical card. Reward customers for purchases, engage them through **Free WhatsApp Marketing**, and encourage them to keep coming back.",
        "No paper cards. No manual tracking. No complicated setup. **Works with or without a POS.**",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Retail Business Need a Digital Stamp Card?",
      subHeading:
        "Traditional stamp cards can be easy to start but difficult to manage as your customer base grows. Customers can lose physical cards, forget to carry them, or stop using them.",
      description: [
        "Without a **digital stamp card**, businesses may miss opportunities to turn regular purchases into repeat visits.",
        "Many businesses still depend on paper loyalty cards and discounts, but these methods can make customer loyalty difficult to track and manage.",
      ],
      points: [
        {
          title: "Lost or Forgotten Stamp Cards",
          text: "Customers may forget or lose physical cards, making it difficult to maintain consistent loyalty.",
        },
        {
          title: "No Digital Stamp Card",
          text: "Customers don't have a convenient way to collect and track stamps digitally.",
        },
        {
          title: "Low Repeat Visits",
          text: "Without a structured **digital stamp card** program, businesses may struggle to give customers a reason to return.",
        },
        {
          title: "Manual Tracking",
          text: "Paper-based stamp cards are difficult to manage, monitor, and scale.",
        },
        {
          title: "Limited Customer Engagement",
          text: "Traditional stamp cards usually don't provide an easy way to communicate with customers after their purchase.",
        },
        {
          title: "Dependence on Discounts",
          text: "Businesses may rely on discounts instead of using a **digital stamp card** to encourage repeat purchases.",
        },
      ],
      endingLine:
        "**The result?** Businesses miss opportunities to turn regular purchases into repeat visits and long-term customer relationships.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Purchase into a Digital Stamp",
      description: [
        "Fydo's **digital stamp card** helps businesses replace traditional paper-based loyalty cards with a simple digital reward experience.",
      ],
      listLabel: "With Fydo's **digital stamp card**, you can:",
      steps: [
        "Give customers digital stamps for eligible purchases",
        "Set customized stamp and reward rules",
        "Create special stamp campaigns",
        "Reward customers after completing a set number of stamps",
        "Encourage customers to make repeat purchases",
        "Engage customers through **Free WhatsApp Marketing**",
        "Track customer activity and engagement",
        "Manage your **digital stamp card** easily from your mobile",
      ],
      bottomLine:
        "Instead of handing customers a paper card, give them a simple digital way to collect rewards and a clear reason to **come back to your business**.",
    },
    featureSection: {
      heading: "Everything You Need to Build a Powerful Digital Stamp Card",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Reward Customers with Digital Stamps",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make repeat purchases more rewarding with a simple **digital stamp card**.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Give digital stamps for eligible purchases",
            "Set the number of stamps required for a reward",
            "Create customized stamp rules",
            "Offer rewards after completing a stamp cycle",
            "Create special stamp campaigns",
            "Reward customers during festivals and special occasions",
            "Encourage customers to complete their next stamp",
            "Give customers a reason to return",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Don't let your customer relationship end after checkout.",
            "Fydo's **digital stamp card** works with **Free WhatsApp Marketing** to help businesses stay connected with customers.",
            "You can:",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share digital stamp card offers",
            "Remind customers about their stamp progress",
            "Announce available rewards",
            "Promote new products",
            "Announce special deals",
            "Re-engage inactive customers",
            "Send festival and seasonal campaigns",
            "Encourage customers to complete their stamp cycle",
          ],
          closing: [
            "Keep customers engaged and remind them that another reward is just a few purchases away.",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Create Customized Stamp Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Different businesses have different loyalty goals. With a **digital stamp card**, you can create reward rules that work for your business.",
            "Create stamp-based rewards for:",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "Every eligible purchase",
            "Specific products",
            "Specific purchase values",
            "Repeat customers",
            "New customers",
            "Special occasions",
            "Festival campaigns",
            "Promotional periods",
          ],
          closing: [
            "Give customers a simple and motivating path toward their next reward.",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Track Customer Loyalty",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "A **digital stamp card** makes it easier to understand customer participation and engagement.",
            "With Fydo, you can:",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Track repeat purchases",
            "Monitor stamp activity",
            "Identify loyal customers",
            "Understand purchase frequency",
            "Monitor customer engagement",
            "Measure campaign performance",
            "Use customer insights to improve retention",
          ],
          closing: [
            "Understand how customers interact with your loyalty program and make better retention decisions.",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Replace Paper Stamp Cards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Say goodbye to paper cards that can be lost, damaged, or forgotten.",
            "Fydo's **digital stamp card** gives businesses a convenient digital alternative that makes loyalty easier to manage and customers easier to engage.",
            "Customers don't need to carry a physical stamp card every time they visit your business.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "You don't need an expensive POS system to start using a **digital stamp card**.",
            "Fydo works with businesses **with or without a POS**, making digital stamp-based loyalty accessible to local shops, independent retailers, salons, restaurants, cafes, and growing businesses.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Digital Rewards for Every Type of Retail Business",
      subHeading:
        "Fydo helps businesses across different categories encourage repeat purchases and visits through simple, stamp-based loyalty rewards.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward regular customers for their purchases and encourage them to keep shopping with you.",
        },
        {
          title: "Clothing Stores",
          detail:
            "Create reward journeys that encourage customers to return for new collections, seasonal shopping, and special offers.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Reward customers after services and encourage them to book their next appointment.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Encourage repeat visits by rewarding customers throughout their loyalty journey.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger relationships with regular customers through convenient digital rewards.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Create customized rewards that encourage customers to return and engage with your business.",
        },
      ],
      endingLine:
        "Whether you're running a neighborhood store or a growing business, digital rewards can help turn regular purchases into repeat visits and stronger customer relationships.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Digital Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give customers a simple and visible reason to return after every purchase by creating a rewarding loyalty journey.",
        },
        {
          title: "Increase Repeat Visits",
          text: "Create reward milestones that motivate customers to make another purchase or visit and work toward their next reward.",
        },
        {
          title: "Replace Traditional Loyalty Cards",
          text: "Move away from paper-based cards and manual tracking with a convenient digital loyalty solution.",
        },
        {
          title: "Reduce Manual Work",
          text: "Stop maintaining physical cards and manual stamp records. Manage your loyalty program digitally from one place.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Keep customers connected with rewards, offers, campaigns, and **Free WhatsApp Marketing**.",
        },
        {
          title: "Build Customer Loyalty",
          text: "Give customers a reason to continue choosing your business and make your rewards part of their regular shopping experience.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Use **Free WhatsApp Marketing** to share promotions, reward reminders, special offers, and campaigns with customers.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage your loyalty program from your mobile without complicated technology or expensive infrastructure.",
        },
      ],
      endingLine:
        "Fydo helps you turn everyday purchases into repeat visits and long-term customer relationships.",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Thousands of businesses use Fydo's loyalty and rewards solutions to engage customers, encourage repeat purchases, and build stronger relationships.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo makes it easy to reward customers and keep them connected with our store.",
          description:
            "The digital rewards experience gives customers a simple reason to come back and complete their rewards.",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Rewarding Customers in 3 Simple Steps",
      description:
        "Use **Free WhatsApp Marketing** to remind customers about their progress, rewards, promotions, and upcoming offers.",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Loyalty Program",
          description:
            "Choose how customers earn rewards, define your stamp rules, and decide what they receive after completing the required milestones.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Customers Shop & Earn",
          description:
            "Customers make eligible purchases and receive digital stamps or rewards based on the rules you have created.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Customers Return & Redeem",
          description:
            "Customers come back to your business to continue earning rewards and redeem them once they reach the required milestone.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a digital stamp card?",
          answer:
            "A digital stamp card is an electronic version of a traditional loyalty stamp card. Customers collect digital stamps after eligible purchases and receive a reward after completing a defined number of stamps.",
        },
        {
          question: "How does Fydo's rewards system work?",
          answer:
            "Businesses can create stamp-based loyalty rules through Fydo. Customers make eligible purchases and receive digital stamps. Once they reach the required milestone, they can receive the applicable reward.",
        },
        {
          question: "Is this suitable for small businesses?",
          answer:
            "Yes. Fydo is designed for small and medium-sized businesses, including local retail shops, salons, restaurants, cafes, and other businesses.",
        },
        {
          question: "Can I customize my rewards program?",
          answer:
            "Yes. Businesses can customize stamp rules, required milestones, rewards, campaigns, and eligibility according to their business requirements.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for a wide range of businesses.",
        },
        {
          question:
            "What is the difference between a digital and physical stamp card?",
          answer:
            "A physical stamp card requires customers to carry a paper or plastic card. A digital version removes the need for a physical card and makes it easier to manage participation and rewards.",
        },
        {
          question: "Can stamp-based loyalty increase repeat customers?",
          answer:
            "Yes. A stamp-based rewards journey can encourage customers to make additional purchases or visits as they work toward completing their reward milestone.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate promotions, reward reminders, offers, and campaigns with their customers.",
        },
        {
          question: "Can I use this loyalty program for services?",
          answer:
            "Yes. Fydo's loyalty solution can be used by service businesses such as salons and beauty businesses to encourage customers to return for additional services.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Every Purchase into a Reason to Return",
      subtitle:
        "Don't let your customers be one-time buyers. Replace traditional paper cards with a **digital loyalty experience**, reward customers for coming back, engage them with **Free WhatsApp Marketing**, and build a loyal customer base with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "stamp card",
      "punch card",
      "buy 9 get 1 free",
      "loyalty card app",
    ],
  },

  "personalized-loyalty-program": {
    slug: "personalized-loyalty-program",
    cluster: "core-loyalty",
    showMerchantsSection: false,
    seo: {
      title: "Personalized Loyalty Program for Retail Businesses in India",
      description:
        "Create personalized rewards and offers with Fydo's personalized loyalty program. Improve customer retention, increase repeat sales, and engage customers with Free WhatsApp Marketing.",
      keywords: [
        "personalized loyalty program",
        "personalized loyalty program software",
        "personalized customer loyalty program",
        "personalized loyalty software",
        "personalized rewards program",
        "personalized customer rewards",
        "customer loyalty software",
        "personalized loyalty platform",
        "digital personalized loyalty program",
        "customer engagement software",
        "loyalty program software",
        "personalized loyalty program India",
        "personalized loyalty program for small businesses",
        "personalized loyalty program for retail",
        "personalized customer rewards India",
        "personalized digital rewards",
        "customer segmentation loyalty",
        "targeted loyalty offers",
        "customer retention software",
        "digital loyalty program",
        "Free WhatsApp Marketing",
      ],
    },
    heroSection: {
      badgeText: "Personalised Loyalty Program",
      title:
        "Personalized Loyalty Program to Increase Repeat Customers & Sales",
      gradientSubHeading: "",
      baseSubHeading:
        "Give every customer a reason to come back with rewards tailored to their shopping behavior.",
      microSubHeading: "",
      description: [
        "Fydo is a simple **personalized loyalty program** that helps retail businesses create relevant rewards, offers, and campaigns based on customer preferences and purchasing behavior.",
        "Instead of giving the same offer to every customer, businesses can use Fydo to create targeted rewards, engage different customer segments, encourage repeat purchases, and build stronger customer relationships through **Free WhatsApp Marketing**.",
        "No complicated setup. No expensive loyalty infrastructure. **Works with or without a POS.**",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need a Personalized Loyalty Program?",
      subHeading:
        "Customers have different preferences, purchasing habits, and levels of engagement. Giving every customer the same reward or discount may not create the experience needed to keep them coming back.",
      description: [
        "Without a **personalized loyalty program**, businesses may struggle to understand customer behavior and provide offers that are relevant to individual shoppers.",
        "Traditional loyalty methods often treat every customer the same, limiting opportunities to improve engagement and retention.",
      ],
      points: [
        {
          title: "Generic Rewards",
          text: "Giving the same reward to every customer may not be relevant to their individual shopping preferences.",
        },
        {
          title: "Low Customer Engagement",
          text: "Customers may lose interest when loyalty offers do not match their needs or interests.",
        },
        {
          title: "Missed Customer Insights",
          text: "Without customer data and segmentation, businesses may struggle to understand purchasing behavior.",
        },
        {
          title: "Poor Retention",
          text: "Generic promotions may not provide a strong enough reason for customers to return.",
        },
        {
          title: "Ineffective Campaigns",
          text: "Sending the same campaign to everyone can reduce the relevance and effectiveness of your offers.",
        },
        {
          title: "Limited Customer Relationships",
          text: "Businesses may miss opportunities to build stronger relationships through meaningful, targeted interactions.",
        },
      ],
      endingLine:
        "**The result?** Businesses may spend more on customer acquisition while missing opportunities to increase repeat purchases from customers they already have.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Customer Data into Personalized Loyalty Experiences",
      description: [
        "Fydo's **personalized loyalty program** helps businesses move beyond one-size-fits-all rewards and create more relevant customer experiences.",
      ],
      listLabel: "With Fydo's **personalized loyalty program**, you can:",
      steps: [
        "Segment customers based on their activity",
        "Create targeted rewards and offers",
        "Reward repeat customers",
        "Create customer-specific campaigns",
        "Engage inactive customers with relevant offers",
        "Recognize high-value customers",
        "Encourage customers to purchase more frequently",
        "Track customer activity and engagement",
        "Engage customers through **Free WhatsApp Marketing**",
        "Manage your loyalty campaigns from your mobile",
      ],
      bottomLine:
        "Instead of treating every customer the same, use customer insights to deliver rewards and offers that encourage each customer segment to stay engaged with your business.",
    },
    featureSection: {
      heading:
        "Everything You Need to Build a Powerful Personalized Loyalty Program",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Create Personalized Customer Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Make loyalty more relevant by offering rewards based on customer behavior and engagement.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [
            "Create customer-specific rewards",
            "Offer cashback and loyalty points",
            "Create personalized discounts",
            "Reward repeat purchases",
            "Create targeted promotional offers",
            "Reward high-value customers",
            "Create special rewards for loyal customers",
            "Encourage customers to return",
          ],
          closing: [
            "Give customers rewards that are more relevant to their relationship with your business.",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Engage Customers with Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Don't let your customer relationship end after checkout.",
            "Fydo's **personalized loyalty program** works with **Free WhatsApp Marketing** to help businesses communicate relevant offers and rewards to customers.",
            "You can:",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [
            "Share personalized offers",
            "Promote customer-specific rewards",
            "Send reward reminders",
            "Announce new products",
            "Promote seasonal campaigns",
            "Re-engage inactive customers",
            "Send festival offers",
            "Encourage repeat purchases",
          ],
          closing: [
            "Keep customers connected with messages and offers that are relevant to their engagement with your business.",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Segment Your Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand that different customers require different engagement strategies.",
            "Create customer segments such as:",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [
            "New customers",
            "Repeat customers",
            "Frequent shoppers",
            "High-value customers",
            "Inactive customers",
            "Festival shoppers",
            "Recent customers",
            "Customers who haven't purchased recently",
          ],
          closing: [
            "Use customer segments to create more relevant loyalty campaigns.",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Create Targeted Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Run different campaigns for different customer groups and business objectives.",
            "Create targeted campaigns for:",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [
            "Customer retention",
            "Repeat purchases",
            "Customer reactivation",
            "New product launches",
            "Seasonal promotions",
            "Festival campaigns",
            "Special occasions",
            "High-value customers",
          ],
          closing: [
            "Reach the right customer groups with offers designed around their engagement and purchasing behavior.",
          ],
        },
        {
          id: 5,
          titleStart: {
            text: "Track Customer Behavior",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Use customer activity to understand how shoppers interact with your business.",
            "With Fydo, you can:",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [
            "Monitor purchase activity",
            "Track repeat purchases",
            "Identify loyal customers",
            "Understand purchase frequency",
            "Monitor customer engagement",
            "Measure campaign performance",
            "Use insights to improve retention",
          ],
          closing: [
            "Turn customer activity into actionable insights for better loyalty and retention decisions.",
          ],
        },
        {
          id: 6,
          titleStart: {
            text: "Move Beyond One-Size-Fits-All Loyalty",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Not every customer should receive the same offer.",
            "Fydo helps businesses create a more flexible loyalty experience where rewards and campaigns can be designed around different customer groups and business goals.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "You don't need an expensive POS system to start using a **personalized loyalty program**.",
            "Fydo works with businesses **with or without a POS**, making personalized customer engagement accessible to local shops, independent retailers, salons, restaurants, cafes, and growing businesses.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading:
        "Personalized Customer Rewards for Every Type of Retail Business",
      subHeading:
        "Fydo helps businesses across different categories use customer insights to create more relevant rewards, offers, and engagement strategies.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward frequent shoppers, target inactive customers, and create offers based on regular purchasing patterns.",
        },
        {
          title: "Clothing Stores",
          detail:
            "Promote new collections and seasonal offers to customers based on their shopping activity and preferences.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Encourage repeat appointments with relevant offers based on previous services and customer engagement.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Create targeted promotions to encourage customers to return and increase visit frequency.",
        },
        {
          title: "Pharmacies",
          detail:
            "Engage regular customers with relevant offers and loyalty incentives while strengthening ongoing relationships.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Use customer insights to create targeted promotions and rewards for different shopper groups.",
        },
      ],
      endingLine:
        "Whether you're running a neighborhood store or a growing business, relevant rewards can help you create stronger customer relationships and improve retention.",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading:
        "Why Businesses Choose Fydo for Personalized Customer Engagement",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Customers",
          text: "Give customers relevant reasons to return based on their shopping behavior and relationship with your business.",
        },
        {
          title: "Improve Customer Retention",
          text: "Keep customers engaged with offers and rewards that are more relevant than generic promotions.",
        },
        {
          title: "Increase Customer Engagement",
          text: "Create meaningful interactions through targeted rewards, campaigns, and **Free WhatsApp Marketing**.",
        },
        {
          title: "Deliver More Relevant Offers",
          text: "Move beyond blanket discounts by creating promotions for specific customer groups and shopping behaviors.",
        },
        {
          title: "Reward High-Value Customers",
          text: "Recognize your most valuable customers with targeted incentives that encourage them to continue shopping with you.",
        },
        {
          title: "Re-Engage Inactive Customers",
          text: "Reach customers who haven't purchased recently with relevant offers and campaigns designed to bring them back.",
        },
        {
          title: "Increase Customer Lifetime Value",
          text: "Encourage customers to purchase more frequently and maintain a stronger relationship with your business over time.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Use **Free WhatsApp Marketing** to communicate personalized offers, rewards, promotions, and campaigns directly with customers.",
        },
        {
          title: "Simple & Easy to Use",
          text: "Manage customer segments, rewards, and campaigns from your mobile without complicated loyalty technology.",
        },
      ],
      endingLine:
        "Fydo helps businesses move from generic promotions to more meaningful customer relationships.",
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Thousands of businesses use Fydo's loyalty and customer engagement solutions to reward customers, improve engagement, and encourage repeat purchases.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo makes it easier to give customers offers that are relevant to them.",
          description:
            "The personalized rewards experience helps us keep our customers engaged and gives them more reasons to return.",
          author: "",
          rating: 5,
          authorStoreName: "",
          authorStoreType: "",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Create Personalized Customer Experiences in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Understand Your Customers",
          description:
            "Use customer activity and purchase behavior to identify different customer groups and engagement patterns.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Relevant Rewards & Offers",
          description:
            "Set up rewards, discounts, campaigns, and promotions based on customer segments and your business objectives.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Retain Customers",
          description:
            "Reach customers with relevant offers and **Free WhatsApp Marketing**, encourage repeat purchases, and build stronger long-term relationships.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a personalized loyalty program?",
          answer:
            "A personalized loyalty program is a customer loyalty system that uses customer information, purchasing behavior, and engagement patterns to provide more relevant rewards, offers, and experiences.",
        },
        {
          question: "How does Fydo personalize customer rewards?",
          answer:
            "Fydo helps businesses segment customers and create targeted rewards, offers, and campaigns based on customer activity and purchasing behavior.",
        },
        {
          question: "Is this suitable for small businesses?",
          answer:
            "Yes. Fydo is designed for small and medium-sized businesses, including local retail stores, salons, restaurants, cafes, pharmacies, and other customer-facing businesses.",
        },
        {
          question: "What types of customer segments can I create?",
          answer:
            "Businesses can create segments such as new customers, repeat customers, frequent shoppers, high-value customers, inactive customers, and other groups based on their business requirements.",
        },
        {
          question: "Can I create customer-specific offers?",
          answer:
            "Yes. Businesses can create targeted offers and campaigns for different customer groups based on their engagement and purchasing behavior.",
        },
        {
          question: "Do I need a POS system?",
          answer:
            "No. Fydo works with or without a POS system, making it suitable for businesses with different technology setups.",
        },
        {
          question: "Can I reward high-value and repeat customers?",
          answer:
            "Yes. You can create targeted rewards and offers to recognize loyal, frequent, or high-value customers and encourage them to continue shopping.",
        },
        {
          question: "Does Fydo provide Free WhatsApp Marketing?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate personalized offers, rewards, promotions, and campaigns directly with customers.",
        },
        {
          question: "Can personalized rewards improve customer retention?",
          answer:
            "Relevant rewards can give customers a stronger reason to remain engaged with a business and return for future purchases.",
        },
        {
          question: "Is Fydo only for retail stores?",
          answer:
            "No. Fydo can be used by different business categories, including grocery stores, clothing stores, salons, restaurants, pharmacies, lifestyle stores, and other local businesses.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Give Every Customer a Reason to Come Back Again",
      subtitle:
        "Don't treat every customer the same. Create relevant rewards, engage customers with **Free WhatsApp Marketing**, encourage repeat purchases, and build stronger customer relationships with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "customer segmentation",
      "win-back offer",
      "targeted offers",
      "lapsed customers",
    ],
  },

  "cashback-and-rewards-system-for-shops": {
    slug: "cashback-and-rewards-system-for-shops",
    cluster: "core-loyalty",
    // Title/description are deliberately distinct from heroSection.title (the
    // H1) - a concise, front-loaded SERP title and a benefit-led meta
    // description read better in search results than a literal H1 match.
    seo: {
      title: "Cashback and Rewards System for Retail Shops",
      description:
        "Set up rule-based cashback tiers, track redemptions, and reward repeat visits without cutting margins on every sale. Free cashback app for shops.",
    },
    heroSection: {
      badgeText: "Cashback and Rewards",
      title: "Reward Customers With Cashback Instead of Flat Discounts",
      gradientSubHeading: "Customers buy once",
      baseSubHeading: "but don’t come back?",
      microSubHeading:
        "Fydo lets shop owners set cashback rules based on visit count or spend amount, so customers earn wallet balance on every purchase instead of a one-time discount. Customers return to redeem their cashback, and you track exactly how much you've given out and how much came back in sales.",
      supportingText:
        "Simple app. No complicated setup - works with your existing POS system or even if you don’t use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Is a Cashback and Rewards System?
    whatIsSection: {
      badgeText: "Cashback and Rewards",
      heading: "What Is a Cashback and Rewards System?",
      description:
        "A cashback and rewards system gives customers back a percentage of what they spend as redeemable wallet balance, instead of reducing the price at the time of sale like a flat discount does. Because the reward is only usable on a future visit, cashback creates a reason for the customer to come back, while a discount is used up and forgotten the moment the sale is over.",
      steps: [],
      bottomLine:
        "Fydo lets you set cashback rules by visit count or spend amount, credit it automatically at checkout, and let customers redeem it on their next purchase - so every reward you give is tied to a repeat visit, not a one-time markdown.",
    },
    // H2 - Why Does Your Shop Need a Cashback and Rewards System?
    problemSection: {
      badgeText: "Why It Matters",
      heading: "Why Does Your Shop Need a Cashback and Rewards System?",
      subHeading:
        "Many shops rely only on flat discounts to bring customers in, but discounts don't build a reason to return. Without a structured cashback system:",
      points: [
        "Flat discounts cut into margins on every single sale",
        "Customers use the discount once and don't come back",
        "No wallet balance sitting with the customer to pull them back in",
        "No way to reward frequent customers differently from one-time buyers",
        "Cashback given out isn't tracked against redemptions",
        "No urgency for customers to return and redeem what they've earned",
        "Rewards can't be combined with other offers or occasions",
        "No visibility into whether the rewards program is actually profitable",
      ],
      endingLine:
        "Without a proper cashback and rewards system, every rupee you discount is spent once - it doesn't buy you a second visit.",
    },
    // H2 - Features Built for Cashback and Rewards Programs (each item below renders as an H3)
    featureSection: {
      heading: "Features Built for Cashback and Rewards Programs",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Rule-Based", gradient: false },
          titleGrad: { text: "Cashback Tiers", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Set cashback percentages by spend amount or visit count, so bigger purchases and repeat customers earn more.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 2,
          titleStart: { text: "Instant vs.", gradient: false },
          titleGrad: { text: "Delayed Cashback", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Credit cashback immediately at checkout, or delay it to encourage a return visit within a set window.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 3,
          titleStart: { text: "Simple", gradient: false },
          titleGrad: { text: "Redemption Flow", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Customers redeem their cashback wallet balance at checkout in a few taps - no coupon codes or manual calculations.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [],
        },
        {
          id: 4,
          titleStart: { text: "Combine Cashback", gradient: false },
          titleGrad: { text: "With Other Offers", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Stack cashback with festival offers or loyalty rewards without breaking your margin calculations.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 5,
          titleStart: { text: "Expiry and", gradient: false },
          titleGrad: { text: "Urgency Mechanics", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Set expiry windows on cashback balances so customers have a reason to return sooner rather than sit on unused rewards.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 6,
          titleStart: { text: "Track", gradient: false },
          titleGrad: { text: "Cashback ROI", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "See how much cashback you've issued, how much has been redeemed, and how much repeat sales it generated.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Perfect for All Types of Shops",
      subHeading:
        "Fydo helps different types of shops run effective cashback and rewards programs.",
      useCases: [
        {
          title: "Grocery Shops",
          detail: "Encourage daily repeat purchases",
        },
        {
          title: "Clothing Stores",
          detail: "Bring customers back for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat visits and bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat orders and loyalty",
        },
      ],
      endingLine:
        "No matter your business type, rewards drive customer retention.",
    },
    // H2 - Benefits of a Cashback and Rewards System Over Flat Discounts
    benefitsSection: {
      badgeText: "Benefits",
      heading: "Benefits of a Cashback and Rewards System Over Flat Discounts",
      description:
        "Cashback ties every rupee you give back to a future visit, instead of giving it away with nothing in return.",
      benefits: [
        "Built-In Reason to Return: Cashback sits in the customer's wallet until they come back to redeem it, unlike a discount that's used and forgotten",
        "Lower Effective Cost Than Discounts: You only pay out cashback that actually gets redeemed, not every rupee promised upfront",
        "Rewards Scale With Loyalty: Give bigger cashback percentages to frequent customers instead of the same flat rate for everyone",
        "Redemption Drives a Second Sale: Every redemption happens alongside a new purchase, not as a standalone giveaway",
        "Expiry Creates Urgency: Time-bound cashback balances push customers to return sooner instead of sitting on unused rewards",
        "Full Visibility Into ROI: Track exactly how much cashback you've issued against how much repeat revenue it generated",
      ],
    },
    customerTestimonialsSection: {
      heading: "Shop Owners Are Growing with Cashback and Rewards by Fydo",
      subHeading:
        "Retail businesses use Fydo to reward customers and increase repeat visits.",
      testimonials: [
        {
          id: 1,
          quote:
            "Our repeat customers increased after we started offering cashback through Fydo. It really works.",
          description:
            "We replaced basic discounts with a structured cashback program using Fydo. Now, our customers actively return to redeem their rewards, significantly boosting our sales.",
          author: "Retail Shop Owner",
          rating: 5.0,
          authorStoreName: "Fydo Partner Shop",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start Your Cashback and Rewards Program in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Quick and easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Your Rewards Program",
          description: "Set cashback and offers based on your business",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Grow Your Business",
          description: "Start increasing repeat customers and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a cashback and rewards system for shops?",
          answer:
            "A cashback and rewards system credits customers a percentage of their purchase as redeemable wallet balance, which they can use on a future visit, instead of reducing the price at the time of sale.",
        },
        {
          question: "How is cashback different from a flat discount?",
          answer:
            "A flat discount is used up immediately and gives the customer no reason to return, while cashback stays in their wallet until they come back to redeem it on a future purchase.",
        },
        {
          question: "How do I set cashback rules for my shop?",
          answer:
            "With Fydo, you can set cashback percentages based on spend amount or visit count, and even offer higher cashback for repeat or high-value customers.",
        },
        {
          question: "Can cashback expire?",
          answer:
            "Yes, you can set an expiry window on cashback balances so customers have a reason to return sooner instead of letting the reward sit unused.",
        },
        {
          question: "Can I combine cashback with other offers?",
          answer:
            "Yes, Fydo lets you stack cashback with festival offers or loyalty rewards without manually recalculating your margins.",
        },
        {
          question: "How do customers redeem their cashback?",
          answer:
            "Customers redeem their cashback wallet balance directly at checkout in a few taps, with no coupon codes needed.",
        },
        {
          question:
            "Can I track how much cashback I've given out and redeemed?",
          answer:
            "Yes, Fydo tracks total cashback issued, redeemed, and the repeat sales it generated, so you can measure the program's ROI.",
        },
      ],
    },
    signupSection: {
      badgeText: "Cashback and Rewards",
      title: "Start Rewarding Your Customers and Growing Your Business Today",
      subtitle:
        "Don’t let customers visit once and disappear. Build loyalty with cashback and rewards using Fydo.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: ["cashback"],
  },

  "customer-loyalty-platform": {
    slug: "customer-loyalty-platform",
    cluster: "rewards-platform",
    // Angle: all-in-one platform - a central hub that replaces the spreadsheet +
    // SMS tool + punch card stack most retailers cobble together.
    seo: {
      title: "All-in-One Customer Loyalty Platform for Retail",
      description:
        "Fydo replaces scattered spreadsheets, SMS tools, and punch cards with one loyalty platform - rewards, WhatsApp campaigns, and reporting in a single dashboard.",
    },
    heroSection: {
      badgeText: "All-In-One Loyalty Platform",
      title: "One Loyalty Platform to Replace Every Tool You're Juggling Today",
      gradientSubHeading: "Spreadsheet, SMS tool, punch card",
      baseSubHeading: "running your loyalty program instead of you?",
      microSubHeading:
        "Most retail businesses run loyalty across a spreadsheet for customer records, a separate WhatsApp or SMS tool for offers, and a paper punch card or register note for rewards. Fydo brings loyalty programs, customer communication, cashback, and reporting into one platform, so nothing lives in three different places anymore.",
      supportingText:
        "No rip-and-replace needed. Works with your existing POS system or even if you don't use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Makes Fydo an All-In-One Loyalty Platform?
    whatIsSection: {
      badgeText: "All-In-One Loyalty Platform",
      heading: "What Makes Fydo an All-In-One Loyalty Platform?",
      description:
        "Instead of stitching together a spreadsheet, a messaging app, and a punch card system, Fydo combines customer records, rewards and cashback, WhatsApp campaigns, and performance reporting inside one dashboard. Every part of your loyalty program - who your customers are, what they've earned, and how you're reaching them - lives in the same place and stays in sync automatically.",
      steps: [],
      bottomLine:
        "One login, one dashboard, one source of truth for your entire loyalty program.",
    },
    // H2 - The Real Cost of Running Loyalty Across Disconnected Tools
    problemSection: {
      badgeText: "The Real Cost",
      heading: "The Real Cost of Running Loyalty Across Disconnected Tools",
      subHeading:
        "Most retail businesses don't lack loyalty tools - they have too many of them, and none of them talk to each other:",
      points: [
        "Customer records spread across a notebook, a spreadsheet, and your memory",
        "A separate SMS or WhatsApp tool just to send offers",
        "Paper punch cards or register notes to track visits and rewards",
        "No single view of who your repeat customers actually are",
        "Switching between three apps just to run one campaign",
        "Data that never syncs, so your reports never quite add up",
        "New staff need training on multiple systems instead of one",
        "Nobody owns the full picture of what's actually working",
      ],
      endingLine:
        "Every disconnected tool is one more place customer loyalty can quietly fall through the cracks. A single platform closes those gaps.",
    },
    // H2 - One Dashboard for Everything Your Loyalty Program Needs (each item below renders as an H3)
    featureSection: {
      heading: "One Dashboard for Everything Your Loyalty Program Needs",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Replace Your Spreadsheet With a",
            gradient: false,
          },
          titleGrad: { text: "Live Customer Database", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Every customer, their visit history, and their rewards balance in one searchable place - no more cross-referencing a spreadsheet against a notebook.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [
            "Automatic customer profiles built from every visit",
            "Search and filter by visit frequency or spend",
            "No manual data entry or reconciliation",
          ],
        },
        {
          id: 2,
          titleStart: {
            text: "Replace Your SMS Tool With Built-In",
            gradient: false,
          },
          titleGrad: { text: "WhatsApp Campaigns", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Send offers and updates from the same platform that already knows who your customers are and what they've bought, instead of exporting lists to a separate tool.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [
            "Campaigns pull directly from your existing customer list",
            "No exporting or re-importing contact lists",
            "One place to see who received which offer",
          ],
        },
        {
          id: 3,
          titleStart: {
            text: "Replace Punch Cards With Automatic",
            gradient: false,
          },
          titleGrad: { text: "Rewards & Cashback", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Rewards and cashback apply themselves based on rules you set once, instead of a punch card that gets lost or forgotten at the counter.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [
            "Rules-based rewards that apply automatically at checkout",
            "No paper cards to lose, forget, or fake",
            "Balance visible to you and the customer at any time",
          ],
        },
        {
          id: 4,
          titleStart: {
            text: "Replace Guesswork With a Single",
            gradient: false,
          },
          titleGrad: { text: "Reporting Dashboard", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "See repeat customers, campaign performance, and rewards activity together instead of piecing together separate spreadsheets and app exports.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [
            "One view of customers, campaigns, and rewards",
            "No manual copy-paste between tools to build a report",
            "Numbers you can trust because they come from one system",
          ],
        },
        {
          id: 5,
          titleStart: { text: "Replace Multiple Logins With", gradient: false },
          titleGrad: { text: "One Team Workspace", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Your team accesses customer records, campaigns, and rewards from a single account instead of separate logins for separate tools.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [
            "One account for your whole team to work from",
            "No juggling passwords across multiple apps",
            "Everyone sees the same, up-to-date information",
          ],
        },
        {
          id: 6,
          titleStart: { text: "Keep Your Existing", gradient: false },
          titleGrad: { text: "POS or Billing Setup", gradient: true },
          titleEnd: { text: "If You Have One", gradient: false },
          description:
            "Fydo sits on top of what you already use, or works entirely on its own if you don't have a POS - either way, there's nothing to rip out.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [
            "Works with your existing POS or billing setup",
            "Works just as well if you don't use one at all",
            "No forced migration to a new billing system",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Built for Every Type of Retail Business",
      subHeading:
        "Fydo is designed for all types of retail businesses looking to increase repeat customers and improve customer engagement.",
      useCases: [
        {
          title: "Grocery Stores",
          detail:
            "Increase daily repeat customers and build regular buying habits",
        },
        {
          title: "Clothing Stores",
          detail:
            "Bring customers back for new collections and seasonal offers",
        },
        {
          title: "Salons & Spas",
          detail: "Build long-term customer relationships and repeat visits",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Increase repeat orders and customer loyalty",
        },
      ],
      endingLine:
        "No matter your business type, Fydo helps you grow consistently.",
    },
    // H2 - Why an All-In-One Platform Beats a Stack of Point Tools
    benefitsSection: {
      badgeText: "Benefits",
      heading: "Why an All-In-One Platform Beats a Stack of Point Tools",
      description:
        "Consolidating onto one platform isn't just tidier - it changes how consistently your loyalty program actually runs.",
      benefits: [
        "Fewer Tools, Less Chaos: Run rewards, campaigns, and reporting from one login instead of juggling three separate apps",
        "One Source of Truth: Customer data stays consistent because it's never copied between spreadsheets and tools",
        "Faster Onboarding: New staff learn a single dashboard instead of a stack of disconnected systems",
        "Lower Total Cost: Replace several subscriptions and manual processes with one platform",
        "Consistent Customer Experience: Rewards and messages feel unified because they come from the same system",
        "Faster Decisions: See customers, campaigns, and rewards together instead of piecing together separate reports",
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Thousands of shop owners use Fydo to increase repeat customers, improve customer engagement, and grow their business every day.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo made it easy for us to bring customers back. We’ve seen a clear increase in repeat visits and sales.",
          description:
            "We set up a flexible loyalty program easily and started engaging our customers automatically. We now see more repeat visits and stronger retention.",
          author: "Shop Owner",
          rating: 5.0,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start Growing Your Business in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started in minutes with a simple and quick setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Your Loyalty Programs",
          description:
            "Set rewards based on visits, spending, or special occasions",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Bring Customers Back",
          description:
            "Run campaigns and start increasing repeat customers and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question:
            "Does Fydo replace the spreadsheet I currently use to track customers?",
          answer:
            "Yes. Fydo automatically builds a customer database from every visit, so you no longer need a separate spreadsheet to track who's shopping with you.",
        },
        {
          question:
            "Can Fydo replace a separate WhatsApp or SMS marketing tool?",
          answer:
            "Yes. WhatsApp campaigns are built into Fydo, so you can message customers directly from the same platform that manages your loyalty program.",
        },
        {
          question:
            "Do I still need punch cards or a separate rewards tracker?",
          answer:
            "No. Rewards and cashback are tracked and applied automatically inside Fydo, removing the need for paper punch cards.",
        },
        {
          question: "Can Fydo work alongside tools I'm already using?",
          answer:
            "Yes. Fydo works with your existing POS system, or on its own if you don't use one, so you don't have to rip out what's already working.",
        },
        {
          question: "Is there one login for my whole team?",
          answer:
            "Yes. Your team can access customer records, campaigns, and rewards from a single account instead of separate logins for different tools.",
        },
        {
          question:
            "How is an all-in-one platform different from using multiple loyalty apps together?",
          answer:
            "Separate apps require manual syncing and re-entry of data. Fydo keeps customer information, rewards, and campaigns connected automatically in one place.",
        },
        {
          question:
            "Does switching to one platform cost more than my current setup?",
          answer:
            "Usually less. Consolidating onto Fydo typically costs less than paying for a spreadsheet workaround plus a separate messaging tool and manual tracking.",
        },
      ],
    },
    signupSection: {
      badgeText: "Customer Loyalty Platform",
      title:
        "Start Increasing Repeat Customers and Growing Your Business Today",
      subtitle:
        "Join thousands of retail businesses already using Fydo to build customer loyalty and increase repeat sales.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [
      "loyalty program",
      "loyalty software",
      "traditional loyalty",
      "tier-2",
      "loyalty trend",
    ],
  },

  "customer-retention-platform-india": {
    slug: "customer-retention-platform-india",
    cluster: "retention-platform",
    // Angle: India-market specifics - WhatsApp-first habits, cash/UPI-heavy
    // retail, and working without a POS system. (A "vernacular/regional
    // language messaging" claim was removed throughout this file - no such
    // feature exists anywhere in the codebase; it was an unverified capability
    // claim that shouldn't have shipped.)
    seo: {
      title: "Customer Retention Platform Built for Indian Retail",
      description:
        "Fydo helps Indian retailers retain customers with WhatsApp-first engagement, UPI-friendly rewards, and support for cash transactions - no POS required.",
    },
    heroSection: {
      badgeText: "Retention Platform for India",
      title: "Customer Retention Built the Way Indian Retail Actually Works",
      gradientSubHeading: "Cash counters, WhatsApp chats, no POS",
      baseSubHeading: "Fydo was built for exactly that.",
      microSubHeading:
        "Most retention software assumes email inboxes, card swipes, and a POS system at every counter - not how most Indian retail actually runs. Fydo tracks customers by phone number, sends offers over WhatsApp where Indian shoppers already are, and works whether you bill on a POS, a notebook, or purely in cash.",
      supportingText:
        "No POS required. Works with cash, UPI, or card. Built for how Indian shops actually run.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - Why Don't Generic Loyalty Tools Fit Indian Retail?
    whatIsSection: {
      badgeText: "Built for Indian Retail",
      heading: "Why Don't Generic Loyalty Tools Fit Indian Retail?",
      description:
        "Most retention software is built assuming email addresses, card-linked purchases, and a POS system at every counter. Indian retail runs differently - customers are reached over WhatsApp, and a large share of transactions are cash or UPI with no card trail to track a repeat visit. Fydo is built around phone numbers, WhatsApp, and manual entry, so it fits how Indian shops actually operate.",
      steps: [],
      bottomLine:
        "Built for the Indian shop counter, not adapted from a Western retention playbook.",
    },
    // H2 - Retention Challenges That Are Unique to Indian Retail
    problemSection: {
      badgeText: "Problem",
      heading: "Retention Challenges That Are Unique to Indian Retail",
      subHeading:
        "Most loyalty and retention platforms are built for a market that doesn't look like Indian retail:",
      points: [
        "Customers are reachable on WhatsApp, not email - but most tools are built around email",
        "A large share of sales are cash or UPI, with no card data to track purchases",
        "Many shops still bill manually, with no POS system to plug a tool into",
        "Owners and staff don't have time to learn complicated software on top of running the shop",
        "Neighbourhood competition means one bad experience sends a customer next door",
        "Festival and occasion-based buying spikes need timely, local campaigns",
        "Owners don't have time to reconcile data between a register and an app",
      ],
      endingLine:
        "A retention platform built for India needs to work around cash, WhatsApp, and phone numbers, not assume a card swipe and an email address.",
    },
    // H2 - Built Around How Indian Retail Actually Runs (each item below renders as an H3)
    featureSection: {
      heading: "Built Around How Indian Retail Actually Runs",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "WhatsApp-First", gradient: false },
          titleGrad: { text: "Customer Engagement", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Reach customers on the app they already check every day, without asking them to download anything new or check an inbox they rarely open.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [
            "Offers delivered where customers already are",
            "No app download required for the customer",
            "Higher open rates than email or SMS",
          ],
        },
        {
          id: 2,
          titleStart: { text: "Works Without a", gradient: false },
          titleGrad: { text: "POS System", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Log visits and rewards by phone number even if you bill manually or purely in cash, so a missing POS is never a reason to skip retention.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [
            "No POS or billing software required",
            "Visits logged in seconds by phone number",
            "Works for cash-only counters just as well",
          ],
        },
        {
          id: 3,
          titleStart: { text: "UPI-Friendly", gradient: false },
          titleGrad: { text: "Cashback & Rewards", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Reward customers in a way that fits how they already pay, without requiring a card or a linked bank account tied to a specific network.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [
            "Cashback that fits UPI-first payment habits",
            "No card-linking required to earn or redeem",
            "Works alongside cash and digital payments",
          ],
        },
        {
          id: 4,
          titleStart: { text: "Simple Setup,", gradient: false },
          titleGrad: { text: "No Training Needed", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Get started without a lengthy onboarding process - staff can log a visit or apply a reward in seconds, with no technical setup on your end.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [
            "No complicated setup or IT support needed",
            "Staff can use it on day one",
            "Works alongside however you already run the counter",
          ],
        },
        {
          id: 5,
          titleStart: { text: "Festival & Occasion", gradient: false },
          titleGrad: { text: "Campaigns", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Run offers around Diwali, Eid, and regional festivals when Indian retail sees its biggest buying spikes, using the same campaign tools you'd use any other day.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [
            "Run campaigns for major Indian festivals",
            "Works for regional occasions, not just national ones",
            "Campaigns ready before the buying spike hits",
          ],
        },
        {
          id: 6,
          titleStart: { text: "Track Every Customer by", gradient: false },
          titleGrad: { text: "Phone Number, Not Card", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Identify repeat customers by phone number, since cash and UPI transactions don't leave the card trail most retention tools expect.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [
            "Works even when there's no card transaction to track",
            "One identity across cash, UPI, and card visits",
            "No dependency on a payment gateway",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Built for Retail Businesses Across India",
      subHeading:
        "Fydo is designed for Indian retail businesses that want to improve customer retention and increase repeat customers.",
      useCases: [
        {
          title: "Grocery Stores",
          detail: "Build daily repeat buying habits",
        },
        {
          title: "Clothing Stores",
          detail: "Bring customers back for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat visits and orders",
        },
      ],
      endingLine:
        "No matter your business type, Fydo helps you retain customers and grow consistently.",
    },
    // H2 - Why Indian Retailers Choose Fydo Over Generic Retention Tools
    benefitsSection: {
      badgeText: "Benefits",
      heading: "Why Indian Retailers Choose Fydo Over Generic Retention Tools",
      description:
        "Fydo is designed around how Indian retail actually operates, not adapted after the fact from a Western retention playbook.",
      benefits: [
        "No POS Required: Track visits and rewards by phone number even if you bill manually or in cash",
        "WhatsApp-Native Engagement: Reach customers on the app they already check every day",
        "UPI-Friendly Rewards: Cashback works alongside how your customers already pay",
        "No Training Overhead: Staff can start logging visits and rewards on day one, no onboarding required",
        "Festival-Ready Campaigns: Run offers around Diwali, Eid, and regional occasions with the same easy campaign tools",
        "Built for Local Competition: Stay top of mind in a market where the next shop is a five-minute walk away",
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Retailers across India use Fydo to increase repeat customers, improve customer engagement, and grow their business.",
      testimonials: [
        {
          id: 1,
          quote:
            "After using Fydo, we started seeing customers come back more frequently. It’s easy to use and works well for our shop.",
          description:
            "We set up our customer retention strategy effortlessly with Fydo. It helped us connect with our customers better, and we have seen a noticeable improvement in retention and repeat sales.",
          author: "Retail Store Owner",
          rating: 5.0,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start Improving Customer Retention in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started quickly with a simple setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Loyalty Programs",
          description: "Set rewards based on visits, spending, or occasions",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Bring Customers Back",
          description: "Run campaigns and start increasing repeat customers",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "Does Fydo work without a POS system?",
          answer:
            "Yes. Fydo tracks customers and rewards by phone number, so it works even if you bill manually or purely in cash, with no POS required.",
        },
        {
          question:
            "Do I need any technical setup or training to start using Fydo?",
          answer:
            "No. Fydo is designed to be used directly by shop staff, without IT support or a lengthy onboarding process - you can start logging visits and rewards the same day.",
        },
        {
          question: "Does Fydo support UPI-based cashback?",
          answer:
            "Yes. Cashback and rewards in Fydo work alongside UPI and cash payments, not just card-linked transactions.",
        },
        {
          question: "How does Fydo track cash transactions?",
          answer:
            "Customers are identified by phone number at the counter, so a cash sale can still be logged and rewarded like any other visit.",
        },
        {
          question:
            "Can Fydo run festival-specific campaigns like Diwali or Eid offers?",
          answer:
            "Yes. You can run time-based and festival campaigns easily, including for regional occasions, using the same tools you'd use for any other offer.",
        },
        {
          question: "Is Fydo built specifically for the Indian market?",
          answer:
            "Yes. Fydo is designed around WhatsApp-first communication and UPI and cash-heavy retail, rather than adapted from a foreign retention tool.",
        },
        {
          question: "Do customers need to download an app to receive offers?",
          answer:
            "No. Offers can be sent over WhatsApp, so customers don't need to install a new app just to receive rewards and updates.",
        },
      ],
    },
    signupSection: {
      badgeText: "Customer Retention",
      title: "Start Retaining More Customers and Growing Your Business Today",
      subtitle:
        "Join thousands of businesses across India using Fydo to improve customer retention and increase repeat sales.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "customer-retention-strategies-for-small-business": {
    slug: "customer-retention-strategies-for-small-business",
    cluster: "retention-platform",
    // Title/description are deliberately distinct from heroSection.title (the
    // H1) - a concise, front-loaded SERP title and a benefit-led meta
    // description read better in search results than a literal H1 match.
    seo: {
      title: "Affordable Customer Retention Tips for Small Shops",
      description:
        "Keep customers coming back without hiring a marketing team. Fydo gives small businesses low-cost loyalty tools and WhatsApp campaigns built for tight budgets.",
    },
    heroSection: {
      badgeText: "Customer Retention",
      title: "Customer Retention Strategies for Small Businesses on a Budget",
      gradientSubHeading: "No marketing budget",
      baseSubHeading: "or team to run one?",
      microSubHeading:
        "Retention doesn't require expensive campaigns or a dedicated marketing hire. Fydo gives small business owners a low-cost way to reward repeat customers, send WhatsApp updates, and stay top of mind - all from a phone, without extra staff or complicated software.",
      supportingText:
        "No complicated setup. Works with your existing system or even if you don't use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Does Customer Retention Actually Cost a Small Business?
    whatIsSection: {
      badgeText: "Customer Retention",
      heading: "What Does Customer Retention Actually Cost a Small Business?",
      description:
        "Retaining a customer costs far less than acquiring a new one, but many small businesses still treat retention as an expensive extra. In reality, it comes down to a few low-cost habits: rewarding customers who return, sending the occasional personal update, and giving people a small reason to choose you again. None of that requires a big budget - it requires consistency.",
      steps: [],
      bottomLine:
        "Fydo makes those habits easy to run consistently - cashback rewards, WhatsApp updates, and repeat-visit tracking - without needing a marketing budget or a dedicated team.",
    },
    // H2 - Why Small Businesses Struggle to Retain Customers on a Tight Budget
    problemSection: {
      badgeText: "Problem",
      heading:
        "Why Small Businesses Struggle to Retain Customers on a Tight Budget",
      subHeading:
        "For a small business, every rupee spent on marketing has to earn its place. Common retention struggles include:",
      points: [
        "No budget for paid ads or expensive marketing campaigns",
        "No time to manually track who's a repeat customer",
        "Can't afford to hire a dedicated marketing person",
        "Generic discounts eat into already thin profit margins",
        "No easy way to remind customers to come back",
        "Losing customers to competitors with bigger ad budgets",
        "Loyalty cards or spreadsheets are too much manual work",
        "No visibility into whether marketing spend is actually working",
      ],
      endingLine:
        "Without an affordable, low-effort system, small businesses end up spending more to replace lost customers than they would to keep the ones they already have.",
    },
    // H2 - Retention Tools Built for Small Business Budgets (each item below renders as an H3)
    featureSection: {
      heading: "Retention Tools Built for Small Business Budgets",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Reward Repeat Customers", gradient: false },
          titleGrad: { text: "Without Discounting Everyone", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Set up cashback or point rewards that cost less than blanket discounts and target the customers who actually come back.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 2,
          titleStart: { text: "Replace Paid Ads with", gradient: false },
          titleGrad: { text: "WhatsApp Campaigns", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Reach customers directly for a fraction of what social or search ads cost.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 3,
          titleStart: { text: "Skip the Spreadsheet with", gradient: false },
          titleGrad: { text: "Automatic Customer Tracking", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "See who's a repeat customer and who's drifting away without manual bookkeeping.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [],
        },
        {
          id: 4,
          titleStart: { text: "Win Back Customers", gradient: false },
          titleGrad: { text: "Before You Lose Them", gradient: true },
          titleEnd: { text: "for Good", gradient: false },
          description:
            "Send targeted offers to customers who've gone quiet, instead of spending on new customer acquisition.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 5,
          titleStart: { text: "Run Campaigns", gradient: false },
          titleGrad: { text: "Without Hiring", gradient: true },
          titleEnd: { text: "a Marketing Person", gradient: false },
          description:
            "Set up and manage loyalty and WhatsApp campaigns yourself, in minutes, from your phone.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 6,
          titleStart: { text: "See What's Actually", gradient: false },
          titleGrad: { text: "Working", gradient: true },
          titleEnd: { text: "for Your Budget", gradient: false },
          description:
            "Track which offers and campaigns bring customers back, so every rupee spent has a clear return.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Perfect for All Types of Small Businesses",
      subHeading:
        "Fydo helps different small businesses improve customer retention and increase repeat customers.",
      useCases: [
        {
          title: "Retail Shops",
          detail: "Increase repeat purchases and loyalty",
        },
        {
          title: "Salons & Services",
          detail: "Improve repeat bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Increase repeat visits and orders",
        },
        {
          title: "Local Stores",
          detail: "Build strong customer relationships",
        },
      ],
      endingLine:
        "No matter your business, customer retention drives long-term success.",
    },
    // H2 - Why Retention Is the Smarter Investment for Small Businesses
    benefitsSection: {
      badgeText: "Benefits",
      heading: "Why Retention Is the Smarter Investment for Small Businesses",
      description:
        "Retention delivers more return per rupee than most acquisition spend - especially on a small business budget.",
      benefits: [
        "Lower Marketing Costs: Keeping a customer costs far less than acquiring a new one",
        "No Extra Staff Needed: Run retention campaigns yourself without hiring help",
        "Predictable Repeat Revenue: Rewarded customers return more consistently",
        "Better Margins Than Discounting: Targeted rewards protect margins that blanket discounts erode",
        "Simple Setup: Get started without complex tools or technical know-how",
        "Grow Without a Big Budget: Build steady repeat business without matching competitors' ad spend",
      ],
    },
    customerTestimonialsSection: {
      heading: "Small Businesses Are Growing with Better Customer Retention",
      subHeading:
        "Shop owners use Fydo to retain customers and grow their business.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helped us improve customer retention and increase repeat customers without extra effort.",
          description:
            "Fydo gives small businesses simple tools for loyalty, WhatsApp marketing, and tracking so retention improves without extra overhead.",
          author: "Small Business Owner",
          rating: 4.8,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Small Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Improve Customer Retention in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started quickly with easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Loyalty Programs and Campaigns",
          description: "Set rewards and engagement strategies",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Retain and Grow",
          description: "Start increasing repeat customers and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question:
            "What are the most affordable customer retention strategies for small businesses?",
          answer:
            "Rewarding repeat visits with cashback or points, sending WhatsApp updates, and re-engaging inactive customers are all low-cost, high-impact retention strategies.",
        },
        {
          question:
            "Is customer retention cheaper than acquiring new customers?",
          answer:
            "Yes. Retaining an existing customer typically costs far less than acquiring a new one through ads or promotions.",
        },
        {
          question:
            "How can a small business retain customers without a marketing team?",
          answer:
            "Simple tools that automate rewards and WhatsApp messaging let one person run retention campaigns without hiring additional staff.",
        },
        {
          question: "Do discounts help retain customers?",
          answer:
            "Discounts can attract a purchase, but they don't build loyalty on their own - targeted rewards for repeat visits are more effective and protect margins better.",
        },
        {
          question:
            "How much should a small business spend on customer retention?",
          answer:
            "There's no fixed number, but low-cost tools like loyalty rewards and WhatsApp campaigns let small businesses retain customers for far less than paid advertising.",
        },
        {
          question: "Is Fydo affordable for small businesses?",
          answer:
            "Yes, Fydo is built for small businesses that need effective retention tools without a large marketing budget or dedicated staff.",
        },
        {
          question: "Can Fydo help win back customers who stopped visiting?",
          answer:
            "Yes, Fydo helps identify inactive customers and send targeted offers to bring them back.",
        },
      ],
    },
    signupSection: {
      badgeText: "Customer Retention",
      title: "Start Retaining More Customers and Growing Your Business Today",
      subtitle:
        "Don't let customers visit once and disappear. Build loyalty and grow your business with Fydo.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "free-customer-loyalty-app": {
    slug: "free-customer-loyalty-app",
    cluster: "app-general",
    // Title/description are deliberately distinct from heroSection.title (the
    // H1) - a concise, front-loaded SERP title and a benefit-led meta
    // description read better in search results than a literal H1 match.
    seo: {
      title: "Free Customer Loyalty App for Businesses",
      description:
        "Increase repeat customers with Fydo's free customer loyalty app. Engage shoppers, drive repeat purchases, and build stronger customer relationships.",
    },
    heroSection: {
      badgeText: "Customer Loyalty App",
      title:
        "Download Free Customer Loyalty App to Increase Repeat Customers for Your Retail Businesses.",
      gradientSubHeading: "Customers visit once",
      baseSubHeading: "but don’t come back?",
      microSubHeading:
        "Fydo is a customer loyalty app built for retail businesses that want to increase repeat purchases and retain more customers. Create loyalty programs, offer cashback and rewards, run WhatsApp campaigns, and understand customer behavior-all from one simple platform.",
      supportingText:
        "Simple app. No complicated setup - works with your existing system or even if you don’t use one.",
      primaryButton: {
        title: "Start Building Customer Loyalty",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Is a Customer Loyalty App?
    whatIsSection: {
      badgeText: "Customer Loyalty App",
      heading: "What Is a Customer Loyalty App?",
      description:
        "Free customer loyalty app is a digital solution that helps businesses build stronger relationships with their customers and encourage them to shop again. Instead of relying on physical loyalty cards or generic discounts, businesses can use a loyalty app to reward purchases, deliver personalized offers, track customer activity, and stay connected with shoppers.",
      steps: [],
      bottomLine:
        "Fydo helps retail businesses turn customer transactions into actionable insights and meaningful loyalty experiences. From rewards and cashback to WhatsApp engagement, Fydo gives businesses the tools they need to increase customer retention and repeat purchases.",
    },
    // H2 - Why Does Your Business Need a Customer Loyalty App?
    problemSection: {
      badgeText: "Why It Matters",
      heading: "Why Does Your Business Need a Customer Loyalty App?",
      subHeading:
        "Getting a customer to make their first purchase is only the beginning. The real opportunity is getting that customer to return. Free customer loyalty app helps businesses:",
      points: [
        "Increase repeat purchases",
        "Reward loyal customers",
        "Improve customer retention",
        "Create personalized offers",
        "Increase customer engagement",
        "Understand customer buying behavior",
        "Re-engage inactive customers",
        "Build long-term customer relationships",
      ],
      endingLine:
        "With Fydo, your loyalty strategy becomes more than just giving discounts. You can use customer data and engagement tools to create relevant experiences that encourage customers to come back.",
    },
    // H2 - Customer Loyalty App Features (each item below renders as an H3)
    featureSection: {
      heading: "Customer Loyalty App Features",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Rewards &", gradient: false },
          titleGrad: { text: "Cashback", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Give customers a reason to return with flexible rewards and cashback programs. Create offers that align with your business objectives while giving customers tangible value.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 2,
          titleStart: { text: "Personalized", gradient: false },
          titleGrad: { text: "Offers", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Not every customer behaves the same way. Create targeted offers based on customer activity and purchase behavior instead of sending the same promotion to everyone.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 3,
          titleStart: { text: "WhatsApp Customer", gradient: false },
          titleGrad: { text: "Engagement", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Stay connected with customers through WhatsApp campaigns. Share offers, rewards, promotions, and relevant updates to keep your brand top of mind.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [],
        },
        {
          id: 4,
          titleStart: { text: "Customer", gradient: false },
          titleGrad: { text: "Segmentation", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Group customers based on their activity and behavior so you can create more relevant campaigns and loyalty experiences.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 5,
          titleStart: { text: "Customer", gradient: false },
          titleGrad: { text: "Insights", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Understand who your most valuable customers are, how often they purchase, and where opportunities exist to increase repeat purchases.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 6,
          titleStart: { text: "Repeat Customer", gradient: false },
          titleGrad: { text: "Tracking", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Track customer activity and identify returning customers so you can measure the effectiveness of your loyalty strategy.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Perfect for Businesses Across India",
      subHeading:
        "Fydo helps different types of businesses improve customer loyalty and grow.",
      useCases: [
        {
          title: "Retail Shops",
          detail: "Increase repeat purchases",
        },
        {
          title: "Grocery Stores",
          detail: "Build daily customer habits",
        },
        {
          title: "Clothing Stores",
          detail: "Bring customers back for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat visits",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat orders",
        },
      ],
      endingLine: "No matter your business type, loyalty drives growth.",
    },
    // H2 - Benefits of Using a Customer Loyalty App
    benefitsSection: {
      badgeText: "Benefits",
      heading: "Benefits of Using a Customer Loyalty App",
      description:
        "Turn transactions into ongoing customer relationships through consistent engagement and rewarding experiences.",
      benefits: [
        "Increase Repeat Purchases: Encourage customers to return by giving them rewards and personalized incentives",
        "Improve Customer Retention: Build relationships that go beyond a customer's first transaction",
        "Increase Customer Engagement: Stay connected with shoppers through relevant communication and offers",
        "Make Better Marketing Decisions: Use customer data and insights to understand what drives repeat purchases",
        "Reduce Dependence on Blanket Discounts: Instead of offering the same discount to everyone, create targeted rewards for specific customer groups",
        "Build Long-Term Customer Relationships: Turn transactions into ongoing customer relationships through consistent engagement",
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Businesses Across India",
      subHeading:
        "Thousands of businesses use Fydo to increase repeat customers and improve customer loyalty.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo made it easy for us to build customer loyalty. We’ve seen a clear increase in repeat visits.",
          description:
            "We set up our own customized loyalty program effortlessly with Fydo. It helped us connect with our customers better, and we have seen a noticeable improvement in retention and repeat sales.",
          author: "Business Owner",
          rating: 5.0,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Local Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start Building Customer Loyalty in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Quick and easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Loyalty Programs",
          description: "Set rewards based on visits, spending, or occasions",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Grow Your Business",
          description: "Start increasing repeat customers and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a customer loyalty app?",
          answer:
            "A customer loyalty app is software that helps businesses reward customers, increase engagement, encourage repeat purchases, and improve customer retention through digital loyalty programs.",
        },
        {
          question: "How does a customer loyalty app work?",
          answer:
            "A loyalty app tracks customer interactions and purchases, allows businesses to provide rewards or offers, and helps them engage customers with personalized communication.",
        },
        {
          question: "Can a customer loyalty app increase repeat purchases?",
          answer:
            "Yes. By rewarding customers and providing relevant incentives and personalized offers, businesses can encourage customers to return and purchase again.",
        },
        {
          question: "Is Fydo a customer loyalty app for retail businesses?",
          answer:
            "Yes. Fydo is designed to help retail businesses create loyalty programs, reward customers, engage shoppers, and increase repeat purchases.",
        },
        {
          question: "Can Fydo send WhatsApp campaigns?",
          answer:
            "Fydo provides customer engagement capabilities that can be used to communicate relevant offers and promotions through WhatsApp.",
        },
        {
          question: "Can I offer cashback through Fydo?",
          answer:
            "Yes, cashback can be incorporated into your loyalty strategy to give customers an additional incentive to return.",
        },
        {
          question: "What businesses can use a customer loyalty app?",
          answer:
            "Retail stores, fashion businesses, restaurants, cafes, salons, supermarkets, and other customer-facing businesses can use loyalty apps to encourage repeat purchases and improve retention.",
        },
      ],
    },
    signupSection: {
      badgeText: "Customer Loyalty App",
      title: "Start Building Customer Loyalty and Growing Your Business Today",
      subtitle:
        "Join businesses across India using Fydo to increase repeat customers and improve customer retention.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "loyalty-program-for-retail-stores": {
    slug: "loyalty-program-for-retail-stores",
    cluster: "rewards-platform",
    // Angle: program DESIGN - the rules/structure of building a loyalty
    // program (tiers, points vs cashback, rules-based rewards).
    seo: {
      title: "Design a Loyalty Program Structure for Retail Stores",
      description:
        "Fydo helps you design the rules of your loyalty program - points vs cashback, tiered rewards, referral mechanics, and expiry rules that fit your margins.",
    },
    heroSection: {
      badgeText: "Loyalty Program Design",
      title: "Design a Loyalty Program With Rules That Actually Fit Your Store",
      gradientSubHeading: "A generic 'buy 10, get 1 free' rule",
      baseSubHeading: "rarely fits your margins or your customers.",
      microSubHeading:
        "Building a loyalty program means more than turning it on - it means deciding how points convert to value, whether to reward with cashback or points, when rewards expire, and whether your best customers get more than a first-time visitor. Fydo gives you the structure to design those rules yourself, not a one-size-fits-all template.",
      supportingText:
        "Simple app. No complicated setup, works with your existing POS system or even if you don't use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Actually Goes Into Designing a Loyalty Program?
    whatIsSection: {
      badgeText: "Program Design",
      heading: "What Actually Goes Into Designing a Loyalty Program?",
      description:
        "A loyalty program is more than a discount - it's a set of rules that decide who gets rewarded, how much, and when. That means choosing between points and cashback, deciding whether repeat customers should unlock a higher tier than first-time buyers, setting rules for how rewards are triggered - by visit, by spend, or by occasion - and defining when unused rewards expire. Fydo gives you control over each of these rules instead of forcing a single fixed structure.",
      steps: [],
      bottomLine:
        "The strength of a loyalty program lives in its rules, not just its existence.",
    },
    // H2 - Where Most Loyalty Program Designs Go Wrong
    problemSection: {
      badgeText: "Problem",
      heading: "Where Most Loyalty Program Designs Go Wrong",
      subHeading:
        "Many retail stores adopt a loyalty program without ever designing the rules that make it work:",
      points: [
        "Copying a generic 'buy 10, get 1 free' rule that doesn't fit their margins",
        "No clear conversion rate between points earned and value redeemed",
        "One flat reward for every customer, regardless of how much they spend",
        "No tier system to reward the best customers more than occasional ones",
        "Rules too complicated for customers to understand or care about",
        "No expiry or redemption limits, so unused rewards pile up as a liability",
        "Occasion-based offers like birthdays or festivals bolted on as an afterthought",
        "No referral mechanic, so loyal customers have no reason to bring in new ones",
      ],
      endingLine:
        "A loyalty program without clear rules is just a discount with extra steps. Getting the structure right is what makes it work.",
    },
    // H2 - Design the Exact Rules Your Loyalty Program Runs On (each item below renders as an H3)
    featureSection: {
      heading: "Design the Exact Rules Your Loyalty Program Runs On",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Choose Between", gradient: false },
          titleGrad: { text: "Points or Cashback", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Decide whether customers earn points that convert to rewards or straightforward cashback, based on whichever model fits your margins and customers better.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [
            "Switch between points and cashback models",
            "Set your own conversion rate for points",
            "Change the structure as your business evolves",
          ],
        },
        {
          id: 2,
          titleStart: { text: "Tiered Rewards", gradient: false },
          titleGrad: { text: "For Your Best Customers", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Set up bronze, silver, and gold-style tiers so customers who spend or visit more unlock better rewards than someone shopping for the first time.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [
            "Multiple tiers based on spend or visit frequency",
            "Higher-value rewards for your most loyal customers",
            "Automatic tier upgrades as customers qualify",
          ],
        },
        {
          id: 3,
          titleStart: { text: "Rule-Based Triggers", gradient: false },
          titleGrad: { text: "By Visit, Spend, or Occasion", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Decide exactly what earns a reward - a visit count, a spend threshold, a birthday, or a festival - instead of one flat rule for every situation.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [
            "Trigger rewards by visit count or spend threshold",
            "Add occasion-based rules like birthdays or festivals",
            "Combine multiple trigger types in one program",
          ],
        },
        {
          id: 4,
          titleStart: { text: "Referral Mechanics", gradient: false },
          titleGrad: { text: "Built Into the Program", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Give existing customers a reason to bring in new ones by rewarding successful referrals as part of your program's rules, not as a separate afterthought.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [
            "Reward customers for referring new shoppers",
            "Set the referral reward as part of your program rules",
            "Turn loyal customers into a source of new ones",
          ],
        },
        {
          id: 5,
          titleStart: { text: "Expiry & Redemption", gradient: false },
          titleGrad: { text: "Rules You Control", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Set validity periods and redemption limits so unused rewards don't sit as an open-ended liability, without making the program feel restrictive to customers.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [
            "Set expiry periods for points or cashback",
            "Define redemption limits per visit or purchase",
            "Balance fairness for customers with control for your business",
          ],
        },
        {
          id: 6,
          titleStart: { text: "A Flexible Reward", gradient: false },
          titleGrad: { text: "Catalog", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Offer discounts, cashback, or free items as redemption options, so customers can redeem in a way that actually feels valuable to them.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [
            "Mix discounts, cashback, and free items as rewards",
            "Let customers choose how they redeem",
            "Adjust the catalog without rebuilding the program",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Perfect Loyalty Program for Every Retail Store",
      subHeading:
        "Fydo helps different types of retail businesses run effective loyalty programs.",
      useCases: [
        {
          title: "Grocery Stores",
          detail: "Build daily repeat buying habits",
        },
        {
          title: "Clothing Stores",
          detail: "Encourage customers to return for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat visits and bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat orders and loyalty",
        },
      ],
      endingLine:
        "No matter your business type, Fydo helps you grow through customer loyalty.",
    },
    // H2 - What a Well-Designed Loyalty Program Structure Gets You
    benefitsSection: {
      badgeText: "Benefits",
      heading: "What a Well-Designed Loyalty Program Structure Gets You",
      description:
        "The rules behind your loyalty program determine whether it actually changes customer behavior.",
      benefits: [
        "Rewards That Fit Your Margins: Choose points or cashback based on what your business can sustain",
        "Recognition for Your Best Customers: Tiered rewards give frequent shoppers more than a one-time visitor",
        "Rules Customers Actually Understand: Simple, clear triggers instead of confusing fine print",
        "Controlled Liability: Expiry rules keep unused rewards from becoming an open-ended cost",
        "Built-In Growth: Referral mechanics turn loyal customers into a source of new ones",
        "Flexibility to Redesign: Adjust points, tiers, or triggers as your business changes",
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Stores Across India",
      subHeading:
        "Retail businesses use Fydo to run loyalty programs and increase repeat customers.",
      testimonials: [
        {
          id: 1,
          quote:
            "Our repeat customers increased after we started using Fydo's loyalty program. It's simple and very effective.",
          description:
            "Fydo makes it easy to run loyalty programs that actually work for everyday retail businesses.",
          author: "Retail Store Owner",
          rating: 4.8,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start Your Loyalty Program in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Quick and easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Your Loyalty Program",
          description: "Set rewards based on visits, spending, or occasions",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Bring Customers Back",
          description: "Start increasing repeat customers and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "Should my loyalty program use points or cashback?",
          answer:
            "It depends on your margins and customers - points work well for building longer-term engagement, while cashback feels more immediate. Fydo lets you choose either.",
        },
        {
          question: "How do tiered rewards work?",
          answer:
            "Customers move into higher tiers, like bronze, silver, or gold, based on spend or visit frequency, unlocking better rewards as they qualify.",
        },
        {
          question:
            "Can I set different rules for new versus repeat customers?",
          answer:
            "Yes. You can create separate reward triggers for first-time visitors, repeat customers, and inactive customers within the same program.",
        },
        {
          question: "Can rewards expire if unused?",
          answer:
            "Yes. You can set expiry periods and redemption limits so unused points or cashback don't sit as an open-ended cost to your business.",
        },
        {
          question: "Does Fydo support referral-based rewards?",
          answer:
            "Yes. You can reward existing customers for referring new ones as part of your loyalty program's rules.",
        },
        {
          question: "Can I create occasion-based rules, like birthday offers?",
          answer:
            "Yes. You can set rewards to trigger automatically on birthdays, festivals, or other occasions you define.",
        },
        {
          question: "Can I change my loyalty program's rules after launch?",
          answer:
            "Yes. You can adjust points, tiers, triggers, or expiry rules at any time as your business and customer base change.",
        },
      ],
    },
    signupSection: {
      badgeText: "Loyalty Program",
      title: "Start Your Loyalty Program and Grow Your Retail Business Today",
      subtitle:
        "Join thousands of retail stores using Fydo to increase repeat customers and build customer loyalty.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "loyalty-program-software-for-retail": {
    slug: "loyalty-program-software-for-retail",
    cluster: "rewards-platform",
    // Angle: the SOFTWARE/tech capabilities themselves - dashboard, automation,
    // integrations, reporting.
    seo: {
      title: "Loyalty Program Software With Dashboard & Automation",
      description:
        "Fydo is loyalty program software with a real-time dashboard, automation rules, exportable reports, and role-based staff access - works with or without a POS.",
    },
    heroSection: {
      badgeText: "Loyalty Program Software",
      title:
        "Loyalty Program Software Built on a Dashboard, Automation, and Reports",
      gradientSubHeading: "Running a loyalty program by hand",
      baseSubHeading: "doesn't scale past your first few customers.",
      microSubHeading:
        "A loyalty program is only as good as the software running it. Fydo gives you a real-time dashboard to see performance at a glance, automation rules that trigger rewards and campaigns without manual work, and reports you can export whenever you need them - standalone or connected to your POS.",
      supportingText:
        "Simple app. No complicated setup - works with your existing system or even if you don’t use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Capabilities Should Loyalty Program Software Actually Have?
    whatIsSection: {
      badgeText: "Software Capabilities",
      heading:
        "What Capabilities Should Loyalty Program Software Actually Have?",
      description:
        "Running a loyalty program manually works for a handful of customers, but breaks down as your customer base grows. The software behind it needs a dashboard that shows performance without manual tallying, an automation engine that triggers rewards and campaigns on its own, and reporting you can export and act on - whether or not it's connected to a POS. Fydo is built around these capabilities rather than treating them as add-ons.",
      steps: [],
      bottomLine:
        "A loyalty program is only as capable as the software running behind it.",
    },
    // H2 - What's Missing From Most Loyalty Program Software
    problemSection: {
      badgeText: "Problem",
      heading: "What's Missing From Most Loyalty Program Software",
      subHeading:
        "Many loyalty tools handle the basics but fall short on the software capabilities that matter as you grow:",
      points: [
        "No dashboard to see loyalty performance at a glance",
        "Rewards have to be applied manually for every single customer",
        "No integration with your POS or payment system",
        "No way to export customer or campaign data for deeper analysis",
        "Every staff member can see and edit everything - no access control",
        "No automation to trigger offers after inactivity or a spend threshold",
        "No API or webhook support to connect loyalty data to other business tools",
      ],
      endingLine:
        "Without these capabilities, a loyalty program stays a manual side task instead of a system that runs itself.",
    },
    // H2 - The Software Capabilities Behind Every Fydo Loyalty Program (each item below renders as an H3)
    featureSection: {
      heading: "The Software Capabilities Behind Every Fydo Loyalty Program",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Real-Time", gradient: false },
          titleGrad: { text: "Analytics Dashboard", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "See repeat customers, campaign performance, and rewards activity in one dashboard, updated as it happens instead of tallied by hand.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [
            "Live view of customer and campaign performance",
            "No manual tallying or spreadsheet updates",
            "One place to check how the program is performing",
          ],
        },
        {
          id: 2,
          titleStart: { text: "Automation Rules", gradient: false },
          titleGrad: { text: "That Trigger Themselves", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Set a rule once - like sending an offer after 30 days of inactivity or a spend threshold - and let it fire automatically without manual follow-up.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [
            "Trigger-based rewards and campaigns",
            "No manual follow-up needed for routine actions",
            "Rules keep running in the background",
          ],
        },
        {
          id: 3,
          titleStart: { text: "Works With or", gradient: false },
          titleGrad: { text: "Without a POS", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Use Fydo standalone with no POS at all, or on the Enterprise plan get a custom integration built for your existing POS or billing setup.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [
            "No POS required to get started",
            "Works alongside however you already bill customers",
            "Custom POS/billing integration available on Enterprise",
          ],
        },
        {
          id: 4,
          titleStart: { text: "Exportable", gradient: false },
          titleGrad: { text: "Reports", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Export customer and campaign data as CSV or PDF whenever you need to analyze it further or share it outside the app.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [
            "Export data in CSV or PDF formats",
            "Analyze performance outside the app if needed",
            "Share reports with partners or accountants easily",
          ],
        },
        {
          id: 5,
          titleStart: { text: "Role-Based", gradient: false },
          titleGrad: { text: "Staff Access", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Control what each staff member can see or edit, so sensitive customer and business data isn't open to everyone by default.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [
            "Permission levels for different staff roles",
            "Limit access to sensitive data by default",
            "Reduce risk of accidental changes",
          ],
        },
        {
          id: 6,
          titleStart: { text: "Custom Integrations on", gradient: false },
          titleGrad: { text: "Enterprise", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Need loyalty data connected to your ecommerce platform or other business tools? Enterprise plans include dedicated engineering support to build exactly what you need.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [
            "Not locked into a single, closed system",
            "Dedicated engineering support on Enterprise",
            "Built around what your business actually uses",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Perfect for All Retail Businesses",
      subHeading:
        "Fydo helps different types of retail businesses run effective loyalty programs.",
      useCases: [
        {
          title: "Grocery Stores",
          detail: "Build daily repeat purchases",
        },
        {
          title: "Clothing Stores",
          detail: "Bring customers back for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat visits and bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat orders and loyalty",
        },
      ],
      endingLine:
        "No matter your business type, loyalty programs help you grow.",
    },
    // H2 - What Solid Software Underneath a Loyalty Program Gets You
    benefitsSection: {
      badgeText: "Benefits",
      heading: "What Solid Software Underneath a Loyalty Program Gets You",
      description:
        "The right software capabilities are what let a loyalty program run itself as you grow.",
      benefits: [
        "Instant Visibility: See loyalty performance on a dashboard instead of tallying it manually",
        "Less Manual Work: Automation rules apply rewards and trigger campaigns on their own",
        "Works However You Bill: No POS required, with custom POS/billing integration available on Enterprise",
        "Data You Can Act On: Export reports whenever you need to analyze deeper",
        "Controlled Access: Role-based permissions keep sensitive data limited to the right staff",
        "Room to Extend: Enterprise plans include dedicated engineering support for custom integrations",
      ],
    },
    customerTestimonialsSection: {
      heading: "Retail Businesses Are Growing with Fydo",
      subHeading:
        "Thousands of businesses use Fydo to run loyalty programs and increase repeat customers.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo made it easy to manage our loyalty program and bring customers back regularly.",
          description:
            "We set up a flexible loyalty program easily and started engaging our customers automatically. We now see more repeat visits and stronger retention.",
          author: "Retail Store Owner",
          rating: 5.0,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start Your Loyalty Program in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Quick and easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Your Loyalty Programs",
          description: "Set rewards based on visits, spending, or occasions",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Grow Your Business",
          description: "Start increasing repeat customers and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "Does Fydo have a dashboard to track loyalty performance?",
          answer:
            "Yes. Fydo includes a real-time dashboard showing customer activity, campaign performance, and rewards, without manual tallying.",
        },
        {
          question: "Can Fydo automate rewards without manual work?",
          answer:
            "Yes. You can set automation rules that trigger rewards or campaigns based on visits, spend, or inactivity, and they run without manual follow-up.",
        },
        {
          question: "Does Fydo integrate with my POS or payment system?",
          answer:
            "Fydo works entirely on its own with no POS required. If you want it connected to an existing POS or billing setup, that's available as a custom integration on the Enterprise plan.",
        },
        {
          question: "Can I export loyalty and customer reports?",
          answer:
            "Yes. Reports can be exported in CSV or PDF format whenever you need to analyze data outside the app.",
        },
        {
          question: "Can I control what different staff members can access?",
          answer:
            "Yes. Fydo supports role-based access so you can limit what each staff member can see or edit.",
        },
        {
          question: "Does Fydo support an API or webhooks?",
          answer:
            "Custom integrations with other business tools are available on the Enterprise plan, with dedicated engineering support to build what you need.",
        },
        {
          question: "Is Fydo scalable as my customer base grows?",
          answer:
            "Yes. The dashboard, automation, and reporting are built to keep working smoothly as your customer base and transaction volume increase.",
        },
      ],
    },
    signupSection: {
      badgeText: "Loyalty Program Software",
      title:
        "Start Building Customer Loyalty and Growing Your Retail Business Today",
      subtitle:
        "Don’t let customers visit once and leave. Build loyalty and grow your business with Fydo.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "loyalty-software-for-shop-owners": {
    slug: "loyalty-software-for-shop-owners",
    cluster: "rewards-platform",
    // Angle: the day-to-day owner/operator perspective - ease of setup, running
    // it themselves without IT staff, and time saved.
    seo: {
      title: "Loyalty Software Shop Owners Can Set Up Themselves",
      description:
        "Fydo is loyalty software built for shop owners, not IT departments - set it up yourself in minutes, no manual, no staff training, no tech support.",
    },
    heroSection: {
      badgeText: "Loyalty Software for Owners",
      title: "Loyalty Software You Set Up Yourself - No IT Team, No Manual",
      gradientSubHeading: "You're the owner, the cashier, and the marketer",
      baseSubHeading: "you don't have time to learn complicated software.",
      microSubHeading:
        "Fydo is built for shop owners who run everything themselves, not enterprise IT departments. Set up your loyalty program in minutes on your own phone, without a demo call, a manual, or a dedicated tech person on staff.",
      supportingText:
        "No demo call required. No IT background needed. Set up between customers.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Does 'Easy to Use' Actually Mean for a Shop Owner?
    whatIsSection: {
      badgeText: "Built for Owners",
      heading: "What Does 'Easy to Use' Actually Mean for a Shop Owner?",
      description:
        "Most loyalty software is designed and sold to marketing or IT teams, with dashboards and settings that assume someone technical is running it full-time. For a shop owner who's also the cashier and the marketer, that's a barrier, not a feature. Fydo strips setup down to what an owner can actually do alone, on a phone, in the minutes between customers - no manuals, no support tickets, no dedicated staff required.",
      steps: [],
      bottomLine:
        "If you can use WhatsApp, you can run your loyalty program on Fydo.",
    },
    // H2 - Why Loyalty Software Feels Out of Reach for Most Shop Owners
    problemSection: {
      badgeText: "Problem",
      heading: "Why Loyalty Software Feels Out of Reach for Most Shop Owners",
      subHeading:
        "Most loyalty tools are built for teams with time and technical staff to spare, not for an owner running the shop solo:",
      points: [
        "You're the owner, cashier, and marketer - there's no time to learn new software",
        "Most loyalty tools are built for IT teams, not solo shop owners",
        "Training staff on a complicated dashboard eats into busy selling hours",
        "There's no time to build reports or read spreadsheets between customers",
        "Getting started often means booking a demo call just to understand the tool",
        "Any downtime spent learning software is time not spent with customers",
        "Complicated settings mean the loyalty program never actually gets switched on",
      ],
      endingLine:
        "If setting up your loyalty program takes longer than running your shop for a day, it's already too complicated.",
    },
    // H2 - Built to Be Run by One Person - You (each item below renders as an H3)
    featureSection: {
      heading: "Built to Be Run by One Person - You",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Set Up in Minutes, Not", gradient: false },
          titleGrad: { text: "a Training Session", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "A guided setup walks you through creating your first loyalty program without needing a demo call, a manual, or someone else to do it for you.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [
            "Guided setup you can finish yourself",
            "No demo call required to get started",
            "Ready to use the same day you download it",
          ],
        },
        {
          id: 2,
          titleStart: { text: "No Staff", gradient: false },
          titleGrad: { text: "Training Required", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Reward redemption at the counter is simple enough that any staff member can use it correctly from their very first day, with no training session needed.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [
            "One-tap reward redemption at checkout",
            "No onboarding session needed for new staff",
            "Works the same way every time, for every cashier",
          ],
        },
        {
          id: 3,
          titleStart: { text: "Runs on the Phone", gradient: false },
          titleGrad: { text: "You Already Carry", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "There's no new hardware, dedicated computer, or extra device to buy or maintain - your loyalty program runs on the phone that's already in your pocket.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [
            "No new hardware or device purchase needed",
            "Works on the phone you already use daily",
            "Nothing extra to maintain or charge",
          ],
        },
        {
          id: 4,
          titleStart: { text: "Set Campaigns Once,", gradient: false },
          titleGrad: { text: "They Run on Their Own", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Configure a WhatsApp offer or reward rule once, and let it keep running in the background instead of sending messages manually every day.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [
            "Campaigns keep running without daily manual work",
            "Set a rule once instead of repeating it weekly",
            "Frees up time for actually running the shop",
          ],
        },
        {
          id: 5,
          titleStart: { text: "No Manual, No", gradient: false },
          titleGrad: { text: "Support Ticket Needed", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "The app is designed to be understood without documentation, so you're not stuck waiting on a support ticket just to figure out a basic setting.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [
            "Self-explanatory screens, no manual required",
            "No waiting on support tickets for basic tasks",
            "Built for someone learning it for the first time alone",
          ],
        },
        {
          id: 6,
          titleStart: { text: "One Screen to See", gradient: false },
          titleGrad: { text: "How Your Shop Is Doing", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "A simple, owner-level view of repeat customers and rewards activity, without enterprise-style analytics that need a data team to interpret.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [
            "A simple summary, not an analytics overload",
            "Understand performance without training",
            "See what matters without digging through menus",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Perfect for All Types of Shops",
      subHeading:
        "Fydo is designed for shop owners across different industries who want to increase repeat customers and grow their business.",
      useCases: [
        {
          title: "Grocery Shops",
          detail: "Increase daily repeat customers",
        },
        {
          title: "Clothing Stores",
          detail: "Bring customers back for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat visits and bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat orders and customer loyalty",
        },
      ],
      endingLine:
        "No matter your business type, Fydo helps you grow consistently.",
    },
    // H2 - What Shop Owners Get Back by Switching to Fydo
    benefitsSection: {
      badgeText: "Benefits",
      heading: "What Shop Owners Get Back by Switching to Fydo",
      description:
        "Fydo is built to save the one resource shop owners have the least of - time.",
      benefits: [
        "Time Back: Spend minutes on setup instead of hours in training or demo calls",
        "No Hiring Needed: Run a full loyalty program without hiring tech support or a marketing person",
        "Staff Can Use It Immediately: Reward redemption is simple enough for any staff member on day one",
        "Campaigns Run Themselves: Set offers once and let automation handle the rest of the week",
        "Everything Fits on Your Phone: No new hardware or dedicated computer required",
        "Confidence Without Complexity: A clear, simple view of your shop's loyalty performance",
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Shop Owners Across India",
      subHeading:
        "Thousands of shop owners are already using Fydo to increase repeat customers and grow their business.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo made it easy for us to bring customers back. We’ve seen a clear increase in repeat visits and sales.",
          description:
            "We set up a flexible loyalty program easily and started engaging our customers automatically. We now see more repeat visits and stronger retention.",
          author: "Shop Owner",
          rating: 5.0,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start Growing Your Shop in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started in minutes with an easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Your Loyalty Offers",
          description: "Set rewards based on visits, spending, or special days",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Bring Customers Back",
          description:
            "Start increasing repeat customers and growing your sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "Do I need any technical knowledge to use Fydo?",
          answer:
            "No. Fydo is designed for shop owners without a technical background, with a guided setup you can complete on your own.",
        },
        {
          question: "How long does it take to set up Fydo?",
          answer:
            "Most shop owners can create their first loyalty program in a few minutes, without a demo call or reading a manual first.",
        },
        {
          question: "Can my staff use Fydo without training?",
          answer:
            "Yes. Reward redemption at the counter is simple enough that staff can use it correctly from their first shift, with no separate training session.",
        },
        {
          question: "Do I need to book a demo before I can start?",
          answer:
            "No. You can download the app and set up your loyalty program yourself, though a demo is available if you'd like one.",
        },
        {
          question: "Does Fydo require new hardware or a dedicated device?",
          answer:
            "No. Fydo runs on the phone you already use, so there's nothing extra to buy or maintain.",
        },
        {
          question: "Can I run Fydo entirely from my phone?",
          answer:
            "Yes. Setup, campaigns, and reward redemption can all be managed from your phone without needing a computer.",
        },
        {
          question: "What if I get stuck while setting it up?",
          answer:
            "Fydo is designed to be self-explanatory, and a demo or callback is available any time you'd like extra help.",
        },
      ],
    },
    signupSection: {
      badgeText: "Loyalty Software",
      title: "Start Increasing Repeat Customers for Your Shop Today",
      subtitle:
        "Join thousands of shop owners who are already growing their business with Fydo.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "retail-customer-engagement-platform": {
    slug: "retail-customer-engagement-platform",
    cluster: "engagement-marketing",
    // Angle: the communication/engagement channels themselves - multi-channel
    // (WhatsApp + app notifications + in-store), staying "top of mind."
    seo: {
      title: "Multi-Channel Customer Engagement Platform for Retail",
      description:
        "Fydo keeps your brand top of mind across WhatsApp, app notifications, and in-store reminders - so customers engage on whichever channel they use.",
    },
    heroSection: {
      badgeText: "Multi-Channel Engagement",
      title:
        "Stay Top of Mind Across WhatsApp, App, and In-Store - Not Just One Channel",
      gradientSubHeading: "One channel reaches some customers",
      baseSubHeading: "three channels reach almost all of them.",
      microSubHeading:
        "Some customers check WhatsApp daily but never open an app. Others respond to a reminder at the billing counter more than any message. Fydo combines WhatsApp campaigns, app push notifications, and in-store reward prompts into one engagement strategy, so you're not relying on a single channel that only reaches part of your customers.",
      supportingText:
        "Simple app. No complicated setup, works with your existing POS system or even if you don't use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Does 'Multi-Channel' Engagement Actually Mean?
    whatIsSection: {
      badgeText: "Multi-Channel Engagement",
      heading: "What Does 'Multi-Channel' Engagement Actually Mean?",
      description:
        "Multi-channel engagement means reaching customers through more than one touchpoint instead of betting everything on a single message type. Fydo combines WhatsApp campaigns for direct outreach, in-app push notifications for customers who've installed the app, and in-store prompts at the billing counter for reminders that land in the moment of purchase. Each channel covers a gap the others leave open.",
      steps: [],
      bottomLine:
        "No single channel reaches every customer - together, they cover far more ground.",
    },
    // H2 - Why Single-Channel Engagement Leaves Customers Behind
    problemSection: {
      badgeText: "Problem",
      heading: "Why Single-Channel Engagement Leaves Customers Behind",
      subHeading:
        "Relying on just one way to reach customers means missing everyone who prefers another:",
      points: [
        "Customers check WhatsApp daily but often ignore SMS or email entirely",
        "An app notification alone won't reach customers who rarely open the app",
        "In-store signage gets seen once at checkout and forgotten by the next visit",
        "Relying on a single channel means missing customers who prefer another",
        "Messaging across WhatsApp, app, and in-store separately with no coordination wastes time",
        "Inconsistent messaging across channels makes your brand feel disorganized",
        "No visibility into which channel actually drives a customer back",
      ],
      endingLine:
        "Customer attention is split across channels - your engagement strategy needs to be too.",
    },
    // H2 - Three Channels, One Coordinated Engagement Strategy (each item below renders as an H3)
    featureSection: {
      heading: "Three Channels, One Coordinated Engagement Strategy",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "WhatsApp Campaigns", gradient: false },
          titleGrad: { text: "That Reach Customers Directly", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Send offers and updates straight to the app customers already check daily, instead of hoping they notice an email or SMS.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [
            "Direct outreach on the channel customers check most",
            "No dependency on the customer opening an app",
            "Works for both new and existing customers",
          ],
        },
        {
          id: 2,
          titleStart: { text: "App Push Notifications", gradient: false },
          titleGrad: {
            text: "For Customers Who've Installed Fydo",
            gradient: true,
          },
          titleEnd: { text: "", gradient: false },
          description:
            "Reach app users with timely nudges about rewards and offers, adding a second touchpoint on top of WhatsApp for customers who use the app.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [
            "A second touchpoint for app-installed customers",
            "Timely nudges about rewards and offers",
            "Complements, rather than replaces, WhatsApp outreach",
          ],
        },
        {
          id: 3,
          titleStart: { text: "In-Store Prompts", gradient: false },
          titleGrad: { text: "At the Billing Counter", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Remind customers of their rewards balance or an active offer at the exact moment of purchase, when it's most likely to influence what they do next.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [
            "Reminders that land at the moment of purchase",
            "No reliance on the customer remembering later",
            "Works even for customers who ignore digital messages",
          ],
        },
        {
          id: 4,
          titleStart: { text: "Cross-Channel", gradient: false },
          titleGrad: { text: "Campaign Scheduling", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Plan a single campaign that runs across WhatsApp, app notifications, and in-store prompts at once, instead of setting up three separate ones.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [
            "One campaign, scheduled across every channel",
            "No repeating the same setup three times",
            "Consistent messaging across every touchpoint",
          ],
        },
        {
          id: 5,
          titleStart: { text: "Channel-Level", gradient: false },
          titleGrad: { text: "Engagement Tracking", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "See which channel actually brings a customer back, instead of guessing whether WhatsApp, app notifications, or in-store prompts are working.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [
            "Performance visibility broken down by channel",
            "See what's working instead of guessing",
            "Adjust effort toward what actually drives visits",
          ],
        },
        {
          id: 6,
          titleStart: { text: "One Timeline", gradient: false },
          titleGrad: { text: "Across Every Touchpoint", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "View a customer's engagement history across WhatsApp, app, and in-store activity in one place, instead of piecing it together from three systems.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [
            "A single history per customer, across all channels",
            "No cross-referencing separate tools",
            "Full context before you reach out again",
          ],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Built for Every Retail Business",
      subHeading:
        "Fydo helps different types of retail businesses improve customer engagement and increase repeat customers.",
      useCases: [
        {
          title: "Grocery Stores",
          detail: "Build daily engagement and repeat visits",
        },
        {
          title: "Clothing Stores",
          detail: "Keep customers engaged with new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat bookings and loyalty",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat visits and customer interaction",
        },
      ],
      endingLine:
        "No matter your business type, Fydo helps you stay connected with your customers.",
    },
    // H2 - What a Multi-Channel Strategy Gets You
    benefitsSection: {
      badgeText: "Benefits",
      heading: "What a Multi-Channel Strategy Gets You",
      description:
        "Combining channels changes how much of your customer base you can actually reach.",
      benefits: [
        "Wider Reach: Engage customers who prefer WhatsApp, app notifications, or in-store reminders",
        "Consistent Brand Presence: The same offer shows up wherever the customer actually looks",
        "Better Timing: In-store prompts catch customers at the exact moment of purchase",
        "Less Manual Work: Schedule one campaign across all channels instead of three separate ones",
        "Clearer Insights: See which channel actually drives repeat visits, not just guesses",
        "Stronger Recall: Customers who see you across channels are less likely to forget your shop",
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Retailers use Fydo to improve customer engagement, increase repeat customers, and grow their business.",
      testimonials: [
        {
          id: 1,
          quote:
            "Fydo helped us stay connected with our customers. We now see more repeat visits and better engagement.",
          description:
            "We easily set up campaigns to reach out to our customers consistently. Our communication is much more structured now, leading to noticeably stronger loyalty.",
          author: "Retail Store Owner",
          rating: 5.0,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Engage Customers and Grow Your Business in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started quickly with an easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Offers and Campaigns",
          description: "Set up loyalty programs and marketing campaigns",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage and Bring Customers Back",
          description: "Start increasing engagement and repeat customers",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What channels does Fydo use to engage customers?",
          answer:
            "Fydo combines WhatsApp campaigns, in-app push notifications, and in-store prompts at the billing counter to reach customers across multiple touchpoints.",
        },
        {
          question: "Do customers need the app installed to receive offers?",
          answer:
            "No. Offers can be sent over WhatsApp, so customers without the app installed can still receive and act on them.",
        },
        {
          question:
            "Can I run the same campaign across WhatsApp, app, and in-store at once?",
          answer:
            "Yes. Fydo lets you schedule one campaign that runs across all three channels instead of setting each one up separately.",
        },
        {
          question: "How do in-store prompts work at the billing counter?",
          answer:
            "At checkout, staff can see a customer's rewards balance or an active offer, so it can be mentioned at the exact moment of purchase.",
        },
        {
          question: "Can I see which channel brings back the most customers?",
          answer:
            "Yes. Fydo tracks engagement at the channel level so you can see whether WhatsApp, app notifications, or in-store prompts drive more repeat visits.",
        },
        {
          question: "Is multi-channel engagement more work to manage?",
          answer:
            "No. Fydo is designed so one campaign covers all channels at once, rather than requiring separate setup for each.",
        },
        {
          question: "Does Fydo work if I only want to use WhatsApp for now?",
          answer:
            "Yes. You can start with WhatsApp alone and add app notifications or in-store prompts later as your engagement strategy grows.",
        },
      ],
    },
    signupSection: {
      badgeText: "Customer Engagement",
      title: "Start Engaging Your Customers and Growing Your Business Today",
      subtitle:
        "Join thousands of retail businesses using Fydo to improve customer engagement and increase repeat sales.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "whatsapp-marketing-for-retail-business": {
    slug: "whatsapp-marketing-for-retail-business",
    cluster: "engagement-marketing",
    // Title/description are deliberately distinct from heroSection.title (the
    // H1) - a concise, front-loaded SERP title and a benefit-led meta
    // description read better in search results than a literal H1 match.
    seo: {
      title: "WhatsApp Marketing Software for Retail Shops",
      description:
        "Send WhatsApp broadcast offers, automated reminders, and personalized messages to bring customers back. Free WhatsApp marketing tool for retail shops.",
    },
    heroSection: {
      badgeText: "WhatsApp Marketing",
      title: "Send WhatsApp Campaigns That Bring Customers Back to Your Shop",
      gradientSubHeading: "Struggling to bring customers back",
      baseSubHeading: "to your shop?",
      microSubHeading:
        "Fydo lets retail businesses send WhatsApp broadcast offers, automated visit reminders, and personalized messages to customers who haven't returned. Share catalogs, images, and offers directly in a chat customers already open every day, and see who opens, replies, and redeems.",
      supportingText:
        "Simple app. No complicated setup, works with your existing system or even if you don’t use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Is WhatsApp Marketing for Retail Businesses?
    whatIsSection: {
      badgeText: "WhatsApp Marketing",
      heading: "What Is WhatsApp Marketing for Retail Businesses?",
      description:
        "WhatsApp marketing is the practice of sending broadcast offers, order updates, and personalized messages to customers directly on WhatsApp instead of relying on SMS, ads, or hoping customers remember to come back on their own. Because customers already check WhatsApp multiple times a day, messages get seen and acted on far more often than a text blast or a flyer ever would.",
      steps: [],
      bottomLine:
        "Fydo turns your customer list into a WhatsApp channel: create a broadcast campaign, send offers, catalogs, and reminders to segmented customer groups, and track who opened, clicked, and returned to redeem - all from one app.",
    },
    // H2 - Why Does Your Business Need WhatsApp Marketing?
    problemSection: {
      badgeText: "Why It Matters",
      heading: "Why Does Your Business Need WhatsApp Marketing?",
      subHeading:
        "Most shop owners know WhatsApp is powerful but have no structured way to use it for marketing. Without a system in place:",
      points: [
        "No structured way to send offers on WhatsApp",
        "Customers forget your shop after their visit",
        "Offers get sent once and never followed up",
        "No reminders for customers who haven't returned",
        "No way to send images, catalogs, or rich offers",
        "No tracking of who opened or replied to a message",
        "Festival and sale offers reach only a few customers",
        "No way to know which messages actually drive visits",
      ],
      endingLine:
        "Without a proper WhatsApp marketing system, customers don't return - even if they liked your shop the first time.",
    },
    // H2 - Features Built for WhatsApp Marketing in Retail (each item below renders as an H3)
    featureSection: {
      heading: "Features Built for WhatsApp Marketing in Retail",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "WhatsApp Broadcast", gradient: false },
          titleGrad: { text: "Campaigns", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Send one offer to hundreds of customers at once on WhatsApp. Announce sales, new arrivals, and festival offers without spending on ads.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 2,
          titleStart: { text: "Automated Visit", gradient: false },
          titleGrad: { text: "Reminders", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Automatically message customers who haven't visited in a while, so they're reminded of your shop without you having to message them one by one.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 3,
          titleStart: { text: "Personalized Offer", gradient: false },
          titleGrad: { text: "Messages", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Send different WhatsApp messages to different customer groups based on their past visits and spending, instead of one generic offer for everyone.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [],
        },
        {
          id: 4,
          titleStart: { text: "Opt-In and", gradient: false },
          titleGrad: { text: "Consent Management", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Collect customer consent at checkout so every WhatsApp message you send is welcomed, not marked as spam.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 5,
          titleStart: { text: "Rich Media:", gradient: false },
          titleGrad: { text: "Images and Catalogs", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Share product photos, offer banners, and catalogs directly in WhatsApp so customers can see exactly what you're promoting.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 6,
          titleStart: { text: "Two-Way Replies", gradient: false },
          titleGrad: { text: "and Tracking", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "See who opened your message, who replied, and which campaigns actually brought customers back into your shop.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Built for Every Type of Retail Business",
      subHeading:
        "Fydo helps retail businesses use WhatsApp marketing effectively to increase customer engagement and repeat visits.",
      useCases: [
        {
          title: "Grocery Stores",
          detail: "Share daily offers and bring customers back regularly",
        },
        {
          title: "Clothing Stores",
          detail: "Promote new arrivals and collections",
        },
        {
          title: "Salons & Services",
          detail: "Send reminders and offers to increase bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Share deals and bring customers back",
        },
      ],
      endingLine:
        "No matter your business type, WhatsApp marketing helps you stay connected.",
    },
    // H2 - Benefits of WhatsApp Marketing for Retail Shops
    benefitsSection: {
      badgeText: "Benefits",
      heading: "Benefits of WhatsApp Marketing for Retail Shops",
      description:
        "Turn a chat app your customers already use every day into your most reliable repeat-customer channel.",
      benefits: [
        "Higher Open Rates Than SMS or Email: WhatsApp messages get seen because customers check the app multiple times a day",
        "No Ad Spend Required: Reach existing customers directly without paying for every impression",
        "Automated Reminders: Bring back inactive customers without manually messaging them one by one",
        "Personalized Offers: Send different messages to different customer groups instead of one blast for everyone",
        "Rich Media Sharing: Show off products, catalogs, and offer banners instead of plain text",
        "Measurable Results: Track opens, replies, and redemptions to see which campaigns actually drive visits",
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Retail businesses use Fydo to run WhatsApp marketing campaigns and increase repeat customers.",
      testimonials: [
        {
          id: 1,
          quote:
            "WhatsApp marketing with Fydo helped us stay connected with customers and bring them back regularly.",
          description:
            "We easily set up campaigns and started reaching out to our customers on WhatsApp. We now see more repeat visits and stronger customer engagement.",
          author: "Retail Store Owner",
          rating: 5.0,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start WhatsApp Marketing in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started quickly with an easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Your Campaigns",
          description: "Set up offers and promotions",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Share and Grow",
          description:
            "Send campaigns on WhatsApp and start increasing repeat customers",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is WhatsApp marketing for retail businesses?",
          answer:
            "WhatsApp marketing is sending broadcast offers, reminders, and personalized messages to customers directly on WhatsApp instead of relying on SMS, ads, or in-store signage alone.",
        },
        {
          question:
            "Do customers need to save my number to receive WhatsApp messages?",
          answer:
            "Customers typically opt in when you collect their number at checkout or sign-up, which lets you message them without appearing as spam.",
        },
        {
          question:
            "Can I send images, offers, and catalogs on WhatsApp through Fydo?",
          answer:
            "Yes. Fydo lets you send rich media, including product photos, offer banners, and catalogs, alongside your text message.",
        },
        {
          question: "How is WhatsApp marketing different from sending SMS?",
          answer:
            "WhatsApp messages get opened far more often than SMS because customers already check the app several times a day, and you can include images, offers, and replies that SMS doesn't support.",
        },
        {
          question:
            "Can I send different WhatsApp messages to different customers?",
          answer:
            "Yes, Fydo lets you segment customers by visit history and spending so you can send personalized offers instead of one message to everyone.",
        },
        {
          question:
            "Can I track who opened or responded to my WhatsApp campaign?",
          answer:
            "Yes, Fydo tracks opens, replies, and redemptions so you can see which WhatsApp campaigns actually bring customers back.",
        },
        {
          question: "Is WhatsApp marketing effective for small retail shops?",
          answer:
            "Yes, it is one of the most effective and affordable ways for small retail businesses to stay in touch with customers and increase repeat visits.",
        },
      ],
    },
    signupSection: {
      badgeText: "WhatsApp Marketing",
      title: "Start Using WhatsApp Marketing to Grow Your Business Today",
      subtitle:
        "Join thousands of retail businesses using Fydo to engage customers and increase repeat sales.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: ["whatsapp"],
  },

  "how-to-bring-customers-back-to-shop": {
    slug: "how-to-bring-customers-back-to-shop",
    cluster: "retention-platform",
    // Title/description are deliberately distinct from heroSection.title (the
    // H1) - a concise, front-loaded SERP title and a benefit-led meta
    // description read better in search results than a literal H1 match.
    seo: {
      title: "How to Win Back Inactive Customers to Your Shop",
      description:
        "Struggling with customers who've stopped visiting? Learn proven win-back tactics, and how Fydo automates re-engagement offers and WhatsApp nudges.",
    },
    heroSection: {
      badgeText: "Customer Win-Back",
      title: "How to Bring Customers Back to Your Shop",
      gradientSubHeading: "Customers stopped visiting",
      baseSubHeading: "and you don't know why?",
      microSubHeading:
        "Some customers don't leave for a dramatic reason - they simply stop thinking about your shop. Winning them back takes a deliberate nudge: the right offer, at the right time, sent the right way. Here's how retail businesses re-engage customers who've gone quiet, and how Fydo helps automate it.",
      supportingText:
        "No complicated setup. Works with your existing system or even if you don't use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Causes Customers to Stop Returning?
    whatIsSection: {
      badgeText: "Customer Win-Back",
      heading: "What Causes Customers to Stop Returning?",
      description:
        "Customers rarely announce that they're leaving - they just stop showing up. Usually it's one of a few things: they forgot how long it had been, a competitor made it easier to switch, a single bad experience went unaddressed, or there was simply no reason to make the trip back. Identifying which one applies determines what kind of win-back approach will actually work.",
      steps: [],
      bottomLine:
        "Fydo helps you spot customers who've gone quiet and automatically reach out with the kind of offer that gives them a reason to return.",
    },
    // H2 - Why Do Customers Stop Visiting Your Shop?
    problemSection: {
      badgeText: "Problem",
      heading: "Why Do Customers Stop Visiting Your Shop?",
      subHeading:
        "Before you can win a customer back, it helps to know why they stopped coming. The most common reasons include:",
      points: [
        "They found a closer or cheaper alternative",
        "They simply lost track of how long it had been since their last visit",
        "No one reached out after they stopped coming",
        "A product or service they wanted wasn't available",
        "A single bad experience went unaddressed",
        "There was no incentive that made the trip back worthwhile",
        "Their routine changed and your shop wasn't part of the new one",
        "You have no way of knowing who's actually gone inactive",
      ],
      endingLine:
        "Most lapsed customers aren't gone for good - they're just waiting for a reason to come back, and someone has to give them one.",
    },
    // H2 - 6 Ways to Win Back Customers Who've Stopped Visiting (each item below renders as an H3)
    featureSection: {
      heading: "6 Ways to Win Back Customers Who've Stopped Visiting",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Identify Customers Who've", gradient: false },
          titleGrad: { text: "Gone Quiet", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "You can't win back customers you can't identify. Fydo flags customers who haven't visited within your chosen window.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 2,
          titleStart: { text: "Send a Win-Back Offer at", gradient: false },
          titleGrad: { text: "the Right Moment", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Timing matters - an offer sent too late feels irrelevant. Fydo automates offers as soon as a customer crosses the inactive threshold.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 3,
          titleStart: { text: "Remind Them", gradient: false },
          titleGrad: {
            text: "Before They Forget You Completely",
            gradient: true,
          },
          titleEnd: { text: "", gradient: false },
          description:
            "A simple WhatsApp nudge is often enough to bring a lapsed customer back. Fydo sends these automatically.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 4,
          titleStart: { text: "Give Lapsed Customers an", gradient: false },
          titleGrad: { text: "Exclusive Reason to Return", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "A win-back offer should feel different from a regular promotion. Fydo lets you create offers just for inactive customers.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
        {
          id: 5,
          titleStart: { text: "Make the Return", gradient: false },
          titleGrad: { text: "Easy and Rewarding", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Once they're back, reward the visit so it doesn't become a one-time comeback. Fydo's loyalty rewards carry the relationship forward.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 6,
          titleStart: { text: "Measure Who", gradient: false },
          titleGrad: { text: "Actually Came Back", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Track win-back campaign performance to see which offers actually bring lapsed customers back, not just who received them.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Works for Every Type of Shop",
      subHeading:
        "These strategies help all types of retail businesses bring customers back.",
      useCases: [
        {
          title: "Grocery Stores",
          detail: "Encourage regular daily visits",
        },
        {
          title: "Clothing Stores",
          detail: "Bring customers back for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat visits and orders",
        },
      ],
      endingLine:
        "No matter your business, bringing customers back is key to growth.",
    },
    // H2 - What Happens When Customers Start Coming Back
    benefitsSection: {
      badgeText: "Benefits",
      heading: "What Happens When Customers Start Coming Back",
      description:
        "Winning back a lapsed customer is often cheaper and faster than acquiring a brand-new one.",
      benefits: [
        "Recovered Revenue: Lapsed customers who return add back sales you'd otherwise written off",
        "Lower Cost Than New Acquisition: Winning back a known customer costs less than acquiring a new one",
        "Fewer Silent Losses: Tracking inactivity means fewer customers slip away unnoticed",
        "Stronger Long-Term Retention: A customer who returns after lapsing often becomes more loyal",
        "Better Understanding of Churn: Win-back campaigns reveal why customers were leaving in the first place",
        "More Consistent Repeat Business: Regularly winning back lapsed customers smooths out revenue dips",
      ],
    },
    customerTestimonialsSection: {
      heading: "Shop Owners Are Already Bringing Customers Back with Fydo",
      subHeading:
        "Retail businesses use Fydo to stay connected with customers and increase repeat visits.",
      testimonials: [
        {
          id: 1,
          quote:
            "We started using Fydo to bring customers back, and we saw a clear increase in repeat visits.",
          description:
            "Fydo helps businesses run loyalty and marketing campaigns that keep customers engaged and coming back.",
          author: "Retail Store Owner",
          rating: 4.8,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Bring Customers Back in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started quickly with easy setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Offers and Campaigns",
          description: "Set rewards and marketing campaigns",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Bring Customers Back",
          description: "Start increasing repeat visits and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "How often should I run a win-back campaign?",
          answer:
            "Most retail businesses see good results running win-back campaigns monthly, targeting customers who've crossed a set period of inactivity.",
        },
        {
          question: "How long before a customer should be considered inactive?",
          answer:
            "This varies by business type, but 30-60 days without a visit is a common threshold for retail and service businesses to flag a customer as inactive.",
        },
        {
          question:
            "What kind of offer works best for winning back lapsed customers?",
          answer:
            "Offers that feel exclusive to returning customers, rather than a generic discount, tend to perform better at winning customers back.",
        },
        {
          question:
            "Is it worth trying to win back inactive customers, or better to focus on new ones?",
          answer:
            "Winning back a known customer is usually cheaper and faster than acquiring a new one, making it worth trying before shifting focus elsewhere.",
        },
        {
          question:
            "Can small shops run win-back campaigns without extra staff?",
          answer:
            "Yes, automated tools can identify inactive customers and send win-back offers without requiring manual tracking or extra staff.",
        },
        {
          question: "How does Fydo help bring customers back?",
          answer:
            "Fydo identifies customers who've gone inactive and automatically sends them targeted win-back offers through WhatsApp.",
        },
        {
          question:
            "Can Fydo tell me which win-back offers are actually working?",
          answer:
            "Yes, Fydo tracks campaign performance so you can see which win-back offers are actually bringing lapsed customers back.",
        },
      ],
    },
    signupSection: {
      badgeText: "Bring Customers Back",
      title: "Start Bringing Customers Back to Your Shop Today",
      subtitle:
        "Don't let customers visit once and forget your shop. Build loyalty and grow your business with Fydo.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "how-to-increase-repeat-customers": {
    slug: "how-to-increase-repeat-customers",
    cluster: "retention-platform",
    // Title/description are deliberately distinct from heroSection.title (the
    // H1) - a concise, front-loaded SERP title and a benefit-led meta
    // description read better in search results than a literal H1 match.
    seo: {
      title: "Proven Ways to Increase Repeat Customers in Retail",
      description:
        "Learn practical strategies to turn first-time buyers into repeat customers - loyalty rewards, WhatsApp follow-ups, and habits that keep shoppers coming back.",
    },
    heroSection: {
      badgeText: "Increase Repeat Customers",
      title: "How to Increase Repeat Customers for Your Retail Business",
      gradientSubHeading: "Customers visit once",
      baseSubHeading: "but don't come back?",
      microSubHeading:
        "Turning a one-time buyer into a repeat customer isn't about luck - it's about giving them a reason, and a reminder, to return. Here's what actually works for retail businesses, and how tools like Fydo make each strategy easier to run.",
      supportingText:
        "No complicated setup. Works with your existing system or even if you don't use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Actually Increases Repeat Customers?
    whatIsSection: {
      badgeText: "Repeat Customers",
      heading: "What Actually Increases Repeat Customers?",
      description:
        "Repeat purchases come down to four things: a reason to return, a reminder before they forget, an experience worth repeating, and a reward that makes coming back feel worthwhile. Businesses that consistently do all four see meaningfully more repeat customers than those relying on product quality alone.",
      steps: [],
      bottomLine:
        "Fydo helps retail businesses put this into practice - automated follow-ups, loyalty rewards, and repeat-visit tracking - so each strategy runs without manual effort.",
    },
    // H2 - Why Don't Customers Come Back After Their First Purchase?
    problemSection: {
      badgeText: "Problem",
      heading: "Why Don't Customers Come Back After Their First Purchase?",
      subHeading:
        "Before fixing the problem, it helps to understand why it happens. The most common reasons customers don't return include:",
      points: [
        "They forget your shop within days of their first visit",
        "Nothing reminded them that it was time to come back",
        "The first purchase was fine, but not memorable",
        "A discount attracted them once but gave no reason to return at full price",
        "Competitors stayed more visible after the sale",
        "There was no reward for choosing to come back",
        "They never felt any relationship with your shop beyond the transaction",
        "No one tracked whether they actually returned or not",
      ],
      endingLine:
        "Most of these are fixable - not with a bigger discount, but with a system that reminds, rewards, and tracks repeat behavior.",
    },
    // H2 - 6 Strategies to Increase Repeat Customers (each item below renders as an H3)
    featureSection: {
      heading: "6 Strategies to Increase Repeat Customers",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Reward Every Visit,", gradient: false },
          titleGrad: { text: "Not Just the First", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Give customers a reason to return every time, not just as a one-time welcome offer. Fydo's cashback and points programs make this automatic.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 2,
          titleStart: { text: "Follow Up", gradient: false },
          titleGrad: { text: "After Every Purchase", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "A simple message after the sale keeps your shop top of mind. Fydo sends WhatsApp follow-ups automatically so nothing falls through.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Give Customers a Reason to Return",
            gradient: false,
          },
          titleGrad: { text: "Soon, Not Someday", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Time-limited offers create urgency to come back within days, not eventually. Set these up as automatic campaigns in Fydo.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 4,
          titleStart: { text: "Personalize Offers Based on", gradient: false },
          titleGrad: { text: "Purchase History", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Different customers respond to different offers. Fydo segments customers by behavior so offers stay relevant instead of generic.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
        {
          id: 5,
          titleStart: { text: "Re-Engage Customers", gradient: false },
          titleGrad: { text: "Before They Go Inactive", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Catching a customer just before they drift away is easier than winning them back later. Fydo flags customers who haven't visited recently.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 6,
          titleStart: { text: "Track Who's", gradient: false },
          titleGrad: { text: "Actually Coming Back", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "You can't improve what you don't measure. Fydo tracks repeat visits so you know which strategies are working.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Works for Every Type of Retail Business",
      subHeading:
        "These strategies help all types of shops increase repeat customers.",
      useCases: [
        {
          title: "Grocery Stores",
          detail: "Build daily repeat buying habits",
        },
        {
          title: "Clothing Stores",
          detail: "Bring customers back for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase repeat visits and bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve repeat orders and visits",
        },
      ],
      endingLine:
        "No matter your business type, repeat customers drive consistent growth.",
    },
    // H2 - What Happens When You Increase Repeat Customers
    benefitsSection: {
      badgeText: "Benefits",
      heading: "What Happens When You Increase Repeat Customers",
      description:
        "Repeat customers cost less to serve, spend more over time, and make revenue more predictable.",
      benefits: [
        "Higher Revenue Without More Marketing Spend: Repeat purchases add revenue without paying for new customer acquisition",
        "Stronger Customer Relationships: Consistent engagement builds loyalty beyond a single transaction",
        "More Predictable Sales: Repeat customers create steadier, more forecastable revenue",
        "Lower Cost Per Sale: Selling to an existing customer costs less than acquiring a new one",
        "Better Word of Mouth: Loyal customers are more likely to recommend your shop",
        "Sustainable Long-Term Growth: A base of repeat customers compounds over time",
      ],
    },
    customerTestimonialsSection: {
      heading:
        "Retail Businesses Are Already Increasing Repeat Customers with Fydo",
      subHeading:
        "Shop owners use Fydo to bring customers back and grow their business consistently.",
      testimonials: [
        {
          id: 1,
          quote:
            "We started focusing on repeat customers using Fydo, and our sales improved without extra effort.",
          description:
            "Fydo made it easy for us to run loyalty programs and customer campaigns from one place. We now see more repeat visits and more stable sales every month.",
          author: "Retail Store Owner",
          rating: 4.8,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Start Increasing Repeat Customers in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started quickly with a simple setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Loyalty Programs and Campaigns",
          description: "Set rewards and offers for your customers",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Bring Customers Back",
          description: "Start increasing repeat visits and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question:
            "How long after a purchase should I follow up with a customer?",
          answer:
            "A follow-up within 2-3 days of purchase, while the experience is still fresh, tends to be most effective at prompting a return visit.",
        },
        {
          question:
            "How many repeat purchases does it take before a customer counts as loyal?",
          answer:
            "There's no fixed number, but most businesses consider a customer loyal after 3 or more repeat purchases within a few months.",
        },
        {
          question: "Do discounts actually increase repeat customers?",
          answer:
            "Discounts can prompt a single return, but ongoing rewards tied to repeat behavior are more effective at building lasting habits.",
        },
        {
          question:
            "What's the easiest way to increase repeat customers for a small shop?",
          answer:
            "Rewarding repeat visits and sending a simple follow-up message after each purchase are the easiest strategies to start with.",
        },
        {
          question: "How can I tell if my repeat customer rate is improving?",
          answer:
            "Tracking repeat visits over time - rather than just total sales - shows whether your retention strategies are working.",
        },
        {
          question: "How does Fydo help increase repeat customers?",
          answer:
            "Fydo automates loyalty rewards, WhatsApp follow-ups, and repeat-visit tracking so these strategies run without manual effort.",
        },
        {
          question: "Is Fydo suitable for small retail shops?",
          answer:
            "Yes, Fydo is built for retail businesses of any size that want to increase repeat customers without complex setup.",
        },
      ],
    },
    signupSection: {
      badgeText: "Repeat Customers",
      title: "Start Increasing Repeat Customers for Your Business Today",
      subtitle:
        "Don't let customers visit once and disappear. Build loyalty and grow your business with Fydo.",
      buttonLabel: "Request a Callback",
      buttonLink: "/contact",
    },
    relatedBlogKeywords: [
      "repeat customer",
      "coming back",
      "come back",
      "not coming back",
    ],
  },

  "increase-footfall-in-retail-store": {
    slug: "increase-footfall-in-retail-store",
    cluster: "retention-platform",
    // Title/description are deliberately distinct from heroSection.title (the
    // H1) - a concise, front-loaded SERP title and a benefit-led meta
    // description read better in search results than a literal H1 match.
    seo: {
      title: "Increase Retail Store Footfall & Walk-In Customers",
      description:
        "Attract nearby shoppers and turn browsers into buyers. Fydo helps retail stores run local offers, loyalty rewards, and WhatsApp campaigns that boost footfall.",
    },
    heroSection: {
      badgeText: "Increase Footfall",
      title: "Increase Footfall in Your Retail Store with Local Offers",
      gradientSubHeading: "Not enough customers",
      baseSubHeading: "walking into your shop?",
      microSubHeading:
        "Foot traffic starts with visibility - shoppers need to know your store exists and what's on offer right now. Fydo helps retail stores promote local deals, stay visible to nearby customers, and turn one-time walk-ins into regular visitors, all from one app.",
      supportingText:
        "No complicated setup. Works with your existing system or even if you don't use one.",
      primaryButton: {
        title: "Download App",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    // H2 - What Drives Footfall Into a Retail Store?
    whatIsSection: {
      badgeText: "Footfall",
      heading: "What Drives Footfall Into a Retail Store?",
      description:
        "Footfall isn't just about location - it's about visibility and timing. Shoppers need a reason to walk in on a given day, whether that's a local offer, a promoted sale, or simply being reminded that your store exists. Stores that stay visible to nearby customers and keep past visitors engaged consistently see more walk-ins than stores that rely on passing traffic alone.",
      steps: [],
      bottomLine:
        "Fydo helps retail stores act on this - running local offers, sending WhatsApp promotions, and rewarding repeat visits - so nearby shoppers know about you and keep walking back in.",
    },
    // H2 - Why Isn't Your Retail Store Getting Enough Footfall?
    problemSection: {
      badgeText: "Problem",
      heading: "Why Isn't Your Retail Store Getting Enough Footfall?",
      subHeading:
        "Low footfall is rarely about product quality - it's usually about visibility and follow-up. Common reasons stores struggle to attract walk-ins include:",
      points: [
        "Nearby shoppers don't know your store or its offers exist",
        "Offers and promotions aren't visible outside the store",
        "No consistent daily or weekly deals to bring people in",
        "Footfall depends only on passing foot traffic",
        "Competitors run more visible local promotions",
        "Past customers are never notified about new arrivals or sales",
        "Festival and weekend footfall isn't planned in advance",
        "No way to track where walk-ins are actually coming from",
      ],
      endingLine:
        "Without a way to promote offers and stay visible to nearby shoppers, footfall stays unpredictable no matter how good your store is.",
    },
    // H2 - Everything You Need to Increase Store Footfall (each item below renders as an H3)
    featureSection: {
      heading: "Everything You Need to Increase Store Footfall",
      subHeading: "",
      features: [
        {
          id: 1,
          titleStart: { text: "Promote", gradient: false },
          titleGrad: { text: "Local Offers", gradient: true },
          titleEnd: { text: "That Get Noticed", gradient: false },
          description:
            "Run time-based offers and promotions that give nearby shoppers a reason to visit today, not someday.",
          isReversed: false,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 2,
          titleStart: { text: "Reach Nearby Shoppers with", gradient: false },
          titleGrad: { text: "WhatsApp Campaigns", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Share deals, new arrivals, and updates directly on WhatsApp to keep your store visible between visits.",
          isReversed: true,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 3,
          titleStart: { text: "Turn Walk-Ins Into", gradient: false },
          titleGrad: { text: "Repeat Visitors", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Reward every visit with cashback or points so first-time footfall turns into regular footfall.",
          isReversed: false,
          animation: "understand" as const,
          bullets: [],
        },
        {
          id: 4,
          titleStart: { text: "Run", gradient: false },
          titleGrad: { text: "Festival & Weekend", gradient: true },
          titleEnd: { text: "Campaigns", gradient: false },
          description:
            "Plan ahead for high-footfall periods with targeted campaigns instead of relying on the calendar alone.",
          isReversed: true,
          animation: "get_customer" as const,
          bullets: [],
        },
        {
          id: 5,
          titleStart: { text: "Bring Back Customers Who've", gradient: false },
          titleGrad: { text: "Stopped Visiting", gradient: true },
          titleEnd: { text: "", gradient: false },
          description:
            "Identify customers who haven't walked in recently and send them a reason to return.",
          isReversed: false,
          animation: "bring_more" as const,
          bullets: [],
        },
        {
          id: 6,
          titleStart: { text: "Track Where Your", gradient: false },
          titleGrad: { text: "Footfall", gradient: true },
          titleEnd: { text: "Comes From", gradient: false },
          description:
            "See which offers, campaigns, and customer segments are actually driving walk-ins.",
          isReversed: true,
          animation: "understand" as const,
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "Use Case Section",
      heading: "Works for Every Type of Retail Business",
      subHeading:
        "Fydo helps all types of retail businesses increase footfall and grow.",
      useCases: [
        {
          title: "Grocery Stores",
          detail: "Increase daily customer visits",
        },
        {
          title: "Clothing Stores",
          detail: "Attract customers for new collections",
        },
        {
          title: "Salons & Services",
          detail: "Increase walk-ins and bookings",
        },
        {
          title: "Restaurants & Cafes",
          detail: "Improve dine-in footfall",
        },
      ],
      endingLine: "No matter your business, more footfall means more sales.",
    },
    // H2 - What Happens When Your Footfall Increases
    benefitsSection: {
      badgeText: "Benefits",
      heading: "What Happens When Your Footfall Increases",
      description:
        "More walk-ins compound into more sales, more repeat visits, and steadier daily revenue.",
      benefits: [
        "More Daily Walk-Ins: Local offers and promotions bring more shoppers through your door",
        "Higher Visibility Nearby: Your store stays top of mind for customers in the area",
        "Better Repeat Footfall: Rewarded visits turn one-time walk-ins into regulars",
        "Stronger Festival Sales: Planned campaigns capture high-footfall periods",
        "Fewer Empty Hours: Targeted offers help fill slow periods with visits",
        "Clear Visibility Into What Works: Track which campaigns actually drive footfall",
      ],
    },
    customerTestimonialsSection: {
      heading: "Retail Businesses Are Increasing Footfall with Fydo",
      subHeading:
        "Shop owners use Fydo to attract customers and increase daily visits.",
      testimonials: [
        {
          id: 1,
          quote:
            "We started using Fydo to run offers and campaigns, and our shop footfall increased significantly.",
          description:
            "Fydo helped us run targeted promotions and stay connected with customers, which improved our daily walk-ins.",
          author: "Retail Store Owner",
          rating: 4.8,
          authorStoreName: "Fydo Partner Store",
          authorStoreType: "Retail Business",
          authorAvatar: "/images/avatar.png",
        },
      ],
      points: [],
    },
    stepsSection: {
      title: "Increase Footfall in 3 Simple Steps",
      description: "",
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Download the Fydo App",
          description: "Get started with a simple setup",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Create Campaigns and Offers",
          description: "Set up promotions and loyalty programs",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Attract and Bring Customers Back",
          description: "Start increasing footfall and sales",
        },
      ],
    },
    // H2 - Frequently Asked Questions
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "How can I increase footfall in my retail store?",
          answer:
            "Increase footfall by running local offers, promoting your store on WhatsApp, rewarding repeat visits, and planning campaigns around festivals and weekends.",
        },
        {
          question: "Why is my shop not getting enough walk-in customers?",
          answer:
            "Low walk-ins are usually caused by low visibility - nearby shoppers don't know about your offers, and past customers aren't reminded to return.",
        },
        {
          question: "What is the fastest way to increase footfall?",
          answer:
            "Running a time-limited local offer and promoting it directly to nearby customers over WhatsApp is one of the fastest ways to drive walk-ins.",
        },
        {
          question:
            "Does rewarding repeat customers actually increase footfall?",
          answer:
            "Yes. Customers who are rewarded for returning visit more often, which increases footfall without needing new customer acquisition.",
        },
        {
          question:
            "Can small retail shops increase footfall without a big budget?",
          answer:
            "Yes. Local offers, WhatsApp campaigns, and loyalty rewards are low-cost ways for small shops to increase walk-ins.",
        },
        {
          question: "How does Fydo help increase footfall in a retail store?",
          answer:
            "Fydo helps you run local offers, send WhatsApp promotions, reward repeat visits, and track which campaigns are driving walk-ins.",
        },
        {
          question: "Can I track where my footfall is coming from?",
          answer:
            "Yes, Fydo lets you track customer visits and campaign performance so you know what's actually bringing shoppers in.",
        },
      ],
    },
    signupSection: {
      badgeText: "Increase Footfall",
      title: "Start Increasing Footfall in Your Retail Store Today",
      subtitle:
        "Don't let your shop stay empty. Attract more customers and grow your business with Fydo.",
      buttonLabel: "Request a Callback",
      buttonLink: "/partner-signup",
    },
    relatedBlogKeywords: [],
  },

  "loyalty-program-for-retail": {
    slug: "loyalty-program-for-retail",
    cluster: "core-loyalty",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Retail Businesses",
      description:
        "Build a Loyalty Program for Retail with Fydo. Reward customers, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Retail",
        "retail loyalty program",
        "loyalty program for retail business",
        "retail customer loyalty program",
        "loyalty program for retail stores",
        "retail loyalty software",
        "retail loyalty solution",
        "customer loyalty program for retail",
        "digital loyalty program for retail",
        "loyalty rewards for retail",
        "retail customer retention",
        "retail rewards program",
        "customer loyalty",
        "digital rewards",
        "loyalty rewards",
        "cashback rewards",
        "points-based rewards",
        "customer engagement",
        "repeat purchases",
        "customer retention",
        "personalized offers",
        "loyalty campaigns",
        "customer re-engagement",
        "win-back campaigns",
        "returning customers",
        "digital offers",
        "customer rewards",
        "WhatsApp marketing",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Retail",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Retail",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "A **Loyalty Program for Retail** helps businesses reward customers, encourage repeat purchases, and build stronger relationships with shoppers.",
        "With Fydo, retail businesses can create cashback and points-based rewards, offer personalized deals, run targeted campaigns, engage customers with **Free WhatsApp Marketing**, and give shoppers more reasons to return.",
        "Turn one-time shoppers into loyal, returning customers.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading:
        "Why Does Your Retail Business Need a Loyalty Program for Retail?",
      subHeading:
        "Customers have more choices than ever. Even when shoppers are satisfied with your products and service, there is no guarantee they will return to your store.",
      description: [
        "Traditional loyalty methods can also be difficult to manage and may not provide enough opportunities to engage customers between purchases.",
      ],
      listLabel: "Retail businesses often struggle with:",
      points: [
        "Customers making only occasional purchases",
        "Difficulty encouraging customers to return",
        "Physical loyalty cards being lost or forgotten",
        "Manual tracking of customer rewards",
        "Generic discounts with limited personalization",
        "Limited customer purchase insights",
        "Difficulty reaching inactive customers",
        "No structured customer retention strategy",
      ],
      endingLine:
        "A well-designed loyalty program can turn everyday transactions into opportunities for long-term customer engagement.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Retail Purchases into Long-Term Customer Relationships",
      description: [
        "Fydo provides a simple digital solution for creating and managing a **Loyalty Program for Retail** businesses.",
        "Reward customers for their purchases, create relevant offers, communicate with shoppers, and build an ongoing engagement strategy from one platform.",
      ],
      listLabel: "With Fydo, you can:",
      steps: [
        "Reward customers for purchases",
        "Offer cashback, points, discounts, and coupons",
        "Create personalized customer offers",
        "Run targeted loyalty campaigns",
        "Engage customers with **Free WhatsApp Marketing**",
        "Re-engage inactive shoppers",
        "Promote products and special offers",
        "Track customer activity",
        "Encourage repeat purchases",
        "Build stronger customer relationships",
      ],
      bottomLine:
        "Reward. Engage. Retain. Give customers more reasons to choose your store again.",
    },
    featureSection: {
      heading: "Powerful Features for Your Retail Loyalty Program",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Choose reward structures that suit your retail business, from cashback and points to discounts and coupons.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Customers for Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Turn purchases into loyalty opportunities and encourage shoppers to come back.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** and share offers, rewards, campaigns, and updates.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Run campaigns for new customers, frequent shoppers, inactive customers, or specific customer segments.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Customer Re-Engagement",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give inactive shoppers a reason to return with targeted offers and loyalty rewards.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Customer Activity Tracking",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Monitor customer engagement and identify opportunities to improve retention and repeat sales.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Product & Offer Promotion",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Promote new products, seasonal sales, special offers, and exclusive deals to your customer base.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Digital Customer Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Replace cumbersome physical loyalty processes with a simpler digital experience.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support retail businesses whether they use a POS system or operate without one.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Retail Business",
      subHeading:
        "Fydo can support different retail categories with flexible rewards and engagement strategies.",
      useCases: [
        {
          title: "Grocery & Kirana/General Stores",
          detail:
            "Reward regular shoppers and encourage customers to return for their everyday purchases.",
        },
        {
          title: "Clothing & Fashion Stores",
          detail:
            "Use exclusive rewards, personalized offers, and promotions to encourage repeat shopping.",
        },
        {
          title: "Salons & Beauty Businesses",
          detail:
            "Reward repeat visits and build stronger relationships with customers through ongoing engagement.",
        },
        {
          title: "Restaurants & Cafes",
          detail:
            "Encourage customers to return with digital rewards, offers, and personalized promotions.",
        },
        {
          title: "Pharmacies",
          detail:
            "Build stronger connections with regular customers through convenient rewards and relevant communication.",
        },
        {
          title: "Lifestyle & Specialty Stores",
          detail:
            "Turn occasional shoppers into returning customers with targeted rewards and promotional campaigns.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Businesses Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give existing customers meaningful reasons to shop with you again.",
        },
        {
          title: "Build Customer Relationships",
          text: "Continue engaging shoppers beyond their individual transactions.",
        },
        {
          title: "Encourage Customer Retention",
          text: "Use rewards and personalized communication to strengthen long-term loyalty.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Stay connected through digital offers, rewards, campaigns, and WhatsApp communication.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Identify customers who have stopped purchasing and create targeted win-back campaigns.",
        },
        {
          title: "Personalize Promotions",
          text: "Deliver offers that are more relevant to different customer groups.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, offers, and customer engagement digitally from one platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers directly through WhatsApp without needing a separate marketing solution.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline businesses build stronger customer relationships through digital rewards, loyalty, engagement, and retention. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Retail Loyalty Program in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Choose cashback, points, discounts, coupons, or other rewards that fit your business.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect with Customers",
          description:
            "Customers interact with your digital loyalty experience and receive rewards based on their purchases.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Retain",
          description:
            "Use personalized offers, campaigns, rewards, and **Free WhatsApp Marketing** to encourage customers to return.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Retail?",
          answer:
            "A Loyalty Program for Retail is a customer retention strategy that rewards shoppers for their purchases and encourages them to continue buying from the same business.",
        },
        {
          question:
            "How can a retail loyalty program increase repeat purchases?",
          answer:
            "Rewards, personalized offers, and targeted customer engagement give shoppers additional reasons to return and make another purchase.",
        },
        {
          question: "What rewards can I offer my retail customers?",
          answer:
            "You can offer cashback, points, discounts, coupons, and other promotional rewards based on your business objectives.",
        },
        {
          question: "Can small retail businesses use Fydo?",
          answer:
            "Yes. Fydo is designed for different types and sizes of offline businesses, including local retailers, grocery stores, salons, restaurants, pharmacies, and specialty stores.",
        },
        {
          question: "Does Fydo require a POS system?",
          answer:
            "No. Fydo can work with or without a POS system, making it suitable for a wide range of retail businesses.",
        },
        {
          question: "Can I use WhatsApp to engage my customers?",
          answer:
            "Yes. Fydo includes Free WhatsApp Marketing to help businesses communicate offers, rewards, campaigns, and updates to customers.",
        },
        {
          question: "Can I create personalized offers?",
          answer:
            "Yes. Businesses can create targeted offers and campaigns based on customer activity and engagement.",
        },
        {
          question: "Can the program help bring back inactive customers?",
          answer:
            "Yes. Businesses can use targeted rewards, offers, and re-engagement campaigns to encourage inactive customers to return.",
        },
        {
          question:
            "Is a digital loyalty program better than physical loyalty cards?",
          answer:
            "A digital approach eliminates the need for customers to carry physical cards and makes it easier for businesses to manage rewards and customer engagement.",
        },
        {
          question: "How can I start a loyalty program with Fydo?",
          answer:
            "You can set up your rewards and customer engagement strategy with Fydo. Get Started Free or Book a Demo to learn more.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn More Retail Shoppers into Loyal Customers",
      subtitle:
        "Your existing customers are one of your most valuable growth opportunities. Reward their purchases, give them personalized reasons to return, and stay connected through digital engagement with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "retail loyalty program",
      "cashback for retail shops",
      "whatsapp marketing for retail",
      "customer retention retail business",
    ],
  },

  "loyalty-program-for-furniture-store": {
    slug: "loyalty-program-for-furniture-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Furniture Store",
      description:
        "Build a Loyalty Program for Furniture Store with Fydo. Reward customers, promote new collections, increase repeat purchases, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Furniture Store",
        "furniture store loyalty program",
        "loyalty program for furniture stores",
        "furniture customer loyalty program",
        "furniture loyalty software",
        "furniture store rewards program",
        "loyalty software for furniture store",
        "digital loyalty program for furniture store",
        "furniture customer retention",
        "furniture rewards platform",
        "customer loyalty software for furniture stores",
        "loyalty solution for furniture retailers",
        "furniture loyalty app",
        "customer rewards program for furniture store",
        "customer loyalty",
        "digital rewards",
        "cashback rewards",
        "points-based rewards",
        "customer engagement",
        "repeat purchases",
        "customer retention",
        "personalized offers",
        "loyalty campaigns",
        "customer re-engagement",
        "new collection promotions",
        "product launches",
        "cross-selling",
        "returning customers",
        "digital offers",
        "customer rewards",
        "WhatsApp marketing",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Furniture Store",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Furniture Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "A **Loyalty Program for Furniture Store** helps furniture retailers build lasting customer relationships, encourage repeat purchases, and stay connected with customers long after their first purchase.",
        "With Fydo, furniture stores can offer cashback, points, discounts, and personalized rewards, promote new collections, run targeted campaigns, and engage customers through **Free WhatsApp Marketing**.",
        "Turn one furniture purchase into a long-term customer relationship.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Furniture Store Need a Loyalty Program?",
      subHeading:
        "Furniture purchases are often high-value and less frequent than everyday retail purchases. A customer may not need another sofa, bed, dining table, or wardrobe immediately—but they may return later when moving homes, renovating, upgrading, or furnishing another space.",
      description: [
        "The challenge is staying connected until the next purchase opportunity.",
      ],
      listLabel: "Furniture retailers often face challenges such as:",
      points: [
        "Long gaps between repeat purchases",
        "Customers comparing multiple furniture stores",
        "Difficulty staying connected after the sale",
        "Limited opportunities to engage customers between purchases",
        "Difficulty promoting new collections and products",
        "Generic offers with limited personalization",
        "Limited customer purchase insights",
        "Difficulty re-engaging inactive customers",
        "No structured strategy for generating repeat business",
      ],
      endingLine:
        "A digital loyalty strategy can help furniture retailers stay top-of-mind and build relationships beyond individual transactions.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Furniture Purchase into a Loyalty Opportunity",
      description: [
        "Fydo provides a simple digital solution for creating and managing a **Loyalty Program for Furniture Store** businesses.",
        "Reward customers for purchases, promote new products and collections, provide personalized offers, and stay connected throughout the customer journey.",
      ],
      listLabel: "With Fydo, your furniture store can:",
      steps: [
        "Reward customers for purchases",
        "Offer cashback, points, discounts, and coupons",
        "Create personalized customer offers",
        "Promote new furniture collections",
        "Run targeted loyalty campaigns",
        "Engage customers with **Free WhatsApp Marketing**",
        "Promote complementary products",
        "Re-engage inactive customers",
        "Track customer activity",
        "Encourage repeat purchases",
        "Build long-term customer relationships",
      ],
      bottomLine:
        "Reward the Purchase. Stay Connected. Create the Next Opportunity.",
    },
    featureSection: {
      heading: "Powerful Features for Your Furniture Store Loyalty Program",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback or points and give them an additional reason to return for future purchases.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Turn every furniture transaction into an opportunity to strengthen the customer relationship.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** and share rewards, offers, new collections, and promotional updates.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and engagement.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Run campaigns for new customers, existing customers, frequent shoppers, or inactive customers.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote New Collections & Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Keep customers informed about new furniture collections, seasonal launches, festive offers, and special promotions.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Promote Complementary Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Encourage additional purchases by promoting relevant furniture, décor, furnishings, and complementary products.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reconnect with customers who have not purchased recently through targeted offers and loyalty rewards.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and identify opportunities to improve retention and repeat sales.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support furniture businesses whether they use a POS system or operate without one.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Furniture Store",
      subHeading:
        "A digital rewards and engagement strategy can help different types of furniture retailers build stronger customer relationships.",
      useCases: [
        {
          title: "Home Furniture Stores",
          detail:
            "Stay connected with customers and encourage future purchases when they renovate, upgrade, or furnish additional spaces.",
        },
        {
          title: "Office Furniture Stores",
          detail:
            "Build relationships with businesses and professionals who may return for additional furniture as their requirements grow.",
        },
        {
          title: "Modular Furniture Stores",
          detail:
            "Stay connected with customers and promote complementary products, upgrades, and new designs.",
        },
        {
          title: "Sofa & Living Room Furniture Stores",
          detail:
            "Use personalized offers and product promotions to encourage future purchases and complementary sales.",
        },
        {
          title: "Bedroom Furniture Stores",
          detail:
            "Build customer relationships that can lead to future purchases of beds, wardrobes, mattresses, and related products.",
        },
        {
          title: "Multi-Category Furniture Retailers",
          detail:
            "Engage different customer segments with relevant rewards, product promotions, and personalized campaigns.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Furniture Businesses Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return when they are ready for their next furniture purchase.",
        },
        {
          title: "Stay Connected Between Purchases",
          text: "Maintain an ongoing relationship even when customers have longer buying cycles.",
        },
        {
          title: "Promote New Collections",
          text: "Keep existing customers informed about new designs, products, launches, and special offers.",
        },
        {
          title: "Encourage Cross-Selling",
          text: "Promote complementary products based on customer interests and previous purchases.",
        },
        {
          title: "Build Long-Term Customer Relationships",
          text: "Turn a single furniture transaction into an ongoing customer relationship.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use digital rewards, personalized offers, campaigns, and WhatsApp communication to stay connected.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Re-engage customers who have not purchased recently with targeted offers and rewards.",
        },
        {
          title: "Personalize Customer Promotions",
          text: "Create more relevant campaigns based on customer activity and engagement.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, offers, and customer engagement digitally from one platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Communicate directly with customers through WhatsApp without requiring a separate marketing solution.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline businesses build stronger customer relationships through digital rewards, customer engagement, and retention. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Your Furniture Store Loyalty Program in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Choose cashback, points, discounts, coupons, or other rewards that suit your furniture business.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect with Customers",
          description:
            "Customers interact with your digital loyalty experience and receive rewards based on their purchases.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, collection promotions, loyalty campaigns, and **Free WhatsApp Marketing** to stay connected with customers.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for a Furniture Store?",
          answer:
            "A Loyalty Program for Furniture Store is a customer retention strategy that rewards customers for purchases and helps furniture retailers build long-term customer relationships.",
        },
        {
          question: "How can a loyalty program help a furniture store?",
          answer:
            "It can help furniture stores encourage repeat purchases, promote new collections, stay connected between purchases, and build stronger customer relationships.",
        },
        {
          question: "What rewards can I offer furniture customers?",
          answer:
            "You can offer cashback, points, discounts, coupons, and other promotional rewards based on your loyalty strategy.",
        },
        {
          question: "Can a furniture store use Fydo without a POS system?",
          answer:
            "Yes. Fydo can work with or without a POS system, making it suitable for different furniture retail setups.",
        },
        {
          question:
            "Can I promote new furniture collections to existing customers?",
          answer:
            "Yes. Businesses can use digital campaigns and customer communication to promote new collections, product launches, seasonal offers, and special promotions.",
        },
        {
          question: "Can I use WhatsApp to engage furniture customers?",
          answer:
            "Yes. Fydo includes Free WhatsApp Marketing to help businesses communicate offers, rewards, new products, and campaigns.",
        },
        {
          question: "Can I create personalized offers for different customers?",
          answer:
            "Yes. Businesses can create targeted offers and campaigns based on customer activity and engagement.",
        },
        {
          question:
            "Can the program help bring back inactive furniture customers?",
          answer:
            "Yes. Targeted rewards, personalized offers, and re-engagement campaigns can encourage inactive customers to return.",
        },
        {
          question:
            "Can a loyalty program help furniture stores with long purchase cycles?",
          answer:
            "Yes. Staying connected through relevant offers, product updates, rewards, and promotions can help keep your store top-of-mind until customers are ready for another purchase.",
        },
        {
          question: "How can I start a loyalty program for my furniture store?",
          answer:
            "You can set up your rewards and customer engagement strategy with Fydo. Get Started Free or Book a Demo to learn more.",
        },
      ],
    },
    signupSection: {
      badgeText: "",
      title: "Turn Furniture Buyers into Long-Term Customers",
      subtitle:
        "A furniture purchase may happen once in several months or years, but the customer relationship can continue throughout that time. Reward customers, promote new collections, stay connected between purchases, and create more opportunities for repeat business with Fydo.",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "furniture store customer retention",
      "whatsapp marketing for furniture stores",
      "cashback loyalty program for furniture shops",
      "referral marketing for furniture stores",
    ],
  },

  "loyalty-program-for-pharmacy": {
    slug: "loyalty-program-for-pharmacy",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Pharmacy | Customer Rewards & Retention",
      description:
        "Build a Loyalty Program for Pharmacy with Fydo. Reward customers, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Pharmacy",
        "Pharmacy loyalty program",
        "Pharmacy customer loyalty",
        "Pharmacy rewards program",
        "Pharmacy customer retention",
        "Loyalty software for pharmacy",
        "Customer rewards for pharmacy",
        "Pharmacy retention software",
        "Digital loyalty program for pharmacy",
        "Pharmacy customer engagement",
        "Pharmacy rewards platform",
        "Customer retention for pharmacies",
        "Loyalty solution for pharmacy",
        "Digital rewards",
        "Cashback rewards",
        "Loyalty rewards",
        "Customer engagement",
        "Repeat purchases",
        "Returning customers",
        "Customer retention",
        "Personalized offers",
        "Digital offers",
        "Re-engagement campaigns",
        "Win-back campaigns",
        "Customer loyalty",
        "Reward system",
        "WhatsApp marketing",
        "Retail customer retention",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Pharmacy",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Pharmacy",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn everyday pharmacy purchases into opportunities to build stronger customer relationships. Fydo helps pharmacies reward customers, offer personalized promotions, encourage repeat purchases, and stay connected through **Free WhatsApp Marketing**.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Pharmacy Need a Loyalty Program?",
      subHeading:
        "Pharmacies often serve customers repeatedly, but without a structured loyalty strategy, these purchases may not translate into stronger long-term relationships.",
      description: [],
      listLabel: "Common challenges include:",
      points: [
        "Customers switching between nearby pharmacies",
        "Limited engagement between purchases",
        "Difficulty identifying regular and inactive customers",
        "Generic offers that don't match customer needs",
        "Manual management of loyalty rewards",
        "Limited ways to promote relevant products and offers",
        "No simple way to reconnect with customers",
      ],
      endingLine:
        "A **Loyalty Program for Pharmacy** can help turn regular transactions into meaningful customer relationships and encourage customers to choose your pharmacy again.",
    },
    solutionSection: {
      badgeText: "",
      heading:
        "Turn Everyday Pharmacy Purchases into Customer Loyalty Opportunities",
      description: [
        "Fydo provides a simple **Loyalty Program for Pharmacy** that helps you reward customers, engage them with relevant offers, and encourage them to return.",
      ],
      listLabel: "With Fydo, your pharmacy can:",
      steps: [
        "Reward customers for their purchases",
        "Offer cashback and loyalty rewards",
        "Create personalized customer offers",
        "Run targeted engagement campaigns",
        "Re-engage inactive customers",
        "Promote relevant products and special offers",
        "Track customer activity",
        "Connect with customers through **Free WhatsApp Marketing**",
        "Build loyalty without depending on complex systems",
      ],
      bottomLine:
        "Whether you operate an independent pharmacy or a growing pharmacy business, Fydo makes customer loyalty easier to manage.",
    },
    featureSection: {
      heading: "Powerful Features for a Loyalty Program for Pharmacy",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a reason to return by rewarding eligible purchases with cashback, points, or other digital rewards.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Turn regular transactions into loyalty opportunities and encourage customers to continue shopping with your pharmacy.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** for promotions, offers, rewards, and customer engagement.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer purchase behaviour and engagement.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create campaigns for specific customer groups instead of sending the same promotion to everyone.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote Products & Special Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Highlight selected products, seasonal promotions, and special offers to existing customers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Identify customers who haven't purchased recently and create campaigns designed to bring them back.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer interactions and purchase behaviour to make better engagement decisions.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Digital Loyalty Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers a convenient digital way to access rewards and offers without relying on physical loyalty cards.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your loyalty strategy whether your pharmacy uses a POS system or not.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Pharmacy",
      subHeading: "",
      useCases: [
        {
          title: "Independent Pharmacies",
          detail:
            "Build stronger relationships with local customers and encourage repeat visits.",
        },
        {
          title: "Chain & Multi-Store Pharmacies",
          detail:
            "Create a consistent customer engagement strategy across multiple locations.",
        },
        {
          title: "Community Pharmacies",
          detail:
            "Reward regular customers and stay connected with the local community.",
        },
        {
          title: "Specialty Pharmacies",
          detail:
            "Use targeted engagement and relevant offers to build long-term customer relationships.",
        },
        {
          title: "Wellness & Healthcare Retailers",
          detail:
            "Promote relevant products, offers, and rewards while keeping customers engaged.",
        },
        {
          title: "Pharmacy & General Healthcare Stores",
          detail:
            "Combine everyday purchases with digital rewards and customer engagement.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Pharmacies Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers additional reasons to return to your pharmacy.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Stay connected beyond individual transactions and create long-term customer relationships.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Reach customers with rewards, offers, promotions, and relevant communication.",
        },
        {
          title: "Encourage Customer Retention",
          text: "Create a consistent experience that motivates existing customers to continue shopping with you.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Use targeted re-engagement campaigns to reconnect with customers who haven't visited recently.",
        },
        {
          title: "Personalize Promotions",
          text: "Deliver more relevant offers based on customer activity and purchasing patterns.",
        },
        {
          title: "Promote Products & Offers",
          text: "Keep customers informed about selected products, promotions, and special campaigns.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, campaigns, customer engagement, and activity from one digital platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers directly through WhatsApp without adding a separate marketing platform to your loyalty workflow.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo is helping businesses build stronger customer relationships through digital loyalty and customer engagement. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business. Build your pharmacy's customer loyalty strategy with a platform designed for modern offline retail.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Building Customer Loyalty in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Create cashback, points, discounts, or other rewards that fit your pharmacy.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect Customers",
          description:
            "Customers can interact with your loyalty program through a simple digital experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, campaigns, rewards, and **Free WhatsApp Marketing** to encourage customers to return.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Pharmacy?",
          answer:
            "A Loyalty Program for Pharmacy is a customer retention system that rewards customers for purchases and helps pharmacies encourage repeat visits, engagement, and long-term loyalty.",
        },
        {
          question: "How can Fydo help my pharmacy build customer loyalty?",
          answer:
            "Fydo helps pharmacies reward customers, create personalized offers, run targeted campaigns, track customer activity, and engage shoppers through digital loyalty tools.",
        },
        {
          question: "Can I offer cashback to pharmacy customers?",
          answer:
            "Yes. Fydo supports cashback-based rewards that can be configured according to your business strategy.",
        },
        {
          question: "Can I use WhatsApp to engage pharmacy customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, promotions, and other customer engagement messages.",
        },
        {
          question: "Can I target different customer groups?",
          answer:
            "Yes. You can create targeted campaigns and offers for different customer segments based on their activity and engagement.",
        },
        {
          question: "Can I bring back inactive customers?",
          answer:
            "Yes. Customer activity can help you identify inactive shoppers and create targeted re-engagement campaigns.",
        },
        {
          question: "Does Fydo work with a pharmacy POS system?",
          answer:
            "Fydo can work with or without POS, making it suitable for pharmacies with different technology setups.",
        },
        {
          question: "Can I promote pharmacy products and offers?",
          answer:
            "Yes. Businesses can use digital campaigns and customer communication to promote selected products, special offers, and promotions.",
        },
        {
          question: "Is Fydo suitable for small pharmacies?",
          answer:
            "Yes. Fydo is designed to support small and mid-sized offline retail businesses, making it suitable for independent pharmacies as well as larger operations.",
        },
        {
          question: "How can I get started with Fydo?",
          answer:
            "You can get started by setting up your rewards and customer engagement strategy with Fydo. Click Get Started Free or Book a Demo to begin.",
        },
      ],
    },
    signupSection: {
      badgeText: "Reward. Engage. Retain. Grow.",
      title: "Turn Pharmacy Customers into Returning Customers",
      subtitle:
        "Don't let everyday transactions be the end of the customer relationship. Reward your customers, stay connected, personalize your offers, and create reasons for them to choose your pharmacy again. Build Stronger Customer Relationships with Fydo",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "pharmacy customer retention",
      "whatsapp marketing for pharmacies",
      "cashback loyalty program for pharmacies",
      "customer loyalty for medical stores",
    ],
  },

  "loyalty-program-for-medical-store": {
    slug: "loyalty-program-for-medical-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Medical Store | Customer Rewards & Retention",
      description:
        "Build a Loyalty Program for Medical Store with Fydo. Reward customers, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Medical Store",
        "Medical store loyalty program",
        "Medical store customer loyalty",
        "Medical store rewards program",
        "Medical store customer retention",
        "Loyalty software for medical store",
        "Customer rewards for medical store",
        "Medical store retention software",
        "Digital loyalty program for medical store",
        "Medical store customer engagement",
        "Medical store rewards platform",
        "Customer retention for medical stores",
        "Loyalty solution for medical store",
        "Digital rewards",
        "Cashback rewards",
        "Loyalty rewards",
        "Customer engagement",
        "Repeat purchases",
        "Returning customers",
        "Customer retention",
        "Personalized offers",
        "Digital offers",
        "Re-engagement campaigns",
        "Win-back campaigns",
        "Customer loyalty",
        "Reward system",
        "WhatsApp marketing",
        "Retail customer retention",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Medical Store",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Medical Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn everyday medical store purchases into opportunities to build stronger customer relationships. Fydo helps medical stores reward customers, offer personalized promotions, encourage repeat purchases, and stay connected with **Free WhatsApp Marketing**.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Medical Store Need a Loyalty Program?",
      subHeading:
        "Customers often return to the same medical store for regular purchases, but without a structured loyalty strategy, businesses may struggle to retain them in a competitive local market.",
      description: [],
      listLabel: "Common challenges include:",
      points: [
        "Customers switching between nearby medical stores",
        "Limited engagement between purchases",
        "Difficulty identifying regular and inactive customers",
        "Generic offers that don't suit different customer groups",
        "Manual management of customer rewards",
        "Limited ways to promote relevant products and offers",
        "No simple way to reconnect with customers who haven't visited recently",
      ],
      endingLine:
        "A **Loyalty Program for Medical Store** can help turn regular transactions into stronger customer relationships and encourage customers to choose your store again.",
    },
    solutionSection: {
      badgeText: "",
      heading:
        "Turn Everyday Medical Store Purchases into Loyalty Opportunities",
      description: [
        "Fydo provides a simple **Loyalty Program for Medical Store** that helps businesses reward customers, engage them with relevant offers, and encourage repeat visits.",
      ],
      listLabel: "With Fydo, your medical store can:",
      steps: [
        "Reward customers for eligible purchases",
        "Offer cashback and digital loyalty rewards",
        "Create personalized customer offers",
        "Run targeted engagement campaigns",
        "Re-engage inactive customers",
        "Promote relevant products and special offers",
        "Track customer activity",
        "Connect with customers through **Free WhatsApp Marketing**",
        "Build customer loyalty without complex systems",
      ],
      bottomLine:
        "Whether you operate an independent medical store or a growing retail healthcare business, Fydo makes customer engagement and retention easier to manage.",
    },
    featureSection: {
      heading: "Powerful Features for a Loyalty Program for Medical Store",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback, points, or other digital rewards based on your loyalty strategy.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers an additional reason to return by turning regular transactions into loyalty opportunities.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** for offers, promotions, rewards, and customer engagement.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant promotions based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reach specific customer groups with targeted campaigns instead of sending the same message to everyone.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote Products & Special Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Highlight selected products, promotions, seasonal offers, and other relevant campaigns.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Identify customers who have not purchased recently and create campaigns to encourage them to return.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and activity to make more informed loyalty and marketing decisions.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Digital Loyalty Experience",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Provide customers with a convenient digital rewards experience without depending on physical loyalty cards.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your loyalty strategy whether your medical store uses a POS system or not.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Medical Store",
      subHeading: "",
      useCases: [
        {
          title: "Independent Medical Stores",
          detail:
            "Build stronger relationships with local customers and encourage repeat visits.",
        },
        {
          title: "Family-Run Medical Stores",
          detail:
            "Reward regular customers and create a more consistent customer experience.",
        },
        {
          title: "Multi-Store Medical Retailers",
          detail:
            "Build a unified customer engagement strategy across multiple locations.",
        },
        {
          title: "Community Medical Stores",
          detail:
            "Stay connected with local customers and encourage them to return for their regular purchases.",
        },
        {
          title: "Pharmacy & Medical Retail Stores",
          detail:
            "Combine everyday retail transactions with digital rewards and customer engagement.",
        },
        {
          title: "Healthcare & Wellness Retailers",
          detail:
            "Promote relevant products, offers, and rewards while keeping customers connected with your business.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Medical Stores Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return and continue shopping with your medical store.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Stay connected with customers beyond individual transactions.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use rewards, offers, promotions, and relevant communication to keep customers engaged.",
        },
        {
          title: "Encourage Customer Retention",
          text: "Create a consistent loyalty experience that encourages existing customers to return.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Reconnect with customers who haven't visited your store recently through targeted campaigns.",
        },
        {
          title: "Personalize Promotions",
          text: "Create more relevant offers based on customer activity and engagement.",
        },
        {
          title: "Promote Products & Offers",
          text: "Keep customers informed about selected products, promotions, and special offers.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, customer engagement, campaigns, and customer activity from one digital platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Stay connected with customers through WhatsApp without needing a separate marketing platform for your loyalty activities.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline retail businesses build stronger customer relationships through digital loyalty and customer engagement. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business. Bring the same digital customer loyalty experience to your medical store with Fydo.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Building Customer Loyalty in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Create cashback, points, discounts, or other rewards that fit your business.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect Customers",
          description:
            "Customers can interact with your loyalty program through a simple digital experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, rewards, campaigns, and **Free WhatsApp Marketing** to encourage customers to return.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for a Medical Store?",
          answer:
            "A Loyalty Program for Medical Stores is a customer retention system that rewards customers for eligible purchases and helps medical stores encourage repeat visits and long-term loyalty.",
        },
        {
          question:
            "How can Fydo help my medical store build customer loyalty?",
          answer:
            "Fydo helps medical stores reward customers, create personalized offers, run targeted campaigns, track customer activity, and engage customers through digital loyalty tools.",
        },
        {
          question: "Can I offer cashback to medical store customers?",
          answer:
            "Yes. Fydo supports cashback-based rewards that can be configured according to your business strategy.",
        },
        {
          question: "Can I use WhatsApp to engage my customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, promotions, and customer engagement messages.",
        },
        {
          question: "Can I create offers for different customer groups?",
          answer:
            "Yes. You can create targeted campaigns and offers for different customer segments based on their activity and engagement.",
        },
        {
          question: "Can Fydo help me bring back inactive customers?",
          answer:
            "Yes. You can identify inactive customers and use targeted re-engagement campaigns to encourage them to return.",
        },
        {
          question: "Does Fydo work with a medical store POS system?",
          answer:
            "Yes. Fydo can work with or without POS, making it suitable for medical stores with different technology setups.",
        },
        {
          question: "Can I promote products and special offers?",
          answer:
            "Yes. You can use digital campaigns and customer communication to promote selected products, offers, and promotions.",
        },
        {
          question: "Is Fydo suitable for small medical stores?",
          answer:
            "Yes. Fydo is designed for small and mid-sized offline retail businesses, making it suitable for independent medical stores as well as larger operations.",
        },
        {
          question: "How can I get started with Fydo?",
          answer:
            "You can start by setting up your rewards and customer engagement strategy with Fydo. Click Get Started Free or Book a Demo to begin.",
        },
      ],
    },
    signupSection: {
      badgeText: "Reward. Engage. Retain. Grow.",
      title: "Turn Medical Store Customers into Returning Customers",
      subtitle:
        "Don't let everyday transactions be the end of your customer relationship. Reward customers, stay connected, personalize your offers, and create more reasons for them to choose your medical store again. Build Stronger Customer Relationships with Fydo",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "medical store customer retention",
      "whatsapp reminders for medical stores",
      "cashback loyalty program for medical stores",
      "prescription refill reminders",
    ],
  },

  "loyalty-program-for-optical-store": {
    slug: "loyalty-program-for-optical-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Optical Store | Customer Rewards & Retention",
      description:
        "Build a Loyalty Program for Optical Store with Fydo. Reward customers, promote new collections, increase repeat purchases, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Optical Store",
        "Optical store loyalty program",
        "Optical store customer loyalty",
        "Optical shop loyalty program",
        "Optical store rewards program",
        "Optical store customer retention",
        "Loyalty software for optical store",
        "Customer rewards for optical store",
        "Optical store retention software",
        "Digital loyalty program for optical store",
        "Optical store customer engagement",
        "Optical store rewards platform",
        "Customer retention for optical stores",
        "Loyalty solution for optical store",
        "Digital rewards",
        "Cashback rewards",
        "Loyalty rewards",
        "Customer engagement",
        "Repeat purchases",
        "Returning customers",
        "Customer retention",
        "Personalized offers",
        "Digital offers",
        "Re-engagement campaigns",
        "Win-back campaigns",
        "Customer loyalty",
        "Reward system",
        "WhatsApp marketing",
        "Retail customer retention",
        "Eyewear customer engagement",
        "Optical customer retention",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Optical Stores",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Optical Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn every eyewear purchase into an opportunity to build lasting customer relationships. Fydo helps optical stores reward customers, promote new products, personalize offers, encourage repeat purchases, and stay connected with **Free WhatsApp Marketing**.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Optical Store Need a Loyalty Program?",
      subHeading:
        "Customers may not purchase eyewear frequently, but when they do return, they often have significant purchase potential. Without a structured loyalty strategy, optical stores can miss opportunities to stay connected with existing customers.",
      description: [],
      listLabel: "Common challenges include:",
      points: [
        "Customers forgetting to return to the same optical store",
        "Long gaps between eyewear purchases",
        "Limited engagement after a purchase",
        "Difficulty identifying regular and inactive customers",
        "Generic promotions that don't match customer interests",
        "Limited ways to promote new frames and collections",
        "Manual customer loyalty management",
        "Difficulty reconnecting with previous customers",
      ],
      endingLine:
        "A **Loyalty Program for Optical Store** can help you maintain customer relationships between purchases and create more opportunities for repeat business.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Eyewear Purchase into a Loyalty Opportunity",
      description: [
        "Fydo provides a simple **Loyalty Program for Optical Store** that helps businesses reward customers, stay connected, and encourage them to return when they need new eyewear or related products.",
      ],
      listLabel: "With Fydo, your optical store can:",
      steps: [
        "Reward customers for eligible purchases",
        "Offer cashback and digital loyalty rewards",
        "Create personalized customer offers",
        "Promote new frames, lenses, and accessories",
        "Run targeted customer campaigns",
        "Re-engage inactive customers",
        "Track customer activity",
        "Encourage repeat purchases and cross-selling",
        "Connect with customers through **Free WhatsApp Marketing**",
        "Build long-term customer relationships",
      ],
      bottomLine:
        "Whether you run an independent optical shop or a multi-location eyewear business, Fydo helps simplify customer engagement and retention.",
    },
    featureSection: {
      heading: "Powerful Features for a Loyalty Program for Optical Store",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback, points, or other digital rewards based on your loyalty strategy.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers an added reason to return when they need new eyewear, accessories, or other products.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** for rewards, offers, new arrivals, promotions, and engagement campaigns.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reach different customer groups with campaigns designed around their engagement and purchase history.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote New Frames & Collections",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Showcase new eyewear collections, frames, lenses, accessories, and special promotions to existing customers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reconnect with customers who haven't purchased recently and encourage them to visit your store again.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Promote Complementary Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Encourage customers to explore related products such as eyewear accessories and other suitable offerings.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and activity to make better decisions about your loyalty and marketing campaigns.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your customer loyalty strategy whether your optical store uses a POS system or not.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Optical Store",
      subHeading: "",
      useCases: [
        {
          title: "Independent Optical Stores",
          detail:
            "Build stronger relationships with local customers and encourage them to return when they need new eyewear.",
        },
        {
          title: "Eyewear Retail Stores",
          detail:
            "Reward customers and keep them engaged with new collections, offers, and product updates.",
        },
        {
          title: "Branded Optical Stores",
          detail:
            "Create a consistent digital loyalty experience for customers and encourage repeat purchases.",
        },
        {
          title: "Multi-Brand Optical Stores",
          detail:
            "Promote different brands, collections, and products to relevant customer segments.",
        },
        {
          title: "Sunglasses & Eyewear Stores",
          detail:
            "Engage previous customers with new styles, seasonal collections, and special offers.",
        },
        {
          title: "Optical & Eye-Care Retailers",
          detail:
            "Combine customer rewards with ongoing engagement to build stronger long-term relationships.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Optical Stores Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Create more opportunities for existing customers to return when they need new eyewear or related products.",
        },
        {
          title: "Stay Connected Between Purchases",
          text: "Maintain customer engagement even when there are long gaps between purchases.",
        },
        {
          title: "Promote New Collections",
          text: "Keep previous customers informed about new frames, sunglasses, lenses, and other products.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Turn one-time purchases into longer-term customer relationships.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use rewards, personalized offers, and relevant communication to stay connected.",
        },
        {
          title: "Encourage Cross-Selling",
          text: "Promote complementary products and relevant offers to existing customers.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Reconnect with customers who haven't purchased recently through targeted campaigns.",
        },
        {
          title: "Personalize Promotions",
          text: "Create more relevant offers based on customer activity and engagement.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, campaigns, customer engagement, and activity from one digital platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers through WhatsApp with offers, promotions, rewards, and updates without needing a separate marketing platform.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline retail businesses build stronger customer relationships through digital loyalty and customer engagement. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business. Bring a modern digital loyalty experience to your optical store with Fydo.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Building Customer Loyalty in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Create cashback, points, discounts, or other rewards that fit your optical store.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect Customers",
          description:
            "Customers can interact with your loyalty program through a simple digital experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, rewards, campaigns, and **Free WhatsApp Marketing** to keep customers connected and encourage them to return.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Optical Store?",
          answer:
            "A Loyalty Program for Optical Store is a customer retention solution that rewards customers for purchases and helps optical businesses encourage repeat visits and build long-term relationships.",
        },
        {
          question:
            "How can Fydo help my optical store build customer loyalty?",
          answer:
            "Fydo helps optical stores reward customers, create personalized offers, promote products, run targeted campaigns, track customer activity, and re-engage previous customers.",
        },
        {
          question: "Can I offer cashback to optical store customers?",
          answer:
            "Yes. Fydo supports cashback-based rewards that can be configured according to your business strategy.",
        },
        {
          question: "Can I promote new frames and eyewear collections?",
          answer:
            "Yes. You can use customer engagement campaigns to promote new frames, sunglasses, lenses, accessories, collections, and special offers.",
        },
        {
          question: "Can I use WhatsApp to engage my customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, promotions, and other customer engagement messages.",
        },
        {
          question: "Can I target different customer groups?",
          answer:
            "Yes. You can create targeted campaigns and offers based on customer activity and engagement.",
        },
        {
          question: "Can Fydo help me bring back inactive customers?",
          answer:
            "Yes. You can identify inactive customers and use targeted re-engagement campaigns to encourage them to return.",
        },
        {
          question: "Does Fydo work with an optical store POS system?",
          answer:
            "Yes. Fydo can work with or without POS, making it suitable for optical stores with different technology setups.",
        },
        {
          question: "Is Fydo suitable for small optical stores?",
          answer:
            "Yes. Fydo is designed to support small and mid-sized offline retail businesses, making it suitable for independent optical stores as well as larger retailers.",
        },
        {
          question: "How can I get started with Fydo?",
          answer:
            "You can start by setting up your rewards and customer engagement strategy with Fydo. Click Get Started Free or Book a Demo to begin.",
        },
      ],
    },
    signupSection: {
      badgeText: "Reward. Engage. Retain. Grow.",
      title: "Turn Optical Shoppers into Long-Term Customers",
      subtitle:
        "Don't let a single eyewear purchase be the end of the customer relationship. Reward customers, promote new collections, stay connected between purchases, and create more reasons for customers to return to your optical store. Build Stronger Customer Relationships with Fydo",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "optical store customer retention",
      "whatsapp marketing for optical stores",
      "cashback loyalty program for eyewear stores",
      "customer engagement for opticians",
    ],
  },

  "loyalty-program-for-cosmetics-store": {
    slug: "loyalty-program-for-cosmetics-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title:
        "Loyalty Program for Cosmetics Store | Customer Rewards & Retention",
      description:
        "Build a Loyalty Program for Cosmetics Store with Fydo. Reward customers, promote new products, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Cosmetics Store",
        "Cosmetics store loyalty program",
        "Cosmetics shop loyalty program",
        "Cosmetics customer loyalty",
        "Cosmetics store rewards program",
        "Cosmetics store customer retention",
        "Loyalty software for cosmetics store",
        "Customer rewards for cosmetics store",
        "Cosmetics store retention software",
        "Digital loyalty program for cosmetics store",
        "Cosmetics customer engagement",
        "Cosmetics rewards platform",
        "Customer retention for cosmetics stores",
        "Loyalty solution for cosmetics store",
        "Digital rewards",
        "Cashback rewards",
        "Loyalty rewards",
        "Customer engagement",
        "Repeat purchases",
        "Returning customers",
        "Customer retention",
        "Personalized offers",
        "Digital offers",
        "Re-engagement campaigns",
        "Win-back campaigns",
        "Customer loyalty",
        "Reward system",
        "WhatsApp marketing",
        "Retail customer retention",
        "Beauty customer engagement",
        "Cosmetics customer retention",
        "Product promotion",
        "Cross-selling",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Cosmetics Store",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Cosmetics Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn every beauty purchase into an opportunity to build lasting customer relationships. Fydo helps cosmetics stores reward customers, promote new products, personalize offers, encourage repeat purchases, and stay connected with **Free WhatsApp Marketing**.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Cosmetics Store Need a Loyalty Program?",
      subHeading:
        "Customers have plenty of choices when shopping for beauty and cosmetic products. Without a structured loyalty strategy, it can be difficult to turn occasional shoppers into regular customers.",
      description: [],
      listLabel: "Common challenges include:",
      points: [
        "Customers switching between different cosmetics stores",
        "High competition from online and offline beauty retailers",
        "Limited engagement after a purchase",
        "Difficulty identifying regular and inactive customers",
        "Generic promotions that don't match customer interests",
        "Limited ways to promote new products and collections",
        "Missed cross-selling opportunities",
        "Manual management of customer rewards",
        "Difficulty reconnecting with customers who haven't purchased recently",
      ],
      endingLine:
        "A **Loyalty Program for Cosmetics Store** can help turn individual purchases into stronger customer relationships and encourage shoppers to return.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Beauty Purchase into a Loyalty Opportunity",
      description: [
        "Fydo provides a simple **Loyalty Program for Cosmetics Store** that helps businesses reward customers, stay connected, promote products, and encourage repeat purchases.",
      ],
      listLabel: "With Fydo, your cosmetics store can:",
      steps: [
        "Reward customers for eligible purchases",
        "Offer cashback and digital loyalty rewards",
        "Create personalized customer offers",
        "Promote new products and beauty collections",
        "Run targeted customer campaigns",
        "Encourage repeat purchases",
        "Promote complementary products",
        "Re-engage inactive customers",
        "Track customer activity",
        "Connect with customers through **Free WhatsApp Marketing**",
      ],
      bottomLine:
        "Whether you run an independent cosmetics shop, beauty retailer, or multi-brand cosmetics store, Fydo helps simplify customer engagement and retention.",
    },
    featureSection: {
      heading: "Powerful Features for a Loyalty Program for Cosmetics Store",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback, points, or other digital rewards based on your loyalty strategy.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers an added reason to return by turning everyday beauty purchases into rewarding experiences.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** for rewards, offers, new arrivals, promotions, and customer engagement.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reach different customer groups with campaigns based on their engagement and purchase activity.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote New Products & Collections",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Showcase new cosmetics, makeup, skincare, beauty products, and seasonal collections to existing customers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Promote Complementary Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Encourage cross-selling by promoting related products that complement previous purchases.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reconnect with customers who haven't purchased recently through targeted re-engagement campaigns.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and activity to make better loyalty and marketing decisions.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your loyalty strategy whether your cosmetics store uses a POS system or not.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Cosmetics Store",
      subHeading: "",
      useCases: [
        {
          title: "Independent Cosmetics Stores",
          detail:
            "Build stronger relationships with local customers and encourage repeat purchases.",
        },
        {
          title: "Multi-Brand Cosmetics Stores",
          detail:
            "Promote different beauty brands and product categories to relevant customer groups.",
        },
        {
          title: "Makeup Stores",
          detail:
            "Engage customers with new makeup launches, collections, offers, and rewards.",
        },
        {
          title: "Skincare & Beauty Stores",
          detail:
            "Promote skincare products, new arrivals, and relevant offers to existing customers.",
        },
        {
          title: "Beauty & Personal Care Retailers",
          detail:
            "Create a digital rewards experience that encourages customers to return regularly.",
        },
        {
          title: "Cosmetics & Lifestyle Stores",
          detail:
            "Combine loyalty rewards with personalized promotions to build long-term customer relationships.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Cosmetics Stores Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return for their favourite beauty and cosmetic products.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Stay connected with shoppers beyond individual transactions and build long-term loyalty.",
        },
        {
          title: "Promote New Products",
          text: "Keep existing customers informed about new launches, collections, seasonal products, and special offers.",
        },
        {
          title: "Encourage Cross-Selling",
          text: "Promote complementary products based on customer activity and purchasing behaviour.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use rewards, personalized offers, campaigns, and relevant communication to keep customers connected.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Reconnect with shoppers who haven't purchased recently through targeted re-engagement campaigns.",
        },
        {
          title: "Personalize Promotions",
          text: "Create more relevant offers based on customer activity and preferences.",
        },
        {
          title: "Increase Customer Retention",
          text: "Give existing customers a reason to continue choosing your store instead of switching to competitors.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, campaigns, customer engagement, and customer activity from one digital platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers through WhatsApp with offers, rewards, promotions, and product updates.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Trusted by Retail Businesses Across India",
      subHeading:
        "Fydo helps offline retail businesses build stronger customer relationships through digital loyalty and customer engagement. With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business. Bring a modern digital loyalty experience to your cosmetics store with Fydo.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Building Customer Loyalty in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Create cashback, points, discounts, or other rewards that fit your cosmetics business.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect Customers",
          description:
            "Customers can interact with your loyalty program through a simple digital experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, rewards, campaigns, and **Free WhatsApp Marketing** to keep customers connected and encourage repeat purchases.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Cosmetics Store?",
          answer:
            "A Loyalty Program for Cosmetics Store is a customer retention solution that rewards shoppers for purchases and helps cosmetics businesses encourage repeat visits and build stronger customer relationships.",
        },
        {
          question:
            "How can Fydo help my cosmetics store build customer loyalty?",
          answer:
            "Fydo helps cosmetics stores reward customers, create personalized offers, promote products, run targeted campaigns, track customer activity, and re-engage previous customers.",
        },
        {
          question: "Can I offer cashback to cosmetics customers?",
          answer:
            "Yes. Fydo supports cashback-based rewards that can be configured according to your business strategy.",
        },
        {
          question: "Can I promote new cosmetics and beauty products?",
          answer:
            "Yes. You can use customer engagement campaigns to promote new products, makeup launches, skincare products, collections, and special offers.",
        },
        {
          question: "Can I use WhatsApp to engage my customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, promotions, and product updates.",
        },
        {
          question: "Can I create offers for different customer groups?",
          answer:
            "Yes. You can create targeted campaigns and offers based on customer activity and engagement.",
        },
        {
          question: "Can Fydo help me bring back inactive customers?",
          answer:
            "Yes. You can identify inactive customers and use targeted re-engagement campaigns to encourage them to return.",
        },
        {
          question: "Can I promote complementary cosmetics products?",
          answer:
            "Yes. You can use customer activity and purchase information to promote relevant complementary products and offers.",
        },
        {
          question: "Does Fydo work with a cosmetics store POS system?",
          answer:
            "Yes. Fydo can work with or without POS, making it suitable for cosmetics stores with different technology setups.",
        },
        {
          question: "How can I get started with Fydo?",
          answer:
            "You can start by setting up your rewards and customer engagement strategy with Fydo. Click Get Started Free or Book a Demo to begin.",
        },
      ],
    },
    signupSection: {
      badgeText: "Reward. Engage. Retain. Grow.",
      title: "Turn Beauty Shoppers into Loyal Customers",
      subtitle:
        "Don't let a single cosmetics purchase be the end of your customer relationship. Reward customers, promote new products, personalize offers, stay connected, and create more reasons for shoppers to return to your store. Build Stronger Customer Relationships with Fydo",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "cosmetics store customer retention",
      "whatsapp marketing for cosmetics stores",
      "cashback loyalty program for beauty stores",
      "customer engagement for cosmetics shops",
    ],
  },

  "loyalty-program-for-sports-store": {
    slug: "loyalty-program-for-sports-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Sports Store | Customer Rewards & Retention",
      description:
        "Build a Loyalty Program for Sports Store with Fydo. Reward customers, promote new products, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Sports Store",
        "Sports store loyalty program",
        "Sports shop loyalty program",
        "Sports store customer loyalty",
        "Sports store rewards program",
        "Sports store customer retention",
        "Loyalty software for sports store",
        "Customer rewards for sports store",
        "Sports store retention software",
        "Digital loyalty program for sports store",
        "Sports store customer engagement",
        "Sports store rewards platform",
        "Customer retention for sports stores",
        "Loyalty solution for sports store",
        "Digital rewards",
        "Cashback rewards",
        "Loyalty rewards",
        "Customer engagement",
        "Repeat purchases",
        "Returning customers",
        "Customer retention",
        "Personalized offers",
        "Digital offers",
        "Re-engagement campaigns",
        "Win-back campaigns",
        "Customer loyalty",
        "Reward system",
        "WhatsApp marketing",
        "Retail customer retention",
        "Sports customer engagement",
        "Sports retail loyalty",
        "Product promotion",
        "Cross-selling",
        "Customer rewards",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Sports Store",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Sports Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn every sports purchase into an opportunity to build stronger customer relationships. Fydo helps sports stores reward customers, promote new products and collections, personalize offers, encourage repeat purchases, and stay connected with **Free WhatsApp Marketing**.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Sports Store Need a Loyalty Program?",
      subHeading:
        "Sports customers have many choices when shopping for footwear, apparel, equipment, and accessories. Without a structured loyalty strategy, it can be difficult to keep customers connected and encourage them to return.",
      description: [],
      listLabel: "Common challenges include:",
      points: [
        "Customers switching between different sports stores",
        "Strong competition from online and offline retailers",
        "Limited engagement after a purchase",
        "Difficulty identifying regular and inactive customers",
        "Generic offers that don't match customer interests",
        "Limited ways to promote new collections and product launches",
        "Missed cross-selling opportunities",
        "Manual management of customer rewards",
        "Difficulty reconnecting with customers who haven't purchased recently",
      ],
      endingLine:
        "A **Loyalty Program for Sports Store** can help turn individual purchases into stronger customer relationships and create more opportunities for repeat business.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Sports Purchase into a Loyalty Opportunity",
      description: [
        "Fydo provides a simple **Loyalty Program for Sports Store** that helps businesses reward customers, promote products, personalize engagement, and encourage repeat purchases.",
      ],
      listLabel: "With Fydo, your sports store can:",
      steps: [
        "Reward customers for eligible purchases",
        "Offer cashback and digital loyalty rewards",
        "Create personalized customer offers",
        "Promote new sports products and collections",
        "Run targeted customer campaigns",
        "Encourage repeat purchases",
        "Promote complementary products",
        "Re-engage inactive customers",
        "Track customer activity",
        "Connect with customers through **Free WhatsApp Marketing**",
      ],
      bottomLine:
        "Whether you run an independent sports shop, branded outlet, or multi-brand sports store, Fydo helps simplify customer engagement and retention.",
    },
    featureSection: {
      heading: "Powerful Features for a Loyalty Program for Sports Store",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback, points, or other digital rewards based on your loyalty strategy.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers an added reason to return by turning sports purchases into rewarding experiences.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** for rewards, offers, new arrivals, promotions, and engagement campaigns.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reach different customer groups with campaigns based on their purchase activity and engagement.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote New Products & Collections",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Showcase new sports footwear, apparel, equipment, accessories, and seasonal collections to existing customers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Promote Complementary Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Encourage cross-selling by promoting products that complement previous purchases.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reconnect with customers who haven't purchased recently through targeted re-engagement campaigns.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and activity to make better loyalty and marketing decisions.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your loyalty strategy whether your sports store uses a POS system or not.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Sports Store",
      subHeading: "",
      useCases: [
        {
          title: "Sports Footwear Stores",
          detail:
            "Reward customers and promote new shoes, collections, and relevant offers.",
        },
        {
          title: "Sports Apparel Stores",
          detail:
            "Engage customers with new arrivals, seasonal collections, and personalized promotions.",
        },
        {
          title: "Sports Equipment Stores",
          detail:
            "Build long-term relationships with customers purchasing sports equipment and accessories.",
        },
        {
          title: "Fitness & Gym Equipment Stores",
          detail:
            "Encourage repeat purchases and promote complementary fitness products.",
        },
        {
          title: "Multi-Brand Sports Stores",
          detail:
            "Promote different brands and product categories to relevant customer segments.",
        },
        {
          title: "Sporting Goods Retailers",
          detail:
            "Create a digital rewards experience that keeps customers connected and encourages repeat purchases.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Sports Stores Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return for sports products, apparel, footwear, and accessories.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Stay connected with shoppers beyond individual transactions and create long-term loyalty.",
        },
        {
          title: "Promote New Collections",
          text: "Keep existing customers informed about new launches, seasonal products, and special offers.",
        },
        {
          title: "Encourage Cross-Selling",
          text: "Promote complementary products based on customer activity and previous purchases.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use rewards, personalized offers, campaigns, and relevant communication to keep customers connected.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Reconnect with shoppers who haven't purchased recently through targeted re-engagement campaigns.",
        },
        {
          title: "Personalize Promotions",
          text: "Create more relevant offers based on customer activity and engagement.",
        },
        {
          title: "Increase Customer Retention",
          text: "Give existing customers more reasons to continue choosing your store.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, campaigns, customer engagement, and customer activity from one digital platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers through WhatsApp with offers, rewards, promotions, and product updates.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Create Connected Customer Experiences with Fydo",
      subHeading:
        "With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business. Bring a modern digital loyalty experience to your sports store and build stronger relationships with your customers.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Building Customer Loyalty in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Create cashback, points, discounts, or other rewards that fit your sports store.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect Customers",
          description:
            "Customers can interact with your loyalty program through a simple digital experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, rewards, campaigns, and **Free WhatsApp Marketing** to keep customers connected and encourage repeat purchases.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Sports Store?",
          answer:
            "A Loyalty Program for Sports Store is a customer retention solution that rewards shoppers for purchases and helps sports retailers encourage repeat visits and build stronger customer relationships.",
        },
        {
          question: "How can Fydo help my sports store build customer loyalty?",
          answer:
            "Fydo helps sports stores reward customers, create personalized offers, promote products, run targeted campaigns, track customer activity, and re-engage previous customers.",
        },
        {
          question: "Can I offer cashback to sports store customers?",
          answer:
            "Yes. Fydo supports cashback-based rewards that can be configured according to your business strategy.",
        },
        {
          question: "Can I promote new sports products and collections?",
          answer:
            "Yes. You can use customer engagement campaigns to promote new footwear, apparel, equipment, accessories, collections, and special offers.",
        },
        {
          question: "Can I use WhatsApp to engage my customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, promotions, and product updates.",
        },
        {
          question: "Can I create offers for different customer groups?",
          answer:
            "Yes. You can create targeted campaigns and offers based on customer activity and engagement.",
        },
        {
          question: "Can Fydo help me bring back inactive customers?",
          answer:
            "Yes. You can identify inactive customers and use targeted re-engagement campaigns to encourage them to return.",
        },
        {
          question: "Can I promote complementary sports products?",
          answer:
            "Yes. You can promote relevant products and offers based on customer activity and previous purchases.",
        },
        {
          question: "Does Fydo work with a sports store POS system?",
          answer:
            "Yes. Fydo can work with or without POS, making it suitable for sports stores with different technology setups.",
        },
        {
          question: "How can I get started with Fydo?",
          answer:
            "You can start by setting up your rewards and customer engagement strategy with Fydo. Click Get Started Free or Book a Demo to begin.",
        },
      ],
    },
    signupSection: {
      badgeText: "Reward. Engage. Retain. Grow.",
      title: "Turn Sports Shoppers into Loyal Customers",
      subtitle:
        "Don't let a single sports purchase be the end of your customer relationship. Reward customers, promote new products, personalize offers, stay connected, and create more reasons for shoppers to return to your store. Build Stronger Customer Relationships with Fydo",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "sports store customer retention",
      "whatsapp marketing for sports stores",
      "cashback loyalty program for sports shops",
      "seasonal customer engagement retail",
    ],
  },

  "loyalty-program-for-mobile-store": {
    slug: "loyalty-program-for-mobile-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title: "Loyalty Program for Mobile Store | Customer Rewards & Retention",
      description:
        "Build a Loyalty Program for Mobile Store with Fydo. Reward customers, promote new launches, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Mobile Store",
        "Mobile store loyalty program",
        "Mobile shop loyalty program",
        "Mobile store customer loyalty",
        "Mobile store rewards program",
        "Mobile store customer retention",
        "Loyalty software for mobile store",
        "Customer rewards for mobile store",
        "Mobile store retention software",
        "Digital loyalty program for mobile store",
        "Mobile store customer engagement",
        "Mobile store rewards platform",
        "Customer retention for mobile stores",
        "Loyalty solution for mobile store",
        "Digital rewards",
        "Cashback rewards",
        "Loyalty rewards",
        "Customer engagement",
        "Repeat purchases",
        "Returning customers",
        "Customer retention",
        "Personalized offers",
        "Digital offers",
        "Re-engagement campaigns",
        "Win-back campaigns",
        "Customer loyalty",
        "Reward system",
        "WhatsApp marketing",
        "Retail customer retention",
        "Smartphone customer engagement",
        "Mobile retail loyalty",
        "Product promotion",
        "Cross-selling",
        "Mobile accessories",
        "Customer rewards",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Mobile Store",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Mobile Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn every mobile purchase into an opportunity to build stronger customer relationships. Fydo helps mobile stores reward customers, promote new products and accessories, personalize offers, encourage repeat purchases, and stay connected with **Free WhatsApp Marketing**.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Mobile Store Need a Loyalty Program?",
      subHeading:
        "Customers have many options when purchasing smartphones, accessories, and other mobile products. Without a structured loyalty strategy, mobile stores can miss opportunities to retain existing customers and generate repeat business.",
      description: [],
      listLabel: "Common challenges include:",
      points: [
        "Customers switching between different mobile retailers",
        "Strong competition from online marketplaces and local stores",
        "Limited engagement after a purchase",
        "Difficulty identifying regular and inactive customers",
        "Generic offers that don't match customer interests",
        "Limited ways to promote new smartphone launches",
        "Missed opportunities to cross-sell accessories and related products",
        "Difficulty reconnecting with previous customers",
        "Manual management of customer rewards",
      ],
      endingLine:
        "A **Loyalty Program for Mobile Store** can help turn individual purchases into stronger customer relationships and create more opportunities for repeat sales.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Mobile Purchase into a Loyalty Opportunity",
      description: [
        "Fydo provides a simple **Loyalty Program for Mobile Store** that helps businesses reward customers, promote products, personalize engagement, and encourage customers to return.",
      ],
      listLabel: "With Fydo, your mobile store can:",
      steps: [
        "Reward customers for eligible purchases",
        "Offer cashback and digital loyalty rewards",
        "Create personalized customer offers",
        "Promote new smartphone launches",
        "Promote accessories and complementary products",
        "Run targeted customer campaigns",
        "Re-engage inactive customers",
        "Track customer activity",
        "Encourage repeat purchases",
        "Connect with customers through **Free WhatsApp Marketing**",
      ],
      bottomLine:
        "Whether you operate an independent mobile shop, branded outlet, or multi-brand mobile store, Fydo helps simplify customer engagement and retention.",
    },
    featureSection: {
      heading: "Powerful Features for a Loyalty Program for Mobile Store",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback, points, or other digital rewards based on your loyalty strategy.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers an additional reason to return by turning mobile and accessory purchases into rewarding experiences.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** for rewards, offers, new launches, promotions, and customer engagement.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reach different customer groups with campaigns based on their purchase activity and engagement.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote New Mobile Launches",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Keep existing customers informed about new smartphones, product launches, upgrades, and special offers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Promote Accessories & Complementary Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Encourage cross-selling by promoting cases, chargers, earphones, wearables, and other relevant accessories.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reconnect with customers who haven't purchased recently through targeted re-engagement campaigns.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and purchase activity to make better loyalty and marketing decisions.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your loyalty strategy whether your mobile store uses a POS system or not.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Mobile Store",
      subHeading: "",
      useCases: [
        {
          title: "Independent Mobile Stores",
          detail:
            "Build stronger relationships with local customers and encourage repeat purchases.",
        },
        {
          title: "Multi-Brand Mobile Stores",
          detail:
            "Promote different smartphone brands and products to relevant customer segments.",
        },
        {
          title: "Smartphone Retailers",
          detail:
            "Engage customers around new launches, upgrades, accessories, and special offers.",
        },
        {
          title: "Mobile & Accessories Stores",
          detail:
            "Reward customers while promoting complementary products and accessories.",
        },
        {
          title: "Branded Mobile Outlets",
          detail:
            "Create a consistent digital loyalty experience and encourage customers to return.",
        },
        {
          title: "Mobile & Electronics Retailers",
          detail:
            "Combine customer rewards with targeted engagement across mobile and related product categories.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Mobile Stores Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return for smartphones, accessories, and related products.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Stay connected with customers beyond individual transactions and build long-term loyalty.",
        },
        {
          title: "Promote New Mobile Launches",
          text: "Keep existing customers informed about new smartphones, upgrades, collections, and special offers.",
        },
        {
          title: "Encourage Cross-Selling",
          text: "Promote relevant accessories and complementary products based on customer activity and purchase history.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use rewards, personalized offers, campaigns, and relevant communication to stay connected.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Reconnect with shoppers who haven't purchased recently through targeted re-engagement campaigns.",
        },
        {
          title: "Personalize Promotions",
          text: "Create more relevant offers based on customer activity and engagement.",
        },
        {
          title: "Increase Customer Retention",
          text: "Give existing customers more reasons to continue choosing your store.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, campaigns, customer engagement, and customer activity from one digital platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers through WhatsApp with offers, rewards, promotions, new product updates, and campaigns.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Create Connected Customer Experiences with Fydo",
      subHeading:
        "With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business. Bring a modern digital loyalty experience to your mobile store and build stronger relationships with your customers.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Building Customer Loyalty in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Create cashback, points, discounts, or other rewards that fit your mobile store.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect Customers",
          description:
            "Customers can interact with your loyalty program through a simple digital experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, rewards, campaigns, and **Free WhatsApp Marketing** to keep customers connected and encourage repeat purchases.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Mobile Store?",
          answer:
            "A Loyalty Program for Mobile Store is a customer retention solution that rewards shoppers for purchases and helps mobile retailers encourage repeat visits and build stronger customer relationships.",
        },
        {
          question: "How can Fydo help my mobile store build customer loyalty?",
          answer:
            "Fydo helps mobile stores reward customers, create personalized offers, promote products, run targeted campaigns, track customer activity, and re-engage previous customers.",
        },
        {
          question: "Can I offer cashback to mobile store customers?",
          answer:
            "Yes. Fydo supports cashback-based rewards that can be configured according to your business strategy.",
        },
        {
          question: "Can I promote new smartphone launches?",
          answer:
            "Yes. You can use customer engagement campaigns to promote new smartphones, launches, upgrades, accessories, and special offers.",
        },
        {
          question: "Can I use WhatsApp to engage my customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, promotions, and product updates.",
        },
        {
          question: "Can I create offers for different customer groups?",
          answer:
            "Yes. You can create targeted campaigns and offers based on customer activity and engagement.",
        },
        {
          question: "Can Fydo help me bring back inactive customers?",
          answer:
            "Yes. You can identify inactive customers and use targeted re-engagement campaigns to encourage them to return.",
        },
        {
          question: "Can I promote mobile accessories to existing customers?",
          answer:
            "Yes. You can promote relevant complementary products such as cases, chargers, earphones, wearables, and other accessories.",
        },
        {
          question: "Does Fydo work with a mobile store POS system?",
          answer:
            "Yes. Fydo can work with or without POS, making it suitable for mobile stores with different technology setups.",
        },
        {
          question: "How can I get started with Fydo?",
          answer:
            "You can start by setting up your rewards and customer engagement strategy with Fydo. Click Get Started Free or Book a Demo to begin.",
        },
      ],
    },
    signupSection: {
      badgeText: "Reward. Engage. Retain. Grow.",
      title: "Turn Mobile Shoppers into Loyal Customers",
      subtitle:
        "Don't let a single smartphone or accessory purchase be the end of your customer relationship. Reward customers, promote new launches, personalize offers, stay connected, and create more reasons for shoppers to return to your store. Build Stronger Customer Relationships with Fydo",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "mobile store customer retention",
      "whatsapp marketing for mobile stores",
      "cashback loyalty program for mobile shops",
      "referral marketing for mobile stores",
    ],
  },

  "loyalty-program-for-hardware-store": {
    slug: "loyalty-program-for-hardware-store",
    cluster: "vertical",
    showMerchantsSection: false,
    seo: {
      title:
        "Loyalty Program for Hardware Store | Customer Rewards & Retention",
      description:
        "Build a Loyalty Program for Hardware Store with Fydo. Reward customers, promote new products, increase repeat purchases, personalize offers, and engage shoppers with Free WhatsApp Marketing.",
      keywords: [
        "Loyalty Program for Hardware Store",
        "Hardware store loyalty program",
        "Hardware shop loyalty program",
        "Hardware store customer loyalty",
        "Hardware store rewards program",
        "Hardware store customer retention",
        "Loyalty software for hardware store",
        "Customer rewards for hardware store",
        "Hardware store retention software",
        "Digital loyalty program for hardware store",
        "Hardware store customer engagement",
        "Hardware store rewards platform",
        "Customer retention for hardware stores",
        "Loyalty solution for hardware store",
        "Digital rewards",
        "Cashback rewards",
        "Loyalty rewards",
        "Customer engagement",
        "Repeat purchases",
        "Returning customers",
        "Customer retention",
        "Personalized offers",
        "Digital offers",
        "Re-engagement campaigns",
        "Win-back campaigns",
        "Customer loyalty",
        "Reward system",
        "WhatsApp marketing",
        "Retail customer retention",
        "Hardware retail loyalty",
        "Hardware customer engagement",
        "Product promotion",
        "Cross-selling",
        "Building materials",
        "Tools and equipment",
        "Customer rewards",
      ],
    },
    heroSection: {
      badgeText: "Loyalty Program for Hardware Store",
      title:
        "Build Customer Loyalty & Increase Repeat Sales with a Loyalty Program for Hardware Store",
      gradientSubHeading: "",
      baseSubHeading: "",
      microSubHeading: "",
      description: [
        "Turn every hardware purchase into an opportunity to build stronger customer relationships. Fydo helps hardware stores reward customers, promote products, personalize offers, encourage repeat purchases, and stay connected with **Free WhatsApp Marketing**.",
      ],
      supportingText: "",
      primaryButton: {
        title: "Get Started Free",
        link: "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      },
      secondaryButton: {
        title: "Book a Demo",
        link: "/partner-signup",
      },
    },
    problemSection: {
      badgeText: "",
      heading: "Why Does Your Hardware Store Need a Loyalty Program?",
      subHeading:
        "Hardware customers often return for tools, building materials, electrical products, plumbing supplies, paints, and other essentials. Without a structured loyalty strategy, businesses can miss opportunities to retain these customers and increase repeat sales.",
      description: [],
      listLabel: "Common challenges include:",
      points: [
        "Customers switching between nearby hardware stores",
        "Strong local competition and price-driven purchases",
        "Limited engagement after a purchase",
        "Difficulty identifying regular and inactive customers",
        "Generic offers that don't match customer needs",
        "Limited ways to promote new products and brands",
        "Missed opportunities to cross-sell related products",
        "Difficulty reconnecting with previous customers",
        "Manual management of customer rewards",
      ],
      endingLine:
        "A **Loyalty Program for Hardware Store** can help turn individual purchases into stronger customer relationships and create more opportunities for repeat business.",
    },
    solutionSection: {
      badgeText: "",
      heading: "Turn Every Hardware Purchase into a Loyalty Opportunity",
      description: [
        "Fydo provides a simple **Loyalty Program for Hardware Store** that helps businesses reward customers, promote products, personalize engagement, and encourage repeat purchases.",
      ],
      listLabel: "With Fydo, your hardware store can:",
      steps: [
        "Reward customers for eligible purchases",
        "Offer cashback and digital loyalty rewards",
        "Create personalized customer offers",
        "Promote new products and brands",
        "Run targeted customer campaigns",
        "Promote complementary products",
        "Re-engage inactive customers",
        "Track customer activity",
        "Encourage repeat purchases",
        "Connect with customers through **Free WhatsApp Marketing**",
      ],
      bottomLine:
        "Whether you operate an independent hardware shop, building materials store, or multi-category hardware retailer, Fydo helps simplify customer engagement and retention.",
    },
    featureSection: {
      heading: "Powerful Features for a Loyalty Program for Hardware Store",
      subHeading: "",
      layout: "grid",
      features: [
        {
          id: 1,
          titleStart: {
            text: "Cashback & Points-Based Rewards",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reward customers with cashback, points, or other digital rewards based on your loyalty strategy.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 2,
          titleStart: {
            text: "Reward Every Purchase",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Give customers an additional reason to return by turning regular hardware purchases into rewarding experiences.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 3,
          titleStart: {
            text: "Free WhatsApp Marketing",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Stay connected with customers through **Free WhatsApp Marketing** for rewards, offers, product updates, promotions, and customer engagement.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 4,
          titleStart: {
            text: "Personalized Customer Offers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Create relevant offers based on customer activity and purchasing behaviour.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 5,
          titleStart: {
            text: "Targeted Loyalty Campaigns",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reach different customer groups with campaigns based on their purchase activity and engagement.",
          ],
          isReversed: false,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 6,
          titleStart: {
            text: "Promote New Products & Brands",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Keep existing customers informed about new tools, materials, brands, products, and special offers.",
          ],
          isReversed: true,
          animation: "understand",
          bullets: [],
        },
        {
          id: 7,
          titleStart: {
            text: "Promote Complementary Products",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Encourage cross-selling by promoting products that complement customers' previous purchases.",
          ],
          isReversed: false,
          animation: "get_customer",
          bullets: [],
        },
        {
          id: 8,
          titleStart: {
            text: "Re-Engage Inactive Customers",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Reconnect with customers who haven't purchased recently through targeted re-engagement campaigns.",
          ],
          isReversed: true,
          animation: "bring_more",
          bullets: [],
        },
        {
          id: 9,
          titleStart: {
            text: "Track Customer Activity",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Understand customer engagement and purchase activity to make better loyalty and marketing decisions.",
          ],
          isReversed: false,
          animation: "understand",
          bullets: [],
        },
        {
          id: 10,
          titleStart: {
            text: "Works With or Without POS",
            gradient: false,
          },
          titleGrad: {
            text: "",
            gradient: true,
          },
          titleEnd: {
            text: "",
            gradient: false,
          },
          description: [
            "Fydo can support your loyalty strategy whether your hardware store uses a POS system or not.",
          ],
          isReversed: true,
          animation: "get_customer",
          bullets: [],
        },
      ],
    },
    useCaseSection: {
      badgeText: "",
      heading: "Customer Loyalty for Every Type of Hardware Store",
      subHeading: "",
      useCases: [
        {
          title: "Independent Hardware Stores",
          detail:
            "Build stronger relationships with local customers and encourage repeat purchases.",
        },
        {
          title: "Building Material Stores",
          detail:
            "Reward regular customers and promote relevant construction and building products.",
        },
        {
          title: "Tools & Equipment Stores",
          detail:
            "Engage customers with new tools, equipment, accessories, and special offers.",
        },
        {
          title: "Electrical Hardware Stores",
          detail:
            "Promote electrical products, accessories, new arrivals, and relevant offers to existing customers.",
        },
        {
          title: "Plumbing Supply Stores",
          detail:
            "Stay connected with customers and promote plumbing products, accessories, and complementary items.",
        },
        {
          title: "Paint & Home Improvement Stores",
          detail:
            "Engage customers with new products, seasonal promotions, and relevant home improvement offers.",
        },
      ],
      endingLine: "",
      layout: "grid",
    },
    benefitsSection: {
      badgeText: "",
      heading: "Why Hardware Stores Choose Fydo for Customer Loyalty",
      description: "",
      benefits: [
        {
          title: "Increase Repeat Purchases",
          text: "Give customers more reasons to return for tools, materials, equipment, and other hardware products.",
        },
        {
          title: "Build Stronger Customer Relationships",
          text: "Stay connected with customers beyond individual transactions and create long-term loyalty.",
        },
        {
          title: "Promote New Products & Brands",
          text: "Keep existing customers informed about new arrivals, brands, product launches, and special offers.",
        },
        {
          title: "Encourage Cross-Selling",
          text: "Promote complementary products based on customer activity and previous purchases.",
        },
        {
          title: "Improve Customer Engagement",
          text: "Use rewards, personalized offers, campaigns, and relevant communication to keep customers connected.",
        },
        {
          title: "Bring Back Inactive Customers",
          text: "Reconnect with customers who haven't purchased recently through targeted re-engagement campaigns.",
        },
        {
          title: "Personalize Promotions",
          text: "Create more relevant offers based on customer activity and engagement.",
        },
        {
          title: "Increase Customer Retention",
          text: "Give existing customers more reasons to continue choosing your hardware store.",
        },
        {
          title: "Simplify Loyalty Management",
          text: "Manage rewards, campaigns, customer engagement, and customer activity from one digital platform.",
        },
        {
          title: "Free WhatsApp Marketing",
          text: "Engage customers through WhatsApp with offers, rewards, promotions, product updates, and campaigns.",
        },
      ],
    },
    customerTestimonialsSection: {
      heading: "Create Connected Customer Experiences with Fydo",
      subHeading:
        "With **10,000+ merchants and 1.6 lakh customers**, Fydo enables businesses to create connected customer experiences and encourage more repeat business. Bring a modern digital loyalty experience to your hardware store and build stronger relationships with your customers.",
      testimonials: [],
      points: [],
      badgeText: "",
      hideTrustPoints: true,
    },
    stepsSection: {
      title: "Start Building Customer Loyalty in 3 Simple Steps",
      description: "",
      showButton: false,
      steps: [
        {
          id: 1,
          iconUrl: "/icons/steps/download.svg",
          title: "Set Up Your Rewards",
          description:
            "Create cashback, points, discounts, or other rewards that fit your hardware store.",
        },
        {
          id: 2,
          iconUrl: "/icons/steps/award.svg",
          title: "Connect Customers",
          description:
            "Customers can interact with your loyalty program through a simple digital experience.",
        },
        {
          id: 3,
          iconUrl: "/icons/steps/arrow-right.svg",
          title: "Engage & Bring Them Back",
          description:
            "Use personalized offers, rewards, campaigns, and **Free WhatsApp Marketing** to keep customers connected and encourage repeat purchases.",
        },
      ],
    },
    FAQSection: {
      heading: "Frequently Asked Questions",
      faqItems: [
        {
          question: "What is a Loyalty Program for Hardware Store?",
          answer:
            "A Loyalty Program for Hardware Store is a customer retention solution that rewards shoppers for purchases and helps hardware retailers encourage repeat visits and build stronger customer relationships.",
        },
        {
          question:
            "How can Fydo help my hardware store build customer loyalty?",
          answer:
            "Fydo helps hardware stores reward customers, create personalized offers, promote products, run targeted campaigns, track customer activity, and re-engage previous customers.",
        },
        {
          question: "Can I offer cashback to hardware store customers?",
          answer:
            "Yes. Fydo supports cashback-based rewards that can be configured according to your business strategy.",
        },
        {
          question: "Can I promote new hardware products and brands?",
          answer:
            "Yes. You can use customer engagement campaigns to promote new tools, equipment, building materials, electrical products, plumbing supplies, paints, brands, and special offers.",
        },
        {
          question: "Can I use WhatsApp to engage my customers?",
          answer:
            "Yes. Fydo provides Free WhatsApp Marketing to help businesses communicate offers, rewards, promotions, product updates, and other customer engagement messages.",
        },
        {
          question: "Can I create offers for different customer groups?",
          answer:
            "Yes. You can create targeted campaigns and offers based on customer activity and engagement.",
        },
        {
          question: "Can Fydo help me bring back inactive customers?",
          answer:
            "Yes. You can identify inactive customers and use targeted re-engagement campaigns to encourage them to return.",
        },
        {
          question: "Can I promote complementary hardware products?",
          answer:
            "Yes. You can promote relevant complementary products and offers based on customer activity and previous purchases.",
        },
        {
          question: "Does Fydo work with a hardware store POS system?",
          answer:
            "Yes. Fydo can work with or without POS, making it suitable for hardware stores with different technology setups.",
        },
        {
          question: "How can I get started with Fydo?",
          answer:
            "You can start by setting up your rewards and customer engagement strategy with Fydo. Click Get Started Free or Book a Demo to begin.",
        },
      ],
    },
    signupSection: {
      badgeText: "Reward. Engage. Retain. Grow.",
      title: "Turn Hardware Shoppers into Loyal Customers",
      subtitle:
        "Don't let a single hardware purchase be the end of your customer relationship. Reward customers, promote new products, personalize offers, stay connected, and create more reasons for shoppers to return to your store. Build Stronger Customer Relationships with Fydo",
      buttonLabel: "Get Started Free",
      buttonLink:
        "https://play.google.com/store/apps/details?id=com.letsdevelopit.lfydnewapp",
      secondaryButton: {
        label: "Book a Demo",
        link: "/partner-signup",
      },
    },
    relatedBlogKeywords: [
      "hardware store customer retention",
      "whatsapp marketing for hardware stores",
      "cashback loyalty program for hardware shops",
      "customer loyalty for building supply stores",
    ],
  },
};
